import React, { forwardRef, useState, useEffect, useMemo, useRef, useCallback, Children, isValidElement, cloneElement, useLayoutEffect, Fragment as Fragment$1, createContext, useContext, useReducer } from 'react';
import kebabCase from 'lodash/kebabCase';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import last from 'lodash/last';
import ReactDOM, { createPortal } from 'react-dom';
import pull from 'lodash/pull';
import concat from 'lodash/concat';
import dropRight from 'lodash/dropRight';
import partition from 'lodash/partition';
import reduce from 'lodash/reduce';
import { C as ClickAwayProvider } from './_internal/ClickAwayProvider.js';
import memoize from 'lodash/memoize';
import castArray from 'lodash/castArray';
import pick from 'lodash/pick';
import isInteger from 'lodash/isInteger';
import throttle from 'lodash/throttle';
import take from 'lodash/take';
import isObject from 'lodash/isObject';
import range from 'lodash/range';
import chunk from 'lodash/chunk';
import set from 'lodash/set';

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : String(i);
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

/**
 * Alignments.
 */
const Alignment = {
  bottom: 'bottom',
  center: 'center',
  end: 'end',
  left: 'left',
  right: 'right',
  spaceAround: 'space-around',
  spaceBetween: 'space-between',
  spaceEvenly: 'space-evenly',
  start: 'start',
  top: 'top'
};
/**
 * See SCSS variable $lumx-color-palette
 */
const ColorPalette = {
  primary: 'primary',
  secondary: 'secondary',
  blue: 'blue',
  dark: 'dark',
  green: 'green',
  yellow: 'yellow',
  red: 'red',
  light: 'light',
  grey: 'grey'
};
/**
 * See SCSS variable $lumx-color-variants
 */
const ColorVariant = {
  D1: 'D1',
  D2: 'D2',
  L1: 'L1',
  L2: 'L2',
  L3: 'L3',
  L4: 'L4',
  L5: 'L5',
  L6: 'L6',
  N: 'N'
};
const Theme = {
  light: 'light',
  dark: 'dark'
};
const Size = {
  xxs: 'xxs',
  xs: 'xs',
  s: 's',
  m: 'm',
  l: 'l',
  xl: 'xl',
  xxl: 'xxl',
  tiny: 'tiny',
  regular: 'regular',
  medium: 'medium',
  big: 'big',
  huge: 'huge'
};
const Orientation = {
  horizontal: 'horizontal',
  vertical: 'vertical'
};
const Emphasis = {
  low: 'low',
  medium: 'medium',
  high: 'high'
};
/**
 * List of typographies that can't be customized.
 */
const TypographyInterface = {
  overline: 'overline',
  caption: 'caption',
  body1: 'body1',
  body2: 'body2',
  subtitle1: 'subtitle1',
  subtitle2: 'subtitle2',
  title: 'title',
  headline: 'headline',
  display1: 'display1'
};
/**
 * List of title typographies that can be customized (via CSS variables).
 */
const TypographyTitleCustom = {
  title1: 'custom-title1',
  title2: 'custom-title2',
  title3: 'custom-title3',
  title4: 'custom-title4',
  title5: 'custom-title5',
  title6: 'custom-title6'
};
/**
 * List of typographies that can be customized (via CSS variables).
 */
const TypographyCustom = _objectSpread2(_objectSpread2({}, TypographyTitleCustom), {}, {
  intro: 'custom-intro',
  'body-large': 'custom-body-large',
  body: 'custom-body',
  quote: 'custom-quote',
  'publish-info': 'custom-publish-info',
  button: 'custom-button'
});
/**
 * List of all typographies.
 */
const Typography = _objectSpread2(_objectSpread2({}, TypographyInterface), {}, {
  custom: TypographyCustom
});
/**
 * All available aspect ratios.
 */
const AspectRatio = {
  /** Intrinsic content ratio. */
  original: 'original',
  /** Ratio 3:1 */
  panoramic: 'panoramic',
  /** Ratio 16:9 */
  wide: 'wide',
  /** Ratio 3:2 */
  horizontal: 'horizontal',
  /** Ratio 3:2 */
  vertical: 'vertical',
  /** Ratio 1:1 */
  square: 'square',
  /** Ratio constrained by the parent. */
  free: 'free'
};
/**
 * Semantic info about the purpose of the component
 */
const Kind = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error'
};
/**
 * All available white-space values
 * */
const WhiteSpace = {
  normal: 'normal',
  nowrap: 'nowrap',
  pre: 'pre',
  'pre-wrap': 'pre-wrap',
  'pre-line': 'pre-line',
  'break-spaces': 'break-spaces'
};

/**
 * Re-exporting some utils types.
 */

function createCommonjsModule(fn, basedir, module) {
	return module = {
		path: basedir,
		exports: {},
		require: function (path, base) {
			return commonjsRequire(path, (base === undefined || base === null) ? module.path : base);
		}
	}, fn(module, module.exports), module.exports;
}

function commonjsRequire () {
	throw new Error('Dynamic requires are not currently supported by @rollup/plugin-commonjs');
}

var classnames = createCommonjsModule(function (module) {
/*!
	Copyright (c) 2018 Jed Watson.
	Licensed under the MIT License (MIT), see
	http://jedwatson.github.io/classnames
*/
/* global define */

(function () {

	var hasOwn = {}.hasOwnProperty;

	function classNames() {
		var classes = [];

		for (var i = 0; i < arguments.length; i++) {
			var arg = arguments[i];
			if (!arg) continue;

			var argType = typeof arg;

			if (argType === 'string' || argType === 'number') {
				classes.push(arg);
			} else if (Array.isArray(arg)) {
				if (arg.length) {
					var inner = classNames.apply(null, arg);
					if (inner) {
						classes.push(inner);
					}
				}
			} else if (argType === 'object') {
				if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes('[native code]')) {
					classes.push(arg.toString());
					continue;
				}

				for (var key in arg) {
					if (hasOwn.call(arg, key) && arg[key]) {
						classes.push(key);
					}
				}
			}
		}

		return classes.join(' ');
	}

	if ( module.exports) {
		classNames.default = classNames;
		module.exports = classNames;
	} else {
		window.classNames = classNames;
	}
}());
});

const mdiAlert='M13 14h-2V9h2m0 9h-2v-2h2M1 21h22L12 2z';const mdiAlertCircle='M13 13h-2V7h2m0 10h-2v-2h2M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2';const mdiArrowDown='M11 4h2v12l5.5-5.5 1.42 1.42L12 19.84l-7.92-7.92L5.5 10.5 11 16z';const mdiArrowUp='M13 20h-2V8l-5.5 5.5-1.42-1.42L12 4.16l7.92 7.92-1.42 1.42L13 8z';const mdiCheck='M21 7 9 19l-5.5-5.5 1.41-1.41L9 16.17 19.59 5.59z';const mdiCheckCircle='M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2m-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z';const mdiChevronDown='M7.41 8.58 12 13.17l4.59-4.59L18 10l-6 6-6-6z';const mdiChevronLeft='M15.41 16.58 10.83 12l4.58-4.59L14 6l-6 6 6 6z';const mdiChevronRight='M8.59 16.58 13.17 12 8.59 7.41 10 6l6 6-6 6z';const mdiChevronUp='M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z';const mdiClose='M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z';const mdiCloseCircle='M12 2a10 10 0 1 1 0 20 10 10 0 1 1 0-20m3.59 5L12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12 17 8.41z';const mdiDragVertical='M9 3h2v2H9zm4 0h2v2h-2zM9 7h2v2H9zm4 0h2v2h-2zm-4 4h2v2H9zm4 0h2v2h-2zm-4 4h2v2H9zm4 0h2v2h-2zm-4 4h2v2H9zm4 0h2v2h-2z';const mdiImageBroken='M19 3a2 2 0 0 1 2 2v6h-2v2h-2v2h-2v2h-2v2h-2v2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm2 12v4a2 2 0 0 1-2 2h-4v-2h2v-2h2v-2zm-2-6.5a.5.5 0 0 0-.5-.5h-13a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .5.5H11v-1h2v-2h2v-2h2V9h2z';const mdiInformation='M13 9h-2V7h2m0 10h-2v-6h2m-1-9A10 10 0 0 0 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2';const mdiMagnifyMinusOutline='M15.5 14h-.79l-.28-.27A6.5 6.5 0 0 0 16 9.5 6.5 6.5 0 0 0 9.5 3 6.5 6.5 0 0 0 3 9.5 6.5 6.5 0 0 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 5 1.5-1.5zm-6 0C7 14 5 12 5 9.5S7 5 9.5 5 14 7 14 9.5 12 14 9.5 14M7 9h5v1H7z';const mdiMagnifyPlusOutline='m15.5 14 5 5-1.5 1.5-5-5v-.79l-.27-.28A6.5 6.5 0 0 1 9.5 16 6.5 6.5 0 0 1 3 9.5 6.5 6.5 0 0 1 9.5 3 6.5 6.5 0 0 1 16 9.5c0 1.61-.59 3.09-1.57 4.23l.28.27zm-6 0C12 14 14 12 14 9.5S12 5 9.5 5 5 7 5 9.5 7 14 9.5 14m2.5-4h-2v2H9v-2H7V9h2V7h1v2h2z';const mdiMenuDown='m7 10 5 5 5-5z';const mdiMinus='M19 13H5v-2h14z';const mdiPauseCircleOutline='M13 16V8h2v8zm-4 0V8h2v8zm3-14a10 10 0 0 1 10 10 10 10 0 0 1-10 10A10 10 0 0 1 2 12 10 10 0 0 1 12 2m0 2a8 8 0 0 0-8 8 8 8 0 0 0 8 8 8 8 0 0 0 8-8 8 8 0 0 0-8-8';const mdiPlayCircleOutline='M12 20a8.01 8.01 0 0 1 0-16 8.01 8.01 0 0 1 0 16m0-18A10 10 0 0 0 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2m-2 14.5 6-4.5-6-4.5z';const mdiRadioboxBlank='M12 20a8 8 0 0 1-8-8 8 8 0 0 1 8-8 8 8 0 0 1 8 8 8 8 0 0 1-8 8m0-18A10 10 0 0 0 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2';const mdiRadioboxMarked='M12 20a8 8 0 0 1-8-8 8 8 0 0 1 8-8 8 8 0 0 1 8 8 8 8 0 0 1-8 8m0-18A10 10 0 0 0 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2m0 5a5 5 0 0 0-5 5 5 5 0 0 0 5 5 5 5 0 0 0 5-5 5 5 0 0 0-5-5';

/**
 * The prefix to use for the CSS classes.
 */
const CSS_PREFIX = 'lumx';

/**
 * Animation duration constants. Take into consideration that if you change one of these variables,
 * you need to update their scss counterpart as well
 */
const DIALOG_TRANSITION_DURATION = 400;
const NOTIFICATION_TRANSITION_DURATION = 200;

/**
 * Delay on hover after which we open or close the tooltip.
 * Only applies to devices supporting pointer hover.
 */
const TOOLTIP_HOVER_DELAY = {
  open: 500,
  close: 500
};

/**
 * Delay on long press after which we open or close the tooltip.
 * Only applies to devices not supporting pointer hover.
 */
const TOOLTIP_LONG_PRESS_DELAY = {
  open: 250,
  close: 3000
};

/**
 * Optional global `window` instance (not defined when running SSR).
 */
const WINDOW = typeof window !== 'undefined' ? window : undefined;

/**
 * Optional global `document` instance (not defined when running SSR).
 */
const DOCUMENT = typeof document !== 'undefined' ? document : undefined;

const _excluded = ["prefix"];

/**
 * Enhance isEmpty method to also works with numbers.
 *
 * @param  value The value to check.
 * @return Whether the input value is empty or != 0.
 */
const _isEmpty = value => {
  if (typeof value === 'number') {
    return value === 0;
  }
  return isEmpty(value);
};

/**
 * Get the basic CSS class for the given type.
 *
 * @param  prefix The class name prefix for the generated CSS class.
 * @param  type   The type of CSS class we want to generate (e.g.: 'color', 'variant', ...).
 * @param  value  The value of the type of the CSS class (e.g.: 'primary', 'button', ...).
 * @return The basic CSS class.
 */
function getBasicClass(_ref) {
  let {
    prefix,
    type,
    value
  } = _ref;
  if (isBoolean(value)) {
    if (!value) {
      // False value should not return a class.
      return '';
    }
    const booleanPrefixes = ['has', 'is'];
    if (booleanPrefixes.some(booleanPrefix => type.toString().startsWith(booleanPrefix))) {
      return `${prefix}--${kebabCase(type)}`;
    }
    return `${prefix}--is-${kebabCase(type)}`;
  }
  return `${prefix}--${kebabCase(type)}-${value}`;
}

/**
 * Return all basic LumX CSS classes which are available for every components.
 *
 * @see {@link /src/components/index.d.ts} for the possible values of each parameter.
 *
 * @param  prefix The class name prefix for the generated CSS class.
 * @param  props  All the other props you want to generate a class.
 *                The rule of thumb: the key is the name of the prop in the class, the value a string that will
 *                be used in the classname to represent the value of the given prop.
 * @return All LumX basic CSS classes.
 */
function handleBasicClasses(_ref2) {
  let {
      prefix
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded);
  const otherClasses = {};
  if (!isEmpty(props)) {
    Object.keys(props).forEach(prop => {
      otherClasses[getBasicClass({
        prefix,
        type: prop,
        value: props[prop]
      })] = isBoolean(props[prop]) ? props[prop] : !_isEmpty(props[prop]);
    });
  }
  return classnames(prefix, otherClasses);
}

/**
 * Checks whether or not the browser support passive events.
 * @see https://github.com/Modernizr/Modernizr/blob/6d56d814b9682843313b16060adb25a58d83a317/feature-detects/dom/passiveeventlisteners.js
 */
function isPassiveEventAvailable() {
  let supportsPassiveOption = false;
  try {
    const opts = Object.defineProperty({}, 'passive', {
      get() {
        supportsPassiveOption = true;
      }
    });
    window.addEventListener('testPassiveEventSupport', noop, opts);
    window.removeEventListener('testPassiveEventSupport', noop, opts);
  } catch (e) {
    // ignored
  }
  return supportsPassiveOption;
}

/**
 * Detects horizontal swipe direction without blocking the browser scroll using passive event.
 * @see http://javascriptkit.com/javatutors/touchevents2.shtml
 * @see https://github.com/WICG/EventListenerOptions/blob/gh-pages/explainer.md
 */
function detectHorizontalSwipe(touchSurface, handleSwipe) {
  let startX;
  let startY;
  // Required min distance traveled to be considered swipe.
  const threshold = 150;
  // Maximum distance allowed at the same time in perpendicular direction.
  const restraint = 150;
  // Maximum time allowed to travel that distance.
  const allowedTime = 300;
  let elapsedTime;
  let startTime;
  let finished;
  const onTouchStart = evt => {
    const [touch] = Array.from(evt.changedTouches);
    startX = touch.pageX;
    startY = touch.pageY;
    // Record time when finger first makes contact with surface.
    startTime = new Date().getTime();
    finished = false;
  };
  const onTouchMove = evt => {
    if (finished) {
      return;
    }
    elapsedTime = new Date().getTime() - startTime;
    if (elapsedTime > allowedTime) {
      // Touch swipe too long to be considered.
      return;
    }
    const [touch] = Array.from(evt.changedTouches);
    // Get horizontal dist traveled by finger while in contact with surface.
    const distX = touch.pageX - startX;
    // Get vertical dist traveled by finger while in contact with surface.
    const distY = touch.pageY - startY;
    if (!(Math.abs(distX) >= threshold && Math.abs(distY) <= restraint)) {
      // Swipe is not horizontal.
      return;
    }
    // Swipe direction.
    const direction = distX < 0 ? 'left' : 'right';
    handleSwipe(direction);
    finished = true;
  };

  // Activate passive event if possible for better scrolling performance.
  const eventOptions = isPassiveEventAvailable() ? {
    passive: true
  } : false;
  touchSurface.addEventListener('touchstart', onTouchStart, eventOptions);
  touchSurface.addEventListener('touchmove', onTouchMove, eventOptions);
  return () => {
    touchSurface.removeEventListener('touchstart', onTouchStart, eventOptions);
    touchSurface.removeEventListener('touchmove', onTouchMove, eventOptions);
  };
}
/**
 * Make sure the pressed key is the enter key before calling the callback.
 *
 * @param  handler The handler to call on enter/return press.
 * @return The decorated function.
 */
function onEnterPressed(handler) {
  return evt => {
    if (evt.key !== 'Enter') {
      return;
    }
    handler(evt);
  };
}

/**
 * Make sure the pressed key is the escape key before calling the callback.
 *
 * @param  handler The handler to call on enter/return press.
 * @return The decorated function.
 */
function onEscapePressed(handler) {
  return evt => {
    if (evt.key !== 'Escape') {
      return;
    }
    handler(evt);
  };
}

/**
 * Handle button key pressed (Enter + Space).
 *
 * @param  handler The handler to call.
 * @return The decorated function.
 */
function onButtonPressed(handler) {
  return evt => {
    if (evt.key !== 'Enter' && evt.key !== ' ') {
      return;
    }
    handler(evt);
  };
}

// See https://regex101.com/r/YjS1uI/3
const LAST_PART_CLASSNAME = /^(.*)-(.+)$/gi;

/**
 * Get the name of the root CSS class of a component based on its name.
 *
 * @param  componentName The name of the component. This name should contains the component prefix and be
 *                       written in PascalCase.
 * @param  subComponent Whether the current component is a sub component, if true, define the class according
 *                      to BEM standards.
 * @return The name of the root CSS class. This classname include the CSS classname prefix and is written in
 *         lower-snake-case.
 */
function getRootClassName(componentName, subComponent) {
  const formattedClassName = `${CSS_PREFIX}-${kebabCase(componentName)}`;
  if (subComponent) {
    return formattedClassName.replace(LAST_PART_CLASSNAME, '$1__$2');
  }
  return formattedClassName;
}

/**
 * Returns the classname associated to the given color and variant.
 * For example, for 'dark' and 'L2' it returns `lumx-color-font-dark-l2`
 */
const getFontColorClassName = function (color) {
  let colorVariant = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ColorVariant.N;
  return `lumx-color-font-${color}-${colorVariant}`;
};

/**
 * Returns the classname associated to the given typography.
 * For example, for `Typography.title` it returns `lumx-typography-title`
 */
const getTypographyClassName = typography => {
  return `lumx-typography-${typography}`;
};

let i = 0;

/**
 * Generate a unique id (for use in a11y or other id based DOM linking).
 *
 * (Tries to emulate React 18 useId hook, to remove once we upgrade React)
 */
function useId() {
  return React.useMemo(() => {
    i += 1;
    return `:lumx${i}:`;
  }, []);
}

const _excluded$1 = ["id", "title", "className", "cancelProps", "confirmProps", "kind", "size", "dialogProps", "children"],
  _excluded2 = ["label", "onClick"],
  _excluded3 = ["label", "onClick"];
/**
 * Associative map from message kind to color and icon.
 */
const CONFIG = {
  [Kind.error]: {
    color: ColorPalette.red,
    icon: mdiAlert
  },
  [Kind.info]: {
    color: ColorPalette.blue,
    icon: mdiInformation
  },
  [Kind.success]: {
    color: ColorPalette.green,
    icon: mdiCheckCircle
  },
  [Kind.warning]: {
    color: ColorPalette.yellow,
    icon: mdiAlertCircle
  }
};

/**
 * Component display name.
 */
const COMPONENT_NAME = 'AlertDialog';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME = getRootClassName(COMPONENT_NAME);

/**
 * Component default props.
 */
const DEFAULT_PROPS = {
  size: Size.tiny,
  kind: Kind.info
};

/**
 * AlertDialog component.
 *
 * An alert dialog is a modal dialog that interrupts the user's workflow to
 * communicate an important message and acquire a response.
 *
 * It should not have a complex content.
 * Children of this component should only be strings, paragraphs or links.
 */
const AlertDialog = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      id,
      title,
      className,
      cancelProps,
      confirmProps,
      kind,
      size,
      dialogProps,
      children
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1);
  const cancelButtonRef = React.useRef(null);
  const confirmationButtonRef = React.useRef(null);
  const {
    color,
    icon
  } = CONFIG[kind] || {};

  // Define a unique ID to target title and description for aria attributes.
  const generatedId = useId();
  const uniqueId = id || generatedId;
  const titleId = `${uniqueId}-title`;
  const descriptionId = `${uniqueId}-description`;

  // If content is a string, set in a paragraph.
  const DescriptionElement = typeof children === 'string' ? 'p' : 'div';
  const {
      label: confirmLabel,
      onClick: confirmOnClick
    } = confirmProps,
    forwardedConfirmProps = _objectWithoutProperties(confirmProps, _excluded2);
  const _ref = cancelProps || {},
    {
      label: cancelLabel,
      onClick: cancelOnClick
    } = _ref,
    forwardedCancelProps = _objectWithoutProperties(_ref, _excluded3);
  return /*#__PURE__*/React.createElement(Dialog, _extends({
    ref: ref,
    focusElement: cancelProps ? cancelButtonRef : confirmationButtonRef,
    size: size,
    dialogProps: _objectSpread2({
      id: uniqueId,
      role: 'alertdialog',
      'aria-labelledby': titleId,
      'aria-describedby': descriptionId
    }, dialogProps),
    className: classnames(className, handleBasicClasses({
      kind,
      prefix: CLASSNAME
    }))
  }, forwardedProps), /*#__PURE__*/React.createElement("header", null, /*#__PURE__*/React.createElement(Toolbar, {
    className: "lumx-spacing-margin-horizontal",
    before: /*#__PURE__*/React.createElement(Icon, {
      icon: icon,
      size: Size.s,
      color: color
    }),
    label: /*#__PURE__*/React.createElement("h2", {
      id: titleId,
      className: "lumx-typography-title"
    }, title)
  })), children && /*#__PURE__*/React.createElement(DescriptionElement, {
    id: descriptionId,
    className: "lumx-typography-body2 lumx-spacing-padding-vertical-big lumx-spacing-padding-horizontal-huge"
  }, children), /*#__PURE__*/React.createElement("footer", null, /*#__PURE__*/React.createElement(Toolbar, {
    className: "lumx-spacing-margin-horizontal",
    after: /*#__PURE__*/React.createElement(React.Fragment, null, cancelProps && /*#__PURE__*/React.createElement(Button, _extends({}, forwardedCancelProps, {
      ref: cancelButtonRef,
      emphasis: Emphasis.medium,
      onClick: cancelOnClick
    }), cancelLabel), /*#__PURE__*/React.createElement(Button, _extends({}, forwardedConfirmProps, {
      ref: confirmationButtonRef,
      color: color,
      className: "lumx-spacing-margin-left-regular",
      onClick: confirmOnClick
    }), confirmLabel))
  })));
});
AlertDialog.displayName = COMPONENT_NAME;
AlertDialog.className = CLASSNAME;
AlertDialog.defaultProps = DEFAULT_PROPS;

/**
 * Hook focusing an element when defined and `focus` boolean `true`.
 *
 * @param element       Element to focus.
 * @param shouldFocus   Boolean flag to trigger the focus
 */
function useFocus(element) {
  let shouldFocus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  const [wasFocus, setWasFocus] = useState(false);
  useEffect(() => {
    if (shouldFocus && wasFocus !== shouldFocus && element) {
      element.focus();
      setWasFocus(shouldFocus);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [element, shouldFocus]);
}

/**
 * Merge refs into a single function ref.
 *
 * @param  refs React references to merge.
 * @return the merged ref.
 */
function mergeRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  return value => refs.forEach(ref => {
    if (typeof ref === 'function') {
      ref(value);
    } else if (ref) {
      // eslint-disable-next-line no-param-reassign
      ref.current = value;
    }
  });
}

/**
 * Same as `mergeRefs` but memoized
 */
const useMergeRefs = function () {
  for (var _len2 = arguments.length, refs = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    refs[_key2] = arguments[_key2];
  }
  return useMemo(() => mergeRefs(...refs),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  refs);
};

const _excluded$2 = ["anchorToInput", "children", "chips", "className", "closeOnClick", "closeOnClickAway", "closeOnEscape", "disabled", "error", "fitToAnchorWidth", "hasError", "helper", "icon", "inputRef", "clearButtonProps", "isDisabled", "isRequired", "isOpen", "isValid", "label", "name", "offset", "onBlur", "onChange", "onClose", "onFocus", "onInfiniteScroll", "placeholder", "placement", "shouldFocusOnClose", "theme", "value", "textFieldProps", "focusAnchorOnClose"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1 = 'Autocomplete';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1 = getRootClassName(COMPONENT_NAME$1);

/**
 * Component default props.
 */
const DEFAULT_PROPS$1 = {
  anchorToInput: false,
  closeOnClick: false,
  closeOnClickAway: true,
  closeOnEscape: true,
  shouldFocusOnClose: false
};

/**
 * Autocomplete component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Autocomplete = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      anchorToInput,
      children,
      chips,
      className,
      closeOnClick,
      closeOnClickAway,
      closeOnEscape,
      disabled,
      error,
      fitToAnchorWidth,
      hasError,
      helper,
      icon,
      inputRef,
      clearButtonProps,
      isDisabled = disabled,
      isRequired,
      isOpen,
      isValid,
      label,
      name,
      offset,
      onBlur,
      onChange,
      onClose,
      onFocus,
      onInfiniteScroll,
      placeholder,
      placement,
      shouldFocusOnClose,
      theme,
      value,
      textFieldProps = {},
      focusAnchorOnClose
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$2);
  const inputAnchorRef = useRef(null);
  const textFieldRef = useRef(null);
  useFocus(inputAnchorRef.current, !isOpen && shouldFocusOnClose);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$1
    }))
  }), /*#__PURE__*/React.createElement(TextField, _extends({}, textFieldProps, {
    chips: chips,
    error: error,
    hasError: hasError,
    helper: helper,
    icon: icon,
    inputRef: mergeRefs(inputAnchorRef, inputRef),
    clearButtonProps: clearButtonProps,
    isDisabled: isDisabled,
    isRequired: isRequired,
    isValid: isValid,
    label: label,
    name: name,
    onBlur: onBlur,
    onChange: onChange,
    onFocus: onFocus,
    placeholder: placeholder,
    textFieldRef: textFieldRef,
    theme: theme,
    value: value
  })), /*#__PURE__*/React.createElement(Dropdown, {
    anchorRef: anchorToInput ? inputAnchorRef : textFieldRef,
    closeOnClick: closeOnClick,
    closeOnClickAway: closeOnClickAway,
    closeOnEscape: closeOnEscape,
    focusAnchorOnClose: focusAnchorOnClose,
    fitToAnchorWidth: fitToAnchorWidth,
    isOpen: isOpen,
    offset: offset,
    onClose: onClose,
    onInfiniteScroll: onInfiniteScroll,
    placement: placement,
    shouldFocusOnOpen: false,
    theme: theme
  }, children));
});
Autocomplete.displayName = COMPONENT_NAME$1;
Autocomplete.className = CLASSNAME$1;
Autocomplete.defaultProps = DEFAULT_PROPS$1;

const _excluded$3 = ["anchorToInput", "children", "chipsAlignment", "className", "closeOnClickAway", "closeOnEscape", "fitToAnchorWidth", "hasError", "helper", "icon", "inputRef", "clearButtonProps", "isDisabled", "isRequired", "isOpen", "isValid", "label", "name", "offset", "onBlur", "onChange", "onClear", "onClose", "onFocus", "onInfiniteScroll", "onKeyDown", "placeholder", "placement", "selectedChipRender", "shouldFocusOnClose", "theme", "type", "value", "values"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$2 = 'AutocompleteMultiple';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$2 = getRootClassName(COMPONENT_NAME$2);

/**
 * Component default props.
 */
const DEFAULT_PROPS$2 = {
  closeOnClickAway: true,
  closeOnEscape: true,
  selectedChipRender(choice, index, onClear, isDisabled) {
    const onClick = event => onClear && onClear(event, choice);
    return /*#__PURE__*/React.createElement(Chip, {
      key: index,
      after: onClear && /*#__PURE__*/React.createElement(Icon, {
        icon: mdiClose,
        size: Size.xxs
      }),
      isDisabled: isDisabled,
      size: Size.s,
      onAfterClick: onClick,
      onClick: onClick
    }, choice);
  },
  values: []
};

/**
 * AutocompleteMultiple component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const AutocompleteMultiple = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      anchorToInput,
      children,
      // `chipsAlignment` needs to be here to remove it from `forwardedProps`.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      chipsAlignment,
      className,
      closeOnClickAway,
      closeOnEscape,
      fitToAnchorWidth,
      hasError,
      helper,
      icon,
      inputRef,
      clearButtonProps,
      isDisabled,
      isRequired,
      isOpen,
      isValid,
      label,
      name,
      offset,
      onBlur,
      onChange,
      onClear,
      onClose,
      onFocus,
      onInfiniteScroll,
      onKeyDown,
      placeholder,
      placement,
      selectedChipRender,
      shouldFocusOnClose,
      theme,
      type,
      value,
      values
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$3);
  return /*#__PURE__*/React.createElement(Autocomplete, _extends({
    ref: ref
  }, forwardedProps, {
    anchorToInput: anchorToInput,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$2
    })),
    name: name,
    value: value,
    onChange: onChange,
    onKeyDown: onKeyDown,
    onBlur: onBlur,
    shouldFocusOnClose: shouldFocusOnClose,
    onFocus: onFocus,
    hasError: hasError,
    helper: helper,
    icon: icon,
    inputRef: inputRef,
    chips: values && values.map((chip, index) => selectedChipRender(chip, index, onClear)),
    isDisabled: isDisabled,
    isRequired: isRequired,
    clearButtonProps: clearButtonProps,
    isValid: isValid,
    label: label,
    placeholder: placeholder,
    theme: theme,
    type: type,
    isOpen: isOpen,
    closeOnClick: false,
    closeOnClickAway: closeOnClickAway,
    closeOnEscape: closeOnEscape,
    onClose: onClose,
    offset: offset,
    placement: placement,
    fitToAnchorWidth: fitToAnchorWidth,
    onInfiniteScroll: onInfiniteScroll
  }), children);
});
AutocompleteMultiple.displayName = COMPONENT_NAME$2;
AutocompleteMultiple.className = CLASSNAME$2;
AutocompleteMultiple.defaultProps = DEFAULT_PROPS$2;

const _excluded$4 = ["actions", "alt", "badge", "className", "image", "linkProps", "linkAs", "onClick", "onKeyPress", "size", "theme", "thumbnailProps"];

/**
 * Avatar sizes.
 */

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$3 = 'Avatar';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$3 = getRootClassName(COMPONENT_NAME$3);

/**
 * Component default props.
 */
const DEFAULT_PROPS$3 = {
  size: Size.m,
  theme: Theme.light
};

/**
 * Avatar component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Avatar = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      actions,
      alt,
      badge,
      className,
      image,
      linkProps,
      linkAs,
      onClick,
      onKeyPress,
      size,
      theme,
      thumbnailProps
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$4);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$3,
      size,
      theme
    }))
  }), /*#__PURE__*/React.createElement(Thumbnail, _extends({
    linkProps: linkProps,
    linkAs: linkAs,
    className: `${CLASSNAME$3}__thumbnail`,
    onClick: onClick,
    onKeyPress: onKeyPress
  }, thumbnailProps, {
    aspectRatio: AspectRatio.square,
    size: size,
    image: image,
    alt: alt,
    theme: theme
  })), actions && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$3}__actions`
  }, actions), badge && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$3}__badge`
  }, badge));
});
Avatar.displayName = COMPONENT_NAME$3;
Avatar.className = CLASSNAME$3;
Avatar.defaultProps = DEFAULT_PROPS$3;

const _excluded$5 = ["children", "className", "color"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$4 = 'Badge';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$4 = getRootClassName(COMPONENT_NAME$4);

/**
 * Component default props.
 */
const DEFAULT_PROPS$4 = {
  color: ColorPalette.primary
};

/**
 * Badge component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Badge = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      color
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$5);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$4,
      color
    }))
  }), children);
});
Badge.displayName = COMPONENT_NAME$4;
Badge.className = CLASSNAME$4;
Badge.defaultProps = DEFAULT_PROPS$4;

/** Get types of the values of a record. */

/**
 * Properties of a component to use to determine it's name.
 * In the order of preference.
 */
const NAME_PROPERTIES = ['type', 'type.displayName', 'displayName', 'name', 'type.name', 'props.mdxType', '_reactInternalFiber.elementType.name'];

/** LumX Component Type. */

/** Union type of all heading elements */

/** Union type of all text elements */

/**
 * Define a generic props types.
 */

/**
 * Callback function type alias (use for readability)
 */

/**
 * Create a predicate function that checks if a ReactNode is a react element from the given component.
 *
 * @param  component React function component or the component name
 * @return predicate returning true if value is instance of the component
 */
const isComponent = component => instance => {
  const componentName = typeof component === 'string' ? component : component.displayName;
  return !!get(instance, '$$typeof') && NAME_PROPERTIES.some(nameProperty => get(instance, nameProperty) === componentName);
};

/**
 * Similar to `isComponent` but more precise as it's not based on the component `displayName` but on the component function reference.
 */
const isComponentType = type => node => /*#__PURE__*/React.isValidElement(node) && node.type === type;

/**
 * JS falsy values.
 * (excluding `NaN` as it can't be distinguished from `number`)
 */

/**
 * Require either `aria-label` or `arial-labelledby` prop.
 * If none are set, the order will prioritize `aria-labelledby` over `aria-label` as it
 * needs a visible element.
 */

/**
 * Extract ref type for a component or JSX intrinsic element
 *
 * @example ComponentRef<'div'> => React.Ref<HTMLDivElement>
 * @example ComponentRef<Button> => React.Ref<HTMLButtonElement
 */

/**
 * Rectangle size
 */

/**
 * Maybe a HTMLElement or a React ref of a HTMLElement
 */

/**
 * A point coordinate in 2D space
 */

const _excluded$6 = ["linkAs"];
/**
 * Render link with default <a> HTML component or a custom one provided by `linkAs`.
 *
 * Can be used to inject the `Link` component from `react-router` and provide better a11y on LumX components.
 *
 * @param linkAs    Custom link component.
 * @param children  Link children.
 * @return          A link.
 */
const renderLink = function (_ref) {
  let {
      linkAs
    } = _ref,
    forwardedProps = _objectWithoutProperties(_ref, _excluded$6);
  for (var _len = arguments.length, children = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    children[_key - 1] = arguments[_key];
  }
  return /*#__PURE__*/React.createElement(linkAs || 'a', forwardedProps, ...children);
};

const _excluded$7 = ["aria-label", "children", "className", "color", "disabled", "emphasis", "hasBackground", "href", "isDisabled", "isSelected", "isActive", "isFocused", "isHovered", "linkAs", "name", "size", "target", "theme", "variant", "type", "fullWidth"];

/**
 * Button size definition.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$5 = 'ButtonRoot';
const BUTTON_WRAPPER_CLASSNAME = `${CSS_PREFIX}-button-wrapper`;
const BUTTON_CLASSNAME = `${CSS_PREFIX}-button`;

/**
 * Render a button wrapper with the ButtonRoot inside.
 *
 * @param  props Component props.
 * @return React element.
 */
const renderButtonWrapper = props => {
  const {
    color,
    emphasis,
    variant,
    fullWidth
  } = props;
  const adaptedColor = emphasis === Emphasis.low && (color === ColorPalette.light ? ColorPalette.dark : ColorPalette.light);
  const wrapperClassName = classnames(handleBasicClasses({
    color: adaptedColor,
    prefix: BUTTON_WRAPPER_CLASSNAME,
    variant,
    fullWidth
  }));
  const buttonProps = _objectSpread2(_objectSpread2({}, props), {}, {
    hasBackground: false
  });
  return /*#__PURE__*/React.createElement("div", {
    className: wrapperClassName
  }, /*#__PURE__*/React.createElement(ButtonRoot, buttonProps));
};

/**
 * ButtonRoot component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ButtonRoot = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      'aria-label': ariaLabel,
      children,
      className,
      color,
      disabled,
      emphasis,
      hasBackground,
      href,
      isDisabled = disabled,
      isSelected,
      isActive,
      isFocused,
      isHovered,
      linkAs,
      name,
      size,
      target,
      theme,
      variant,
      type = 'button',
      fullWidth
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$7);
  const adaptedColor = color || emphasis !== Emphasis.high && theme === Theme.dark && ColorPalette.light || emphasis === Emphasis.high && ColorPalette.primary || ColorPalette.dark;
  if (hasBackground) {
    return renderButtonWrapper(_objectSpread2(_objectSpread2({}, props), {}, {
      ref,
      variant,
      color: adaptedColor
    }));
  }
  const buttonClassName = classnames(className, handleBasicClasses({
    color: adaptedColor,
    emphasis,
    isSelected,
    isDisabled,
    isActive,
    isFocused,
    isHovered,
    prefix: BUTTON_CLASSNAME,
    size,
    theme: emphasis === Emphasis.high && theme,
    variant,
    fullWidth
  }));

  /**
   * If the linkAs prop is used, we use the linkAs component instead of a <button>.
   * If there is an href attribute, we display an <a> instead of a <button>.
   *
   * However, in any case, if the component is disabled, we returned a <button> since disabled is not compatible with <a>.
   */
  if ((linkAs || !isEmpty(props.href)) && !isDisabled) {
    return renderLink(_objectSpread2(_objectSpread2({
      linkAs
    }, forwardedProps), {}, {
      'aria-label': ariaLabel,
      href,
      target,
      className: buttonClassName,
      ref: ref
    }), children);
  }
  return /*#__PURE__*/React.createElement("button", _extends({}, forwardedProps, {
    disabled: isDisabled,
    "aria-disabled": isDisabled,
    "aria-label": ariaLabel,
    ref: ref,
    className: buttonClassName,
    name: name,
    type:
    // eslint-disable-next-line react/button-has-type
    type
  }), children);
});
ButtonRoot.displayName = COMPONENT_NAME$5;
ButtonRoot.defaultProps = {};

const _excluded$8 = ["children", "className", "emphasis", "leftIcon", "rightIcon", "size", "theme"];

/**
 * Button emphasis definition.
 * @deprecated Use Emphasis instead.
 */
const ButtonEmphasis = Emphasis;

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$6 = 'Button';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$5 = getRootClassName(COMPONENT_NAME$6);

/**
 * Component default props.
 */
const DEFAULT_PROPS$5 = {
  emphasis: Emphasis.high,
  size: Size.m,
  theme: Theme.light
};

/**
 * Button component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Button = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      emphasis,
      leftIcon,
      rightIcon,
      size,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$8);
  const buttonClassName = classnames(className, getBasicClass({
    prefix: CLASSNAME$5,
    type: 'hasLeftIcon',
    value: !isEmpty(leftIcon)
  }), getBasicClass({
    prefix: CLASSNAME$5,
    type: 'hasRightIcon',
    value: !isEmpty(rightIcon)
  }));
  return /*#__PURE__*/React.createElement(ButtonRoot, _extends({
    ref: ref
  }, _objectSpread2({
    emphasis,
    size,
    theme
  }, forwardedProps), {
    className: buttonClassName,
    variant: "button"
  }), leftIcon && !isEmpty(leftIcon) && /*#__PURE__*/React.createElement(Icon, {
    icon: leftIcon
  }), children && (isComponent(Text)(children) ? children : /*#__PURE__*/React.createElement("span", null, children)), rightIcon && !isEmpty(rightIcon) && /*#__PURE__*/React.createElement(Icon, {
    icon: rightIcon
  }));
});
Button.displayName = COMPONENT_NAME$6;
Button.className = CLASSNAME$5;
Button.defaultProps = DEFAULT_PROPS$5;

const _excluded$9 = ["emphasis", "image", "icon", "label", "size", "theme", "tooltipProps", "hideTooltip"];
/**
 * Component display name.
 */
const COMPONENT_NAME$7 = 'IconButton';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$6 = getRootClassName(COMPONENT_NAME$7);

/**
 * Component default props.
 */
const DEFAULT_PROPS$6 = {
  emphasis: Emphasis.high,
  size: Size.m,
  theme: Theme.light
};

/**
 * IconButton component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const IconButton = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      emphasis,
      image,
      icon,
      label,
      size,
      theme,
      tooltipProps,
      hideTooltip
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$9);
  return /*#__PURE__*/React.createElement(Tooltip, _extends({
    label: hideTooltip ? '' : label
  }, tooltipProps), /*#__PURE__*/React.createElement(ButtonRoot, _extends({
    ref: ref
  }, _objectSpread2({
    emphasis,
    size,
    theme
  }, forwardedProps), {
    "aria-label": label,
    variant: "icon"
  }), image ? /*#__PURE__*/React.createElement("img", {
    // no need to set alt as an aria-label is already set on the button
    alt: "",
    src: image
  }) : /*#__PURE__*/React.createElement(Icon, {
    icon: icon
  })));
});
IconButton.displayName = COMPONENT_NAME$7;
IconButton.className = CLASSNAME$6;
IconButton.defaultProps = DEFAULT_PROPS$6;

const _excluded$a = ["children", "className"];

/**
 * Defines the props of the component
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$8 = 'ButtonGroup';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$7 = getRootClassName(COMPONENT_NAME$8);

/**
 * Component default props.
 */
const DEFAULT_PROPS$7 = {};

/**
 * ButtonGroup component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ButtonGroup = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$a);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, CLASSNAME$7)
  }), children);
});
ButtonGroup.displayName = COMPONENT_NAME$8;
ButtonGroup.className = CLASSNAME$7;
ButtonGroup.defaultProps = DEFAULT_PROPS$7;

const _excluded$b = ["checked", "className", "disabled", "helper", "id", "inputRef", "isChecked", "isDisabled", "label", "name", "onChange", "theme", "value", "inputProps"];

/**
 * Intermediate state of checkbox.
 */
const INTERMEDIATE_STATE = 'intermediate';

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$9 = 'Checkbox';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$8 = getRootClassName(COMPONENT_NAME$9);

/**
 * Component default props.
 */
const DEFAULT_PROPS$8 = {
  theme: Theme.light
};

/**
 * Checkbox component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Checkbox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      checked,
      className,
      disabled,
      helper,
      id,
      inputRef,
      isChecked = checked,
      isDisabled = disabled,
      label,
      name,
      onChange,
      theme,
      value,
      inputProps = {}
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$b);
  const localInputRef = React.useRef(null);
  const generatedInputId = useId();
  const inputId = id || generatedInputId;
  const handleChange = event => {
    if (onChange) {
      onChange(!isChecked, value, name, event);
    }
  };
  const intermediateState = isChecked === INTERMEDIATE_STATE;
  React.useEffect(() => {
    const input = localInputRef.current;
    if (input) input.indeterminate = intermediateState;
  }, [intermediateState]);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      // Whether state is intermediate class name will "-checked"
      isChecked: intermediateState ? true : isChecked,
      isDisabled,
      isUnchecked: !isChecked,
      prefix: CLASSNAME$8,
      theme
    }))
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$8}__input-wrapper`
  }, /*#__PURE__*/React.createElement("input", _extends({
    ref: useMergeRefs(inputRef, localInputRef),
    type: "checkbox",
    id: inputId,
    className: `${CLASSNAME$8}__input-native`,
    disabled: isDisabled,
    tabIndex: isDisabled ? -1 : 0,
    name: name,
    value: value,
    checked: isChecked,
    onChange: handleChange,
    "aria-describedby": helper ? `${inputId}-helper` : undefined,
    "aria-checked": intermediateState ? 'mixed' : Boolean(isChecked)
  }, inputProps)), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$8}__input-placeholder`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$8}__input-background`
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$8}__input-indicator`
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: intermediateState ? mdiMinus : mdiCheck
  })))), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$8}__content`
  }, label && /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: inputId,
    className: `${CLASSNAME$8}__label`,
    theme: theme
  }, label), helper && /*#__PURE__*/React.createElement(InputHelper, {
    id: `${inputId}-helper`,
    className: `${CLASSNAME$8}__helper`,
    theme: theme
  }, helper)));
});
Checkbox.displayName = COMPONENT_NAME$9;
Checkbox.className = CLASSNAME$8;
Checkbox.defaultProps = DEFAULT_PROPS$8;

/**
 * Wrap mouse event handler to stop event propagation.
 *
 * @param  handler   The mouse handler to wrap.
 * @return Mouse handler stopping propagation.
 */
function useStopPropagation(handler) {
  return useCallback(evt => {
    if (!evt || !isFunction(handler)) {
      return;
    }
    handler(evt);
    evt.stopPropagation();
  }, [handler]);
}

const _excluded$c = ["after", "before", "children", "className", "color", "disabled", "isClickable", "isDisabled", "isHighlighted", "isSelected", "onAfterClick", "onBeforeClick", "onClick", "size", "theme", "href", "onKeyDown"];

/**
 * Chip sizes.
 */

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$a = 'Chip';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$9 = getRootClassName(COMPONENT_NAME$a);

/**
 * Component default props.
 */
const DEFAULT_PROPS$9 = {
  size: Size.m,
  theme: Theme.light
};

/**
 * Chip component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Chip = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      after,
      before,
      children,
      className,
      color,
      disabled,
      isClickable: propIsClickable,
      isDisabled = disabled,
      isHighlighted,
      isSelected,
      onAfterClick,
      onBeforeClick,
      onClick,
      size,
      theme,
      href,
      onKeyDown
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$c);
  const hasAfterClick = isFunction(onAfterClick);
  const hasBeforeClick = isFunction(onBeforeClick);
  const hasOnClick = isFunction(onClick);
  const isButton = hasOnClick && !href;
  const isClickable = Boolean(hasOnClick) || Boolean(href) || propIsClickable;

  // Adapt color to the theme.
  const chipColor = color || (theme === Theme.light ? ColorPalette.dark : ColorPalette.light);
  const handleOnBeforeClick = useStopPropagation(onBeforeClick);
  const handleOnAfterClick = useStopPropagation(onAfterClick);
  const handleKeyDown = evt => {
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(evt);
  };
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("a", _extends({
      role: isButton ? 'button' : undefined,
      tabIndex: isClickable ? 0 : undefined
    }, forwardedProps, {
      href: href,
      ref: ref,
      className: classnames(className, handleBasicClasses({
        clickable: isClickable,
        color: chipColor,
        isDisabled,
        hasAfter: Boolean(after),
        hasBefore: Boolean(before),
        highlighted: Boolean(isHighlighted),
        prefix: CLASSNAME$9,
        selected: Boolean(isSelected),
        size,
        unselected: Boolean(!isSelected)
      })),
      "aria-disabled": isClickable && isDisabled || undefined,
      onClick: hasOnClick ? onClick : undefined,
      onKeyDown: handleKeyDown
    }), before &&
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    React.createElement("div", {
      className: classnames(`${CLASSNAME$9}__before`, {
        [`${CLASSNAME$9}__before--is-clickable`]: hasBeforeClick
      }),
      onClick: handleOnBeforeClick
    }, before), /*#__PURE__*/React.createElement("div", {
      className: `${CLASSNAME$9}__label`
    }, children), after &&
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    React.createElement("div", {
      className: classnames(`${CLASSNAME$9}__after`, {
        [`${CLASSNAME$9}__after--is-clickable`]: hasAfterClick
      }),
      onClick: handleOnAfterClick
    }, after))
  );
});
Chip.displayName = COMPONENT_NAME$a;
Chip.className = CLASSNAME$9;
Chip.defaultProps = DEFAULT_PROPS$9;

const INITIAL_STATE_ACTIVE_CHIP = -1;

/**
 * Hook that provides the necessary information to manage chips navigation.
 * @param chips             List of chips selected.
 * @param onChipDeleted     Callback executed when a chip must be eliminated.
 * @param initialActiveChip Initial active chip index.
 *
 * @return chip navigation tools.
 */
const useChipGroupNavigation = function (chips, onChipDeleted) {
  let initialActiveChip = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : INITIAL_STATE_ACTIVE_CHIP;
  const [wasBackspacePressed, setWasBackspacePressed] = useState(false);
  const [activeChip, setActiveChip] = useState(initialActiveChip);

  /**
   * Resets the active index and backspace control to their initial state
   */
  const resetChipNavigation = () => {
    setWasBackspacePressed(false);
    setActiveChip(initialActiveChip);
  };

  /**
   * Callback to be executed when the backspace was pressed. If there are no chips
   * selected, it will return immediately. If there are it will check if the
   * backspace was already pressed. if it was, it means that the user wants to eliminate
   * the chip, so we execute the `onChipDeleted` function and reset internal state.
   *
   * If it was not pressed before, we set the `wasBackspacePressed` flag to true and
   * highlight the last chip.
   */
  const onBackspacePressed = () => {
    if (chips.length === 0) {
      return;
    }
    if (wasBackspacePressed) {
      const chipDeleted = chips[chips.length - 1];
      onChipDeleted(chipDeleted);
      resetChipNavigation();
    } else {
      setActiveChip(chips.length - 1);
      setWasBackspacePressed(true);
    }
  };
  return {
    activeChip,
    onBackspacePressed,
    resetChipNavigation
  };
};

const _excluded$d = ["align", "children", "className"];

/**
 * Defines the props of the component.
 */

/**
 * Component default props.
 */
const DEFAULT_PROPS$a = {};

/**
 * Component display name.
 */
const COMPONENT_NAME$b = 'ChipGroup';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$a = getRootClassName(COMPONENT_NAME$b);

/**
 * ChipGroup component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const InternalChipGroup = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      align,
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$d);
  const chipGroupClassName = handleBasicClasses({
    prefix: CLASSNAME$a
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, chipGroupClassName)
  }), children);
});
InternalChipGroup.displayName = COMPONENT_NAME$b;
InternalChipGroup.className = CLASSNAME$a;
InternalChipGroup.defaultProps = DEFAULT_PROPS$a;
const ChipGroup = Object.assign(InternalChipGroup, {
  useChipGroupNavigation
});

const _excluded$e = ["actions", "avatarProps", "children", "className", "date", "fullDate", "hasActions", "headerActions", "isOpen", "isRelevant", "name", "onClick", "onMouseEnter", "onMouseLeave", "text", "theme", "variant"];
/**
 * Comment block variants.
 */
const CommentBlockVariant = {
  indented: 'indented',
  linear: 'linear'
};

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$c = 'CommentBlock';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$b = getRootClassName(COMPONENT_NAME$c);

/**
 * Component default props.
 */
const DEFAULT_PROPS$b = {
  theme: Theme.light,
  variant: CommentBlockVariant.indented
};

/**
 * CommentBlock component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const CommentBlock = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      actions,
      avatarProps,
      children,
      className,
      date,
      fullDate,
      hasActions,
      headerActions,
      isOpen,
      isRelevant,
      name,
      onClick,
      onMouseEnter,
      onMouseLeave,
      text,
      theme,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$e);
  const hasChildren = Children.count(children) > 0;
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref,
    className: classnames(className, handleBasicClasses({
      hasChildren: hasChildren && isOpen,
      hasIndentedChildren: hasChildren && variant === CommentBlockVariant.indented,
      hasLinearChildren: hasChildren && variant === CommentBlockVariant.linear,
      isRelevant,
      prefix: CLASSNAME$b,
      theme
    }))
  }, forwardedProps), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$b}__wrapper`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$b}__avatar`
  }, /*#__PURE__*/React.createElement(Avatar, _extends({}, avatarProps, {
    size: Size.m,
    onClick: onClick
  }))), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$b}__container`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$b}__content`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$b}__meta`
  }, name &&
  /*#__PURE__*/
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
  React.createElement("span", {
    className: `${CLASSNAME$b}__name`,
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
  }, name), headerActions && /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$b}__header-actions`
  }, headerActions)), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$b}__text`
  }, text), date && (fullDate ? /*#__PURE__*/React.createElement(Tooltip, {
    label: fullDate,
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$b}__date`
  }, date)) : /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$b}__date`
  }, date))), hasActions && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$b}__actions`
  }, actions))), hasChildren && isOpen && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$b}__children`
  }, children));
});
CommentBlock.displayName = COMPONENT_NAME$c;
CommentBlock.className = CLASSNAME$b;
CommentBlock.defaultProps = DEFAULT_PROPS$b;

/**
 * Add a number of months from a date while resetting the day to prevent month length mismatches.
 */
function addMonthResetDay(date, monthOffset) {
  const newDate = new Date(date.getTime());
  newDate.setDate(1);
  newDate.setMonth(date.getMonth() + monthOffset);
  return newDate;
}

/**
 * Check if given date is valid.
 */
const isDateValid = date => date instanceof Date && !Number.isNaN(date.getTime());

/**
 * Component display name.
 */
const COMPONENT_NAME$d = 'DatePicker';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$c = getRootClassName(COMPONENT_NAME$d);

/** Get first day of week for locale from the browser API */
const getFromBrowser = locale => {
  try {
    var _localeMetadata$getWe;
    const localeMetadata = new Intl.Locale(locale.code);
    const {
      firstDay
    } = ((_localeMetadata$getWe = localeMetadata.getWeekInfo) === null || _localeMetadata$getWe === void 0 ? void 0 : _localeMetadata$getWe.call(localeMetadata)) || localeMetadata.weekInfo;
    // Sunday is represented as `0` in Date.getDay()
    if (firstDay === 7) return 0;
    return firstDay;
  } catch (e) {
    return undefined;
  }
};

/** List first day for each locale (could be removed when all browser implement Locale weekInfo) */
const FIRST_DAY_FOR_LOCALES = [{
  // Locales with Sunday as the first day of the week
  localeRX: /^(af|ar-(dz|eg|sa)|bn|cy|en-(ca|us|za)|fr-ca|gd|he|hi|ja|km|ko|pt-br|te|th|ug|zh-hk)$/i,
  firstDay: 0
}, {
  // Locales with Monday as the first day of the week
  localeRX: /^(ar-(ma|tn)|az|be|bg|bs|ca|cs|da|de|el|en-(au|gb|ie|in|nz)|eo|es|et|eu|fi|fr|fy|gl|gu|hr|ht|hu|hy|id|is|it|ka|kk|kn|lb|lt|lv|mk|mn|ms|mt|nb|nl|nn|oc|pl|pt|ro|ru|sk|sl|sq|sr|sv|ta|tr|uk|uz|vi|zh-(cn|tw))$/i,
  firstDay: 1
}, {
  // Locales with Saturday as the first day of the week
  localeRX: /^(ar|fa-ir)$/i,
  firstDay: 6
}];

/** Find first day of week for locale from the constant */
const getFromConstant = function (locale) {
  let searchBy = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'code';
  // Search for locale (lang + region)
  for (const {
    localeRX,
    firstDay
  } of FIRST_DAY_FOR_LOCALES) {
    if (localeRX.test(locale[searchBy])) return firstDay;
  }
  // Fallback search for locale lang
  if (locale.code !== locale.language) {
    return getFromConstant(locale, 'language');
  }
  return undefined;
};

/**
 * Get first day of the week for the given locale code (language + region).
 */
const getFirstDayOfWeek = locale => {
  // Get from browser API
  const firstDay = getFromBrowser(locale);
  if (firstDay !== undefined) return firstDay;

  // Get from constant
  return getFromConstant(locale);
};

const DAYS_PER_WEEK = 7;

/**
 * List week days (based on locale) with the week day letter (ex: "M" for "Monday") and week day number
 * (0-based index starting on Sunday).
 */
const getWeekDays = locale => {
  var _getFirstDayOfWeek;
  const iterDate = new Date();
  const firstDay = (_getFirstDayOfWeek = getFirstDayOfWeek(locale)) !== null && _getFirstDayOfWeek !== void 0 ? _getFirstDayOfWeek : 1;

  // Go to start of the week
  const offset = firstDay - iterDate.getDay();
  iterDate.setDate(iterDate.getDate() + offset);

  // Iterate through the days of the week
  const weekDays = [];
  for (let i = 0; i < DAYS_PER_WEEK; i++) {
    // Single letter week day (ex: "M" for "Monday", "L" for "Lundi", etc.)
    const letter = iterDate.toLocaleDateString(locale.code, {
      weekday: 'narrow'
    });
    // Weed day long notation
    const long = iterDate.toLocaleDateString(locale.code, {
      weekday: 'long'
    });
    // Day number (1-based index starting on Monday)
    const number = iterDate.getDay();
    weekDays.push({
      letter,
      number,
      long
    });
    iterDate.setDate(iterDate.getDate() + 1);
  }
  return weekDays;
};

/** Up to 6 rows can appear in a month calendar => 4 weeks + 1 start of month partial week + 1 send of month partial week */
const MONTH_ROW_COUNT = 6;

/**
 * Get month calendar.
 * A list of weeks with days indexed by week day number
 */
const getMonthCalendar = function (locale) {
  let referenceDate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();
  let rangeMinDate = arguments.length > 2 ? arguments[2] : undefined;
  let rangeMaxDate = arguments.length > 3 ? arguments[3] : undefined;
  const month = referenceDate.getMonth();
  const iterDate = new Date(referenceDate.getTime());
  iterDate.setDate(1);
  const weekDays = getWeekDays(locale);
  const lastDayOfWeek = last(weekDays);
  const weeks = [];
  let week = {};
  let rowCount = 0;
  while (rowCount < MONTH_ROW_COUNT) {
    const weekDayNumber = iterDate.getDay();
    const day = {
      date: new Date(iterDate.getTime())
    };

    // If a range is specified, check if the day is out of range.
    if (rangeMinDate && iterDate <= rangeMinDate || rangeMaxDate && iterDate >= rangeMaxDate) {
      day.isOutOfRange = true;
    }
    if (iterDate.getMonth() === month) {
      week[weekDayNumber] = day;
    }
    if (weekDayNumber === lastDayOfWeek.number) {
      weeks.push(week);
      rowCount += 1;
      week = {};
    }
    iterDate.setDate(iterDate.getDate() + 1);
  }
  if (Object.keys(week).length) weeks.push(week);
  return {
    weeks,
    weekDays
  };
};

/**
 * Check `date1` is on the same day as `date2`.
 */
const isSameDay = (date1, date2) => isDateValid(date1) && isDateValid(date2) && date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();

/**
 * Get current browser locale.
 */
const getCurrentLocale = () => {
  var _navigator$languages;
  return ((_navigator$languages = navigator.languages) === null || _navigator$languages === void 0 ? void 0 : _navigator$languages[0]) || navigator.language;
};

/**
 * Parse locale code
 * @example
 *   parseLocale('EN') // => { code: 'en', language: 'en' }
 *   parseLocale('en_us') // => { code: 'en-US', language: 'en', region: 'US' }
 *   parseLocale('EN-US') // => { code: 'en-US', language: 'en', region: 'US' }
 */
function parseLocale(locale) {
  const [rawLanguage, rawRegion] = locale.split(/[-_]/);
  if (!rawLanguage) {
    return undefined;
  }
  const language = rawLanguage.toLowerCase();
  let region;
  let code = language;
  if (rawRegion) {
    region = rawRegion.toUpperCase();
    code += `-${region}`;
  }
  return {
    code,
    region,
    language
  };
}

/**
 * Returns the value from the previous render.
 */
function usePreviousValue(value) {
  const prevValueRef = React.useRef();
  const prevValue = prevValueRef.current;
  React.useEffect(() => {
    prevValueRef.current = value;
  }, [value]);
  return prevValue;
}

const getYearDisplayName = locale => {
  let label;
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const displayNames = new Intl.DisplayNames(locale, {
      type: 'dateTimeField'
    });
    label = displayNames.of('year');
  } catch (error) {
    label = '';
  }
  return label;
};

function formatDayNumber(locale, date) {
  const formattedDate = date.toLocaleDateString(locale, {
    day: 'numeric'
  });
  if (formattedDate.match(/\d/)) return formattedDate.replace(/\D*(\d+)\D*/g, '$1');
  return formattedDate;
}

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$e = 'DatePickerControlled';

/**
 * DatePickerControlled component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const DatePickerControlled = /*#__PURE__*/forwardRef((props, ref) => {
  var _RegExp$exec;
  const {
    locale = getCurrentLocale(),
    maxDate,
    minDate,
    nextButtonProps,
    onChange,
    onNextMonthChange,
    onPrevMonthChange,
    previousButtonProps,
    selectedMonth,
    todayOrSelectedDateRef,
    value,
    onMonthChange
  } = props;
  const {
    weeks,
    weekDays
  } = React.useMemo(() => {
    const localeObj = parseLocale(locale);
    return getMonthCalendar(localeObj, selectedMonth, minDate, maxDate);
  }, [locale, minDate, maxDate, selectedMonth]);
  const selectedYear = selectedMonth.toLocaleDateString(locale, {
    year: 'numeric'
  }).slice(0, 4);
  const [textFieldYearValue, setTextFieldYearValue] = React.useState(selectedYear);
  const isYearValid = Number(textFieldYearValue) > 0 && Number(textFieldYearValue) <= 9999;

  // Updates month offset when validating year. Adds or removes 12 months per year when updating year value.
  const updateMonthOffset = React.useCallback(() => {
    if (isYearValid) {
      const yearNumber = selectedMonth.getFullYear();
      const offset = (Number(textFieldYearValue) - yearNumber) * 12;
      if (onMonthChange) {
        onMonthChange(addMonthResetDay(selectedMonth, offset));
      }
    }
  }, [isYearValid, selectedMonth, textFieldYearValue, onMonthChange]);
  const monthYear = selectedMonth.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long'
  });

  // Year can only be validated by pressing Enter key or on Blur. The below handles the press Enter key case
  const handleKeyPress = React.useMemo(() => onEnterPressed(updateMonthOffset), [updateMonthOffset]);

  // Required to update year in the TextField when the user changes year by using prev next month arrows
  React.useEffect(() => {
    if (Number(textFieldYearValue) !== selectedMonth.getFullYear()) {
      setTextFieldYearValue(selectedMonth.getFullYear().toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);
  const prevSelectedMonth = usePreviousValue(selectedMonth);
  const monthHasChanged = prevSelectedMonth && !isSameDay(selectedMonth, prevSelectedMonth);

  // Only set the aria-live after the month has changed otherwise it can get announced on mount when used in the popover dialog
  const [labelAriaLive, setLabelAriaLive] = React.useState();
  React.useEffect(() => {
    if (monthHasChanged) setLabelAriaLive('polite');
  }, [monthHasChanged]);
  const label = getYearDisplayName(locale);
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: `${CLASSNAME$c}`
  }, /*#__PURE__*/React.createElement(Toolbar, {
    className: `${CLASSNAME$c}__toolbar`,
    after: /*#__PURE__*/React.createElement(IconButton, _extends({}, nextButtonProps, {
      emphasis: Emphasis.low,
      icon: mdiChevronRight,
      onClick: onNextMonthChange
    })),
    before: /*#__PURE__*/React.createElement(IconButton, _extends({}, previousButtonProps, {
      emphasis: Emphasis.low,
      icon: mdiChevronLeft,
      onClick: onPrevMonthChange
    })),
    label: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      "aria-live": labelAriaLive,
      className: onMonthChange ? 'visually-hidden' : '',
      dir: "auto"
    }, monthYear), onMonthChange && /*#__PURE__*/React.createElement(FlexBox, {
      className: `${CLASSNAME$c}__month`,
      orientation: "horizontal",
      hAlign: "center",
      gap: "regular",
      vAlign: "center",
      dir: "auto"
    }, (_RegExp$exec = RegExp(`(.*)(${selectedYear})(.*)`).exec(monthYear)) === null || _RegExp$exec === void 0 ? void 0 : _RegExp$exec.slice(1).filter(part => part !== '').map(part => part === selectedYear ? /*#__PURE__*/React.createElement(TextField, {
      value: textFieldYearValue,
      "aria-label": label,
      onChange: setTextFieldYearValue,
      type: "number",
      max: 9999,
      min: 0,
      onBlur: updateMonthOffset,
      onKeyPress: handleKeyPress,
      key: "year",
      className: `${CLASSNAME$c}__year`
    }) : /*#__PURE__*/React.createElement(Text, {
      as: "p",
      key: part
    }, part))))
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$c}__calendar`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$c}__week-days ${CLASSNAME$c}__days-wrapper`
  }, weekDays.map(_ref => {
    let {
      letter,
      number,
      long
    } = _ref;
    return /*#__PURE__*/React.createElement("div", {
      key: number,
      className: `${CLASSNAME$c}__day-wrapper`
    }, /*#__PURE__*/React.createElement("span", {
      className: `${CLASSNAME$c}__week-day`,
      "aria-hidden": true
    }, letter.toLocaleUpperCase()), /*#__PURE__*/React.createElement("span", {
      className: "visually-hidden"
    }, long));
  })), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$c}__month-days ${CLASSNAME$c}__days-wrapper`
  }, weeks.flatMap((week, weekIndex) => {
    return weekDays.map((weekDay, dayIndex) => {
      const {
        date,
        isOutOfRange
      } = week[weekDay.number] || {};
      const key = `${weekIndex}-${dayIndex}`;
      const isToday = !isOutOfRange && date && isSameDay(date, new Date());
      const isSelected = date && value && isSameDay(value, date);
      return /*#__PURE__*/React.createElement("div", {
        key: key,
        className: `${CLASSNAME$c}__day-wrapper`
      }, date && /*#__PURE__*/React.createElement("button", {
        ref: isSelected || !value && isToday ? todayOrSelectedDateRef : null,
        className: classnames(`${CLASSNAME$c}__month-day`, {
          [`${CLASSNAME$c}__month-day--is-selected`]: isSelected,
          [`${CLASSNAME$c}__month-day--is-today`]: isToday
        }),
        disabled: isOutOfRange,
        type: "button",
        onClick: () => onChange(date)
      }, /*#__PURE__*/React.createElement("span", {
        "aria-hidden": true
      }, formatDayNumber(locale, date)), /*#__PURE__*/React.createElement("span", {
        className: "visually-hidden"
      }, date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }))));
    });
  }))));
});
DatePickerControlled.displayName = COMPONENT_NAME$e;
DatePickerControlled.className = CLASSNAME$c;

const _excluded$f = ["defaultMonth", "locale", "value", "onChange"];
/**
 * DatePicker component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const DatePicker = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      defaultMonth,
      locale,
      value,
      onChange
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$f);
  let referenceDate = value || defaultMonth || new Date();
  if (!isDateValid(referenceDate)) {
    // eslint-disable-next-line no-console
    console.warn(`[@lumx/react/DatePicker] Invalid date provided ${referenceDate}`);
    referenceDate = new Date();
  }
  const [selectedMonth, setSelectedMonth] = useState(referenceDate);
  const setPrevMonth = () => setSelectedMonth(current => addMonthResetDay(current, -1));
  const setNextMonth = () => setSelectedMonth(current => addMonthResetDay(current, +1));
  const onDatePickerChange = newDate => {
    onChange(newDate);
  };
  return /*#__PURE__*/React.createElement(DatePickerControlled, _extends({
    ref: ref
  }, forwardedProps, {
    defaultMonth: defaultMonth,
    locale: locale,
    value: value,
    onPrevMonthChange: setPrevMonth,
    onNextMonthChange: setNextMonth,
    selectedMonth: selectedMonth,
    onChange: onDatePickerChange,
    onMonthChange: setSelectedMonth
  }));
});
DatePicker.displayName = COMPONENT_NAME$d;
DatePicker.className = CLASSNAME$c;

const useBooleanState = defaultValue => {
  const [booleanValue, setBoolean] = useState(defaultValue);
  const setToFalse = useCallback(() => setBoolean(false), []);
  const setToTrue = useCallback(() => setBoolean(true), []);
  const toggleBoolean = useCallback(() => setBoolean(previousValue => !previousValue), []);
  return [booleanValue, setToFalse, setToTrue, toggleBoolean];
};

const _excluded$g = ["defaultMonth", "disabled", "isDisabled", "locale", "maxDate", "minDate", "name", "nextButtonProps", "onChange", "previousButtonProps", "value"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$f = 'DatePickerField';

/**
 * DatePickerField component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const DatePickerField = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      defaultMonth,
      disabled,
      isDisabled = disabled,
      locale = getCurrentLocale(),
      maxDate,
      minDate,
      name,
      nextButtonProps,
      onChange,
      previousButtonProps,
      value
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$g);
  const anchorRef = useRef(null);
  const [isOpen, close,, toggleOpen] = useBooleanState(false);
  const handleKeyboardNav = useCallback(evt => {
    if (evt.key === 'Enter' || evt.key === ' ') {
      toggleOpen();
    }
  }, [toggleOpen]);
  const onTextFieldChange = useCallback((textFieldValue, textFieldName, event) => {
    if (!textFieldValue) {
      onChange(undefined, textFieldName, event);
    }
  }, [onChange]);
  const onDatePickerChange = useCallback(newDate => {
    onChange(newDate, name);
    close();
  }, [name, onChange, close]);

  // Format date for text field
  const textFieldValue = (value === null || value === void 0 ? void 0 : value.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })) || '';
  const todayOrSelectedDateRef = React.useRef(null);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextField, _extends({
    ref: ref
  }, forwardedProps, {
    name: name,
    forceFocusStyle: isOpen,
    textFieldRef: anchorRef,
    value: textFieldValue,
    onClick: toggleOpen,
    onChange: onTextFieldChange,
    onKeyPress: handleKeyboardNav,
    isDisabled: isDisabled,
    readOnly: true,
    "aria-haspopup": "dialog"
  })), isOpen ? /*#__PURE__*/React.createElement(PopoverDialog
  // Can't use `aria-labelledby` targeting the text field label element (not correctly read on some screen readers)
  , {
    "aria-label": forwardedProps.label,
    anchorRef: anchorRef,
    placement: Placement.BOTTOM_START,
    isOpen: isOpen,
    onClose: close
    // On open, focus the selected day or today
    ,
    focusElement: todayOrSelectedDateRef
  }, /*#__PURE__*/React.createElement(DatePicker, {
    locale: locale,
    maxDate: maxDate,
    minDate: minDate,
    value: value,
    todayOrSelectedDateRef: todayOrSelectedDateRef,
    onChange: onDatePickerChange,
    defaultMonth: defaultMonth,
    nextButtonProps: nextButtonProps,
    previousButtonProps: previousButtonProps
  })) : null);
});
DatePickerField.displayName = COMPONENT_NAME$f;

/**
 * Keep track of listeners, only the last registered listener gets activated at any point (previously registered
 * listener are disabled).
 * When a listener gets unregistered, the previously registered listener gets enabled again.
 */
function makeListenerTowerContext() {
  const LISTENERS = [];
  return {
    register(listener) {
      var _last;
      // Disable previous listener.
      (_last = last(LISTENERS)) === null || _last === void 0 ? void 0 : _last.disable();
      // Keep track of current listener.
      LISTENERS.push(listener);
      // Enable current listener.
      listener.enable();
    },
    unregister(listener) {
      var _last2;
      // Disable current listener.
      listener.disable();
      // Remove current listener.
      pull(LISTENERS, listener);
      // Enable previous listener.
      (_last2 = last(LISTENERS)) === null || _last2 === void 0 ? void 0 : _last2.enable();
    }
  };
}

const LISTENERS = makeListenerTowerContext();

/**
 * Register a global listener on 'Escape' key pressed.
 *
 * If multiple listener are registered, only the last one is maintained. When a listener is unregistered, the previous
 * one gets activated again.
 *
 * @param callback      Callback
 * @param closeOnEscape Disables the hook when false
 */
function useCallbackOnEscape(callback) {
  let closeOnEscape = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  useEffect(() => {
    const rootElement = DOCUMENT === null || DOCUMENT === void 0 ? void 0 : DOCUMENT.body;
    if (!closeOnEscape || !callback || !rootElement) {
      return undefined;
    }
    const onKeyDown = onEscapePressed(callback);
    const listener = {
      enable: () => rootElement.addEventListener('keydown', onKeyDown),
      disable: () => rootElement.removeEventListener('keydown', onKeyDown)
    };
    LISTENERS.register(listener);
    return () => LISTENERS.unregister(listener);
  }, [callback, closeOnEscape]);
}

/** CSS selector listing all tabbable elements. */
const TABBABLE_ELEMENTS_SELECTOR = 'a[href], button, textarea, input:not([type="hidden"]):not([hidden]), [tabindex]';

/** CSS selector matching element that are disabled (should not receive focus). */
const DISABLED_SELECTOR = '[hidden], [tabindex="-1"], [disabled]:not([disabled="false"]), [aria-disabled]:not([aria-disabled="false"])';

const isNotDisabled = element => !element.matches(DISABLED_SELECTOR);
function getFocusableElements(element) {
  return Array.from(element.querySelectorAll(TABBABLE_ELEMENTS_SELECTOR)).filter(isNotDisabled);
}

/**
 * Get first and last elements focusable in an element.
 *
 * @param parentElement The element in which to search focusable elements.
 * @return first and last focusable elements
 */
function getFirstAndLastFocusable(parentElement) {
  const focusableElements = getFocusableElements(parentElement);

  // First non disabled element.
  const first = focusableElements[0];
  // Last non disabled element.
  const last = focusableElements[focusableElements.length - 1];
  if (last && first) {
    return {
      first,
      last
    };
  }
  return {};
}

const FOCUS_TRAPS = makeListenerTowerContext();

/**
 * Trap 'Tab' focus switch inside the `focusZoneElement`.
 *
 * If multiple focus trap are activated, only the last one is maintained and when a focus trap closes, the previous one
 * gets activated again.
 *
 * @param focusZoneElement The element in which to trap the focus.
 * @param focusElement     The element to focus when the focus trap is activated (otherwise the first focusable element
 *                         will be focused).
 */
function useFocusTrap(focusZoneElement, focusElement) {
  useEffect(() => {
    // Body element can be undefined in SSR context.
    const rootElement = DOCUMENT === null || DOCUMENT === void 0 ? void 0 : DOCUMENT.body;
    if (!rootElement || !focusZoneElement) {
      return undefined;
    }

    // Use the shadow root as focusZoneElement when available
    const focusZoneElementOrShadowRoot = focusZoneElement.shadowRoot || focusZoneElement;

    // Trap 'Tab' key down focus switch into the focus zone.
    const trapTabFocusInFocusZone = evt => {
      const {
        key
      } = evt;
      if (key !== 'Tab') {
        return;
      }
      const focusable = getFirstAndLastFocusable(focusZoneElementOrShadowRoot);

      // Prevent focus switch if no focusable available.
      if (!focusable.first) {
        evt.preventDefault();
        return;
      }
      const activeElement = focusZoneElement.shadowRoot ? focusZoneElement.shadowRoot.activeElement : document.activeElement;
      if (
      // No previous focus
      !activeElement ||
      // Previous focus is at the end of the focus zone.
      !evt.shiftKey && activeElement === focusable.last ||
      // Previous focus is outside the focus zone
      !focusZoneElementOrShadowRoot.contains(activeElement)) {
        focusable.first.focus();
        evt.preventDefault();
        return;
      }
      if (
      // Focus order reversed
      evt.shiftKey &&
      // Previous focus is at the start of the focus zone.
      activeElement === focusable.first) {
        focusable.last.focus();
        evt.preventDefault();
      }
    };
    const focusTrap = {
      enable: () => rootElement.addEventListener('keydown', trapTabFocusInFocusZone),
      disable: () => rootElement.removeEventListener('keydown', trapTabFocusInFocusZone)
    };

    // SETUP:
    if (focusElement && focusZoneElementOrShadowRoot.contains(focusElement)) {
      // Focus the given element.
      focusElement.focus({
        preventScroll: true
      });
    } else {
      var _getFirstAndLastFocus;
      // Focus the first focusable element in the zone.
      (_getFirstAndLastFocus = getFirstAndLastFocusable(focusZoneElementOrShadowRoot).first) === null || _getFirstAndLastFocus === void 0 ? void 0 : _getFirstAndLastFocus.focus({
        preventScroll: true
      });
    }
    FOCUS_TRAPS.register(focusTrap);

    // TEARDOWN:
    return () => FOCUS_TRAPS.unregister(focusTrap);
  }, [focusElement, focusZoneElement]);
}

/**
 * Convenient hook to create interaction observers.
 *
 * @param elements Elements to observe.
 * @param options  IntersectionObserver options.
 * @return Map of intersections.
 */
function useIntersectionObserver(elements, options) {
  const [intersections, setIntersections] = useState(() => new Map());
  useEffect(() => {
    if (elements.length < 1 || !elements.some(Boolean)) {
      return undefined;
    }
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        intersections.set(entry.target, entry);
      }
      setIntersections(new Map(intersections));
    }, options);
    for (const element of elements) {
      if (element) {
        observer.observe(element);
      }
    }
    return () => observer.disconnect();
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [...elements]);
  return intersections;
}

/**
 * Similar to lodash `partition` function but working with multiple predicates.
 *
 * @example
 * const isString = (s) => typeof s === 'string'
 * const isNumber = (s) => typeof s === 'number'
 * const [strings, numbers, others] = partitionMulti(['a', 1, 'b', false], [isString, isNumber])
 * //=> [['a', 'b'], [1], [false]]
 *
 * @param  elements array of elements
 * @param  predicates array of predicates to apply on elements
 * @return partitioned elements by the given predicates
 */
function partitionMulti(elements, predicates) {
  return reduce(predicates, (partitioned, predicate) => concat(dropRight(partitioned), partition(last(partitioned), predicate)), [elements]);
}

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

// Older browsers don't support event options, feature detect it.

// Adopted and modified solution from Bohdan Didukh (2017)
// https://stackoverflow.com/questions/41594997/ios-10-safari-prevent-scrolling-behind-a-fixed-overlay-and-maintain-scroll-posi

var hasPassiveEvents = false;
if (typeof window !== 'undefined') {
  var passiveTestOptions = {
    get passive() {
      hasPassiveEvents = true;
      return undefined;
    }
  };
  window.addEventListener('testPassive', null, passiveTestOptions);
  window.removeEventListener('testPassive', null, passiveTestOptions);
}

var isIosDevice = typeof window !== 'undefined' && window.navigator && window.navigator.platform && (/iP(ad|hone|od)/.test(window.navigator.platform) || window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);


var locks = [];
var documentListenerAdded = false;
var initialClientY = -1;
var previousBodyOverflowSetting = void 0;
var previousBodyPaddingRight = void 0;

// returns true if `el` should be allowed to receive touchmove events.
var allowTouchMove = function allowTouchMove(el) {
  return locks.some(function (lock) {
    if (lock.options.allowTouchMove && lock.options.allowTouchMove(el)) {
      return true;
    }

    return false;
  });
};

var preventDefault = function preventDefault(rawEvent) {
  var e = rawEvent || window.event;

  // For the case whereby consumers adds a touchmove event listener to document.
  // Recall that we do document.addEventListener('touchmove', preventDefault, { passive: false })
  // in disableBodyScroll - so if we provide this opportunity to allowTouchMove, then
  // the touchmove event on document will break.
  if (allowTouchMove(e.target)) {
    return true;
  }

  // Do not prevent if the event has more than one touch (usually meaning this is a multi touch gesture like pinch to zoom).
  if (e.touches.length > 1) return true;

  if (e.preventDefault) e.preventDefault();

  return false;
};

var setOverflowHidden = function setOverflowHidden(options) {
  // If previousBodyPaddingRight is already set, don't set it again.
  if (previousBodyPaddingRight === undefined) {
    var _reserveScrollBarGap = !!options && options.reserveScrollBarGap === true;
    var scrollBarGap = window.innerWidth - document.documentElement.clientWidth;

    if (_reserveScrollBarGap && scrollBarGap > 0) {
      previousBodyPaddingRight = document.body.style.paddingRight;
      document.body.style.paddingRight = scrollBarGap + 'px';
    }
  }

  // If previousBodyOverflowSetting is already set, don't set it again.
  if (previousBodyOverflowSetting === undefined) {
    previousBodyOverflowSetting = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
};

var restoreOverflowSetting = function restoreOverflowSetting() {
  if (previousBodyPaddingRight !== undefined) {
    document.body.style.paddingRight = previousBodyPaddingRight;

    // Restore previousBodyPaddingRight to undefined so setOverflowHidden knows it
    // can be set again.
    previousBodyPaddingRight = undefined;
  }

  if (previousBodyOverflowSetting !== undefined) {
    document.body.style.overflow = previousBodyOverflowSetting;

    // Restore previousBodyOverflowSetting to undefined
    // so setOverflowHidden knows it can be set again.
    previousBodyOverflowSetting = undefined;
  }
};

// https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#Problems_and_solutions
var isTargetElementTotallyScrolled = function isTargetElementTotallyScrolled(targetElement) {
  return targetElement ? targetElement.scrollHeight - targetElement.scrollTop <= targetElement.clientHeight : false;
};

var handleScroll = function handleScroll(event, targetElement) {
  var clientY = event.targetTouches[0].clientY - initialClientY;

  if (allowTouchMove(event.target)) {
    return false;
  }

  if (targetElement && targetElement.scrollTop === 0 && clientY > 0) {
    // element is at the top of its scroll.
    return preventDefault(event);
  }

  if (isTargetElementTotallyScrolled(targetElement) && clientY < 0) {
    // element is at the bottom of its scroll.
    return preventDefault(event);
  }

  event.stopPropagation();
  return true;
};

var disableBodyScroll = function disableBodyScroll(targetElement, options) {
  // targetElement must be provided
  if (!targetElement) {
    // eslint-disable-next-line no-console
    console.error('disableBodyScroll unsuccessful - targetElement must be provided when calling disableBodyScroll on IOS devices.');
    return;
  }

  // disableBodyScroll must not have been called on this targetElement before
  if (locks.some(function (lock) {
    return lock.targetElement === targetElement;
  })) {
    return;
  }

  var lock = {
    targetElement: targetElement,
    options: options || {}
  };

  locks = [].concat(_toConsumableArray(locks), [lock]);

  if (isIosDevice) {
    targetElement.ontouchstart = function (event) {
      if (event.targetTouches.length === 1) {
        // detect single touch.
        initialClientY = event.targetTouches[0].clientY;
      }
    };
    targetElement.ontouchmove = function (event) {
      if (event.targetTouches.length === 1) {
        // detect single touch.
        handleScroll(event, targetElement);
      }
    };

    if (!documentListenerAdded) {
      document.addEventListener('touchmove', preventDefault, hasPassiveEvents ? { passive: false } : undefined);
      documentListenerAdded = true;
    }
  } else {
    setOverflowHidden(options);
  }
};

var enableBodyScroll = function enableBodyScroll(targetElement) {
  if (!targetElement) {
    // eslint-disable-next-line no-console
    console.error('enableBodyScroll unsuccessful - targetElement must be provided when calling enableBodyScroll on IOS devices.');
    return;
  }

  locks = locks.filter(function (lock) {
    return lock.targetElement !== targetElement;
  });

  if (isIosDevice) {
    targetElement.ontouchstart = null;
    targetElement.ontouchmove = null;

    if (documentListenerAdded && locks.length === 0) {
      document.removeEventListener('touchmove', preventDefault, hasPassiveEvents ? { passive: false } : undefined);
      documentListenerAdded = false;
    }
  } else if (!locks.length) {
    restoreOverflowSetting();
  }
};

/**
 * Disables the scroll on the body to make it only usable in the current modal element.
 * When the modal element is not provided anymore, the scroll is restored.
 *
 * @param modalElement The modal element.
 */
const useDisableBodyScroll = modalElement => {
  useEffect(() => {
    if (!modalElement) {
      return undefined;
    }
    // Fixing the document overflow style to prevent a bug that scrolls the window to the top.
    const previousDocumentOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'visible';
    disableBodyScroll(modalElement);
    return () => {
      enableBodyScroll(modalElement);
      // Restore the previous overflow style.
      requestAnimationFrame(() => {
        document.documentElement.style.overflow = previousDocumentOverflow;
      });
    };
  }, [modalElement]);
};

const userHasReducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
};

/**
 * Returns true if the component is visible tracking the opacity transition.
 *
 * @param ref Element on which to listen the transition event.
 * @param isComponentVisible Whether the component intends to be visible or not.
 * @param onVisibilityChange Callback called when the visibility changes.
 * @return true if the component should be rendered
 */
const useTransitionVisibility = (ref, isComponentVisible, timeout, onVisibilityChange) => {
  const [isVisible, setVisible] = useState(isComponentVisible);
  const previousVisibility = useRef(isVisible);

  // On component visibility change.
  useEffect(() => {
    if (isComponentVisible) {
      setVisible(true);
      return undefined;
    }
    const {
      current: element
    } = ref;

    // Transition event is not supported or the user prefers reduced motion.
    // => Skip and set visibility to false directly.
    if (!element || !window.TransitionEvent || userHasReducedMotion()) {
      setVisible(false);
      return undefined;
    }
    const timer = setTimeout(() => setVisible(false), timeout);
    return () => clearTimeout(timer);
  }, [isComponentVisible, ref, timeout]);
  useEffect(() => {
    if (onVisibilityChange && previousVisibility.current !== isVisible) {
      onVisibilityChange(isVisible);
      previousVisibility.current = isVisible;
    }
  }, [isVisible, onVisibilityChange]);
  return isVisible || isComponentVisible;
};

const _excluded$h = ["children", "className", "header", "focusElement", "forceFooterDivider", "forceHeaderDivider", "footer", "isLoading", "isOpen", "onClose", "parentElement", "contentRef", "preventAutoClose", "size", "zIndex", "dialogProps", "onVisibilityChange", "disableBodyScroll", "preventCloseOnClick", "preventCloseOnEscape"];

/**
 * Defines the props of the component.
 */

const isHeader = isComponent('header');
const isFooter = isComponent('footer');

/**
 * Component display name.
 */
const COMPONENT_NAME$g = 'Dialog';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$d = getRootClassName(COMPONENT_NAME$g);

/**
 * Component default props.
 */
const DEFAULT_PROPS$c = {
  size: Size.big,
  disableBodyScroll: true
};

/**
 * Dialog component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Dialog = /*#__PURE__*/forwardRef((props, ref) => {
  var _intersections$get$is, _intersections$get, _intersections$get$is2, _intersections$get2;
  if (!DOCUMENT) {
    // Can't render in SSR.
    return null;
  }
  const {
      children,
      className,
      header,
      focusElement,
      forceFooterDivider,
      forceHeaderDivider,
      footer,
      isLoading,
      isOpen,
      onClose,
      parentElement,
      contentRef,
      preventAutoClose,
      size,
      zIndex,
      dialogProps,
      onVisibilityChange,
      disableBodyScroll,
      preventCloseOnClick,
      preventCloseOnEscape
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$h);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const previousOpen = React.useRef(isOpen);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    if (isOpen !== previousOpen.current) {
      previousOpen.current = isOpen;

      // Focus the parent element on close.
      if (!isOpen && parentElement && parentElement.current) {
        parentElement.current.focus();
      }
    }
  }, [isOpen, parentElement]);
  const shouldPreventCloseOnEscape = preventAutoClose || preventCloseOnEscape;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useCallbackOnEscape(onClose, isOpen && !shouldPreventCloseOnEscape);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const wrapperRef = useRef(null);
  /**
   * Since the `contentRef` comes from the parent and is optional,
   * we need to create a stable contentRef that will always be available.
   */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const localContentRef = useRef(null);
  // Handle focus trap.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useFocusTrap(isOpen && wrapperRef.current, focusElement === null || focusElement === void 0 ? void 0 : focusElement.current);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useDisableBodyScroll(disableBodyScroll && isOpen && localContentRef.current);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [sentinelTop, setSentinelTop] = useState(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [sentinelBottom, setSentinelBottom] = useState(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const intersections = useIntersectionObserver([sentinelTop, sentinelBottom], {
    threshold: [0, 1]
  });
  const hasTopIntersection = sentinelTop && !((_intersections$get$is = (_intersections$get = intersections.get(sentinelTop)) === null || _intersections$get === void 0 ? void 0 : _intersections$get.isIntersecting) !== null && _intersections$get$is !== void 0 ? _intersections$get$is : true);
  const hasBottomIntersection = sentinelBottom && !((_intersections$get$is2 = (_intersections$get2 = intersections.get(sentinelBottom)) === null || _intersections$get2 === void 0 ? void 0 : _intersections$get2.isIntersecting) !== null && _intersections$get$is2 !== void 0 ? _intersections$get$is2 : true);

  // Separate header, footer and dialog content from children.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [[headerChild], [footerChild], content] = useMemo(() => partitionMulti(Children.toArray(children), [isHeader, isFooter]), [children]);
  const headerChildProps = headerChild === null || headerChild === void 0 ? void 0 : headerChild.props;
  const headerChildContent = headerChildProps === null || headerChildProps === void 0 ? void 0 : headerChildProps.children;
  const footerChildProps = footerChild === null || footerChild === void 0 ? void 0 : footerChild.props;
  const footerChildContent = footerChildProps === null || footerChildProps === void 0 ? void 0 : footerChildProps.children;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const clickAwayRefs = useRef([wrapperRef]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const rootRef = useRef(null);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isVisible = useTransitionVisibility(rootRef, Boolean(isOpen), DIALOG_TRANSITION_DURATION, onVisibilityChange);
  const shouldPreventCloseOnClickAway = preventAutoClose || preventCloseOnClick;
  return isOpen || isVisible ? /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement("div", _extends({
    ref: mergeRefs(rootRef, ref)
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      isHidden: !isOpen,
      isLoading,
      isShown: isOpen || isVisible,
      prefix: CLASSNAME$d,
      size
    })),
    style: {
      zIndex
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$d}__overlay`
  }), /*#__PURE__*/React.createElement("section", _extends({
    className: `${CLASSNAME$d}__container`,
    role: "dialog",
    "aria-modal": "true"
  }, dialogProps), /*#__PURE__*/React.createElement(ClickAwayProvider, {
    callback: !shouldPreventCloseOnClickAway && onClose,
    childrenRefs: clickAwayRefs,
    parentRef: rootRef
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$d}__wrapper`,
    ref: wrapperRef
  }, (header || headerChildContent) && /*#__PURE__*/React.createElement("header", _extends({}, headerChildProps, {
    className: classnames(`${CLASSNAME$d}__header`, (forceHeaderDivider || hasTopIntersection) && `${CLASSNAME$d}__header--has-divider`, headerChildProps === null || headerChildProps === void 0 ? void 0 : headerChildProps.className)
  }), header, headerChildContent), /*#__PURE__*/React.createElement("div", {
    ref: mergeRefs(contentRef, localContentRef),
    className: `${CLASSNAME$d}__content`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$d}__sentinel ${CLASSNAME$d}__sentinel--top`,
    ref: setSentinelTop
  }), content, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$d}__sentinel ${CLASSNAME$d}__sentinel--bottom`,
    ref: setSentinelBottom
  })), (footer || footerChildContent) && /*#__PURE__*/React.createElement("footer", _extends({}, footerChildProps, {
    className: classnames(`${CLASSNAME$d}__footer`, (forceFooterDivider || hasBottomIntersection) && `${CLASSNAME$d}__footer--has-divider`, footerChildProps === null || footerChildProps === void 0 ? void 0 : footerChildProps.className)
  }), footer, footerChildContent), isLoading && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$d}__progress-overlay`
  }, /*#__PURE__*/React.createElement(Progress, {
    variant: ProgressVariant.circular
  })))))), document.body) : null;
});
Dialog.displayName = COMPONENT_NAME$g;
Dialog.className = CLASSNAME$d;
Dialog.defaultProps = DEFAULT_PROPS$c;

const _excluded$i = ["className", "theme"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$h = 'Divider';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$e = getRootClassName(COMPONENT_NAME$h);

/**
 * Component default props.
 */
const DEFAULT_PROPS$d = {
  theme: Theme.light
};

/**
 * Divider component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Divider = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$i);
  return /*#__PURE__*/React.createElement("hr", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$e,
      theme
    }))
  }));
});
Divider.displayName = COMPONENT_NAME$h;
Divider.className = CLASSNAME$e;
Divider.defaultProps = DEFAULT_PROPS$d;

const _excluded$j = ["className", "theme"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$i = 'DragHandle';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$f = getRootClassName(COMPONENT_NAME$i);

/**
 * DragHandle component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const DragHandle = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$j);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$f,
      theme
    }))
  }), /*#__PURE__*/React.createElement(Icon, {
    icon: mdiDragVertical,
    color: theme === Theme.dark ? ColorPalette.light : ColorPalette.dark,
    size: Size.xs
  }));
});
DragHandle.displayName = COMPONENT_NAME$i;
DragHandle.className = CLASSNAME$f;

const INITIAL_INDEX = -1;

/**
 * This custom hook provides the necessary set of functions and values to properly navigate
 * a list using the keyboard.
 *
 * @param  items                    the list of items that will be navigated using the keyboard.
 * @param  ref                      A reference to the element that is controlling the navigation.
 * @param  onListItemSelected       callback to be executed when the ENTER key is pressed on an item.
 * @param  onListItemNavigated      callback to be executed when the Arrow keys are pressed.
 * @param  onEnterPressed           callback to be executed when the ENTER key is pressed.
 * @param  onBackspacePressed       callback to be executed when the BACKSPACE key is pressed.
 * @param  keepFocusAfterSelection  determines whether after selecting an item, the focus should be maintained on the current target or not.
 * @param  initialIndex             where should the navigation start from. it defaults to `-1`, so the first item navigated is the item on position `0`.
 * @param  preventTabOnEnteredValue determines whether upon TAB, if there is a value entered, the event is prevented or not.
 * @return useKeyboardListNavigation helpers.
 */
const useKeyboardListNavigation = function (items, ref, onListItemSelected, onListItemNavigated, onEnterPressed, onBackspacePressed) {
  let keepFocusAfterSelection = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  let initialIndex = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : INITIAL_INDEX;
  let preventTabOnEnteredValue = arguments.length > 8 && arguments[8] !== undefined ? arguments[8] : true;
  const [activeItemIndex, setActiveItemIndex] = useState(initialIndex);

  /**
   * This function calculates the next index in the list to be highlighted
   * @param key - key code pressed
   * @return next active index
   */
  const calculateActiveIndex = key => {
    switch (key) {
      case 'ArrowDown':
        return activeItemIndex + 1 < items.length ? activeItemIndex + 1 : 0;
      case 'ArrowUp':
        return activeItemIndex - 1 >= 0 ? activeItemIndex - 1 : items.length - 1;
      default:
        return initialIndex;
    }
  };

  /**
   * Resets the active index to the initial state
   */
  const resetActiveIndex = () => {
    setActiveItemIndex(initialIndex);
  };

  /**
   * Prevents the default event and stops the propagation of said event
   * @param evt - key pressed event
   */
  const preventDefaultAndStopPropagation = evt => {
    evt.preventDefault();
    evt.stopPropagation();
  };

  /**
   * Handles navigation with the arrows using the keyboard
   * @param evt - key pressed event
   */
  const onArrowPressed = evt => {
    const {
      key
    } = evt;
    const nextActiveIndex = calculateActiveIndex(key);
    setActiveItemIndex(nextActiveIndex);
    preventDefaultAndStopPropagation(evt);
    if (onListItemNavigated) {
      const selectedItem = items[nextActiveIndex];
      onListItemNavigated(selectedItem);
    }
  };

  /**
   * Handles the event when the backspace key is pressed
   * @param evt - key pressed event
   */
  const onBackspaceKeyPressed = evt => {
    if (onBackspacePressed) {
      onBackspacePressed(evt);
    }
  };

  /**
   * Handles when the ENTER key is pressed
   * @param evt - key pressed event
   */
  const onEnterKeyPressed = evt => {
    if (!onListItemSelected) {
      return;
    }
    preventDefaultAndStopPropagation(evt);
    if (!keepFocusAfterSelection) {
      evt.currentTarget.blur();
    }
    const selectedItem = items[activeItemIndex];
    if (selectedItem) {
      onListItemSelected(selectedItem);
      resetActiveIndex();
    } else if (activeItemIndex === initialIndex && onEnterPressed) {
      const value = get(evt, 'target.value');
      onEnterPressed(value);
      resetActiveIndex();
    }
  };

  /**
   * Handles when the TAB key is pressed
   * @param evt - key pressed event
   */
  const onTabKeyPressed = evt => {
    const value = get(evt, 'target.value');
    if (preventTabOnEnteredValue && value && value.length > 0) {
      preventDefaultAndStopPropagation(evt);
    }
  };

  /**
   * In order to make it easier in the future to add new events depending on the key
   * a map was created to add these handlers. In the future, if there is another event
   * that we need to manage depending on a specific key, we just need to add the key code
   * here, and as a value, the handler for said key.
   */
  const eventsForKeyPressed = {
    ArrowDown: onArrowPressed,
    Tab: onTabKeyPressed,
    ArrowUp: onArrowPressed,
    Enter: onEnterKeyPressed,
    Backspace: onBackspaceKeyPressed
  };

  /**
   * Calculates the next active item index depending on the key pressed.
   * If the key pressed was ENTER, the function executes the callback `onListItemSelected`
   * and resets the active index, since an item was selected.
   * @param evt - key pressed or key down event
   */
  const onKeyboardNavigation = evt => {
    const {
      key
    } = evt;
    const handler = eventsForKeyPressed[key];
    if (handler) {
      handler(evt);
    }
  };
  useEffect(() => {
    const {
      current: currentElement
    } = ref;
    if (!currentElement) {
      return undefined;
    }
    currentElement.addEventListener('focus', resetActiveIndex);
    currentElement.addEventListener('keydown', onKeyboardNavigation);
    return () => {
      currentElement.removeEventListener('focus', resetActiveIndex);
      currentElement.removeEventListener('keydown', onKeyboardNavigation);
    };
  });
  return {
    activeItemIndex,
    onKeyboardNavigation,
    resetActiveIndex,
    setActiveItemIndex
  };
};

const _excluded$k = ["after", "before", "children", "className", "isHighlighted", "isSelected", "isDisabled", "linkAs", "linkProps", "linkRef", "onItemSelected", "size"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$j = 'ListItem';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$g = getRootClassName(COMPONENT_NAME$j);

/**
 * Component default props.
 */
const DEFAULT_PROPS$e = {
  size: Size.regular
};

/**
 * Check if the list item is clickable.
 * @return `true` if the list item is clickable; `false` otherwise.
 */
function isClickable(_ref) {
  let {
    linkProps,
    onItemSelected
  } = _ref;
  return !isEmpty(linkProps === null || linkProps === void 0 ? void 0 : linkProps.href) || !!onItemSelected;
}

/**
 * ListItem component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ListItem = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      after,
      before,
      children,
      className,
      isHighlighted,
      isSelected,
      isDisabled,
      linkAs,
      linkProps = {},
      linkRef,
      onItemSelected,
      size
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$k);
  const role = linkAs || linkProps.href ? 'link' : 'button';
  const onKeyDown = useMemo(() => {
    if (onItemSelected && role === 'link') return onEnterPressed(onItemSelected);
    if (onItemSelected && role === 'button') return onButtonPressed(onItemSelected);
    return undefined;
  }, [role, onItemSelected]);
  const content = /*#__PURE__*/React.createElement(React.Fragment, null, before && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$g}__before`
  }, before), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$g}__content`
  }, children), after && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$g}__after`
  }, after));
  return /*#__PURE__*/React.createElement("li", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$g,
      size
    }))
  }), isClickable({
    linkProps,
    onItemSelected
  }) ? ( /* Clickable list item */
  renderLink(_objectSpread2(_objectSpread2({
    linkAs,
    tabIndex: !isDisabled && role === 'button' ? 0 : undefined,
    role,
    'aria-disabled': isDisabled
  }, linkProps), {}, {
    href: isDisabled ? undefined : linkProps.href,
    className: classnames(handleBasicClasses({
      prefix: `${CLASSNAME$g}__link`,
      isHighlighted,
      isSelected,
      isDisabled
    })),
    onClick: isDisabled ? undefined : onItemSelected,
    onKeyDown,
    ref: linkRef
  }), content)) :
  /*#__PURE__*/
  /* Non clickable list item */
  React.createElement("div", {
    className: `${CLASSNAME$g}__wrapper`
  }, content));
});
ListItem.displayName = COMPONENT_NAME$j;
ListItem.className = CLASSNAME$g;
ListItem.defaultProps = DEFAULT_PROPS$e;

/**
 * @license React
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var b=Symbol.for("react.element"),c=Symbol.for("react.portal"),d=Symbol.for("react.fragment"),e=Symbol.for("react.strict_mode"),f=Symbol.for("react.profiler"),g=Symbol.for("react.provider"),h=Symbol.for("react.context"),k=Symbol.for("react.server_context"),l=Symbol.for("react.forward_ref"),m=Symbol.for("react.suspense"),n=Symbol.for("react.suspense_list"),p=Symbol.for("react.memo"),q=Symbol.for("react.lazy"),t=Symbol.for("react.offscreen"),u;u=Symbol.for("react.module.reference");
function v(a){if("object"===typeof a&&null!==a){var r=a.$$typeof;switch(r){case b:switch(a=a.type,a){case d:case f:case e:case m:case n:return a;default:switch(a=a&&a.$$typeof,a){case k:case h:case l:case q:case p:case g:return a;default:return r}}case c:return r}}}var ContextConsumer=h;var ContextProvider=g;var Element$1=b;var ForwardRef=l;var Fragment=d;var Lazy=q;var Memo=p;var Portal=c;var Profiler=f;var StrictMode=e;var Suspense=m;
var SuspenseList=n;var isAsyncMode=function(){return !1};var isConcurrentMode=function(){return !1};var isContextConsumer=function(a){return v(a)===h};var isContextProvider=function(a){return v(a)===g};var isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===b};var isForwardRef=function(a){return v(a)===l};var isFragment=function(a){return v(a)===d};var isLazy=function(a){return v(a)===q};var isMemo=function(a){return v(a)===p};
var isPortal=function(a){return v(a)===c};var isProfiler=function(a){return v(a)===f};var isStrictMode=function(a){return v(a)===e};var isSuspense=function(a){return v(a)===m};var isSuspenseList=function(a){return v(a)===n};
var isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===d||a===f||a===e||a===m||a===n||a===t||"object"===typeof a&&null!==a&&(a.$$typeof===q||a.$$typeof===p||a.$$typeof===g||a.$$typeof===h||a.$$typeof===l||a.$$typeof===u||void 0!==a.getModuleId)?!0:!1};var typeOf=v;

var reactIs_production_min = {
	ContextConsumer: ContextConsumer,
	ContextProvider: ContextProvider,
	Element: Element$1,
	ForwardRef: ForwardRef,
	Fragment: Fragment,
	Lazy: Lazy,
	Memo: Memo,
	Portal: Portal,
	Profiler: Profiler,
	StrictMode: StrictMode,
	Suspense: Suspense,
	SuspenseList: SuspenseList,
	isAsyncMode: isAsyncMode,
	isConcurrentMode: isConcurrentMode,
	isContextConsumer: isContextConsumer,
	isContextProvider: isContextProvider,
	isElement: isElement,
	isForwardRef: isForwardRef,
	isFragment: isFragment,
	isLazy: isLazy,
	isMemo: isMemo,
	isPortal: isPortal,
	isProfiler: isProfiler,
	isStrictMode: isStrictMode,
	isSuspense: isSuspense,
	isSuspenseList: isSuspenseList,
	isValidElementType: isValidElementType,
	typeOf: typeOf
};

var reactIs_development = createCommonjsModule(function (module, exports) {

if (process.env.NODE_ENV !== "production") {
  (function() {

// ATTENTION
// When adding new symbols to this file,
// Please consider also adding to 'react-devtools-shared/src/backend/ReactSymbols'
// The Symbol used to tag the ReactElement-like types.
var REACT_ELEMENT_TYPE = Symbol.for('react.element');
var REACT_PORTAL_TYPE = Symbol.for('react.portal');
var REACT_FRAGMENT_TYPE = Symbol.for('react.fragment');
var REACT_STRICT_MODE_TYPE = Symbol.for('react.strict_mode');
var REACT_PROFILER_TYPE = Symbol.for('react.profiler');
var REACT_PROVIDER_TYPE = Symbol.for('react.provider');
var REACT_CONTEXT_TYPE = Symbol.for('react.context');
var REACT_SERVER_CONTEXT_TYPE = Symbol.for('react.server_context');
var REACT_FORWARD_REF_TYPE = Symbol.for('react.forward_ref');
var REACT_SUSPENSE_TYPE = Symbol.for('react.suspense');
var REACT_SUSPENSE_LIST_TYPE = Symbol.for('react.suspense_list');
var REACT_MEMO_TYPE = Symbol.for('react.memo');
var REACT_LAZY_TYPE = Symbol.for('react.lazy');
var REACT_OFFSCREEN_TYPE = Symbol.for('react.offscreen');

// -----------------------------------------------------------------------------

var enableScopeAPI = false; // Experimental Create Event Handle API.
var enableCacheElement = false;
var enableTransitionTracing = false; // No known bugs, but needs performance testing

var enableLegacyHidden = false; // Enables unstable_avoidThisFallback feature in Fiber
// stuff. Intended to enable React core members to more easily debug scheduling
// issues in DEV builds.

var enableDebugTracing = false; // Track which Fiber(s) schedule render work.

var REACT_MODULE_REFERENCE;

{
  REACT_MODULE_REFERENCE = Symbol.for('react.module.reference');
}

function isValidElementType(type) {
  if (typeof type === 'string' || typeof type === 'function') {
    return true;
  } // Note: typeof might be other than 'symbol' or 'number' (e.g. if it's a polyfill).


  if (type === REACT_FRAGMENT_TYPE || type === REACT_PROFILER_TYPE || enableDebugTracing  || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || enableLegacyHidden  || type === REACT_OFFSCREEN_TYPE || enableScopeAPI  || enableCacheElement  || enableTransitionTracing ) {
    return true;
  }

  if (typeof type === 'object' && type !== null) {
    if (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || // This needs to include all possible module reference object
    // types supported by any Flight configuration anywhere since
    // we don't know which Flight build this will end up being used
    // with.
    type.$$typeof === REACT_MODULE_REFERENCE || type.getModuleId !== undefined) {
      return true;
    }
  }

  return false;
}

function typeOf(object) {
  if (typeof object === 'object' && object !== null) {
    var $$typeof = object.$$typeof;

    switch ($$typeof) {
      case REACT_ELEMENT_TYPE:
        var type = object.type;

        switch (type) {
          case REACT_FRAGMENT_TYPE:
          case REACT_PROFILER_TYPE:
          case REACT_STRICT_MODE_TYPE:
          case REACT_SUSPENSE_TYPE:
          case REACT_SUSPENSE_LIST_TYPE:
            return type;

          default:
            var $$typeofType = type && type.$$typeof;

            switch ($$typeofType) {
              case REACT_SERVER_CONTEXT_TYPE:
              case REACT_CONTEXT_TYPE:
              case REACT_FORWARD_REF_TYPE:
              case REACT_LAZY_TYPE:
              case REACT_MEMO_TYPE:
              case REACT_PROVIDER_TYPE:
                return $$typeofType;

              default:
                return $$typeof;
            }

        }

      case REACT_PORTAL_TYPE:
        return $$typeof;
    }
  }

  return undefined;
}
var ContextConsumer = REACT_CONTEXT_TYPE;
var ContextProvider = REACT_PROVIDER_TYPE;
var Element = REACT_ELEMENT_TYPE;
var ForwardRef = REACT_FORWARD_REF_TYPE;
var Fragment = REACT_FRAGMENT_TYPE;
var Lazy = REACT_LAZY_TYPE;
var Memo = REACT_MEMO_TYPE;
var Portal = REACT_PORTAL_TYPE;
var Profiler = REACT_PROFILER_TYPE;
var StrictMode = REACT_STRICT_MODE_TYPE;
var Suspense = REACT_SUSPENSE_TYPE;
var SuspenseList = REACT_SUSPENSE_LIST_TYPE;
var hasWarnedAboutDeprecatedIsAsyncMode = false;
var hasWarnedAboutDeprecatedIsConcurrentMode = false; // AsyncMode should be deprecated

function isAsyncMode(object) {
  {
    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 18+.');
    }
  }

  return false;
}
function isConcurrentMode(object) {
  {
    if (!hasWarnedAboutDeprecatedIsConcurrentMode) {
      hasWarnedAboutDeprecatedIsConcurrentMode = true; // Using console['warn'] to evade Babel and ESLint

      console['warn']('The ReactIs.isConcurrentMode() alias has been deprecated, ' + 'and will be removed in React 18+.');
    }
  }

  return false;
}
function isContextConsumer(object) {
  return typeOf(object) === REACT_CONTEXT_TYPE;
}
function isContextProvider(object) {
  return typeOf(object) === REACT_PROVIDER_TYPE;
}
function isElement(object) {
  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
}
function isForwardRef(object) {
  return typeOf(object) === REACT_FORWARD_REF_TYPE;
}
function isFragment(object) {
  return typeOf(object) === REACT_FRAGMENT_TYPE;
}
function isLazy(object) {
  return typeOf(object) === REACT_LAZY_TYPE;
}
function isMemo(object) {
  return typeOf(object) === REACT_MEMO_TYPE;
}
function isPortal(object) {
  return typeOf(object) === REACT_PORTAL_TYPE;
}
function isProfiler(object) {
  return typeOf(object) === REACT_PROFILER_TYPE;
}
function isStrictMode(object) {
  return typeOf(object) === REACT_STRICT_MODE_TYPE;
}
function isSuspense(object) {
  return typeOf(object) === REACT_SUSPENSE_TYPE;
}
function isSuspenseList(object) {
  return typeOf(object) === REACT_SUSPENSE_LIST_TYPE;
}

exports.ContextConsumer = ContextConsumer;
exports.ContextProvider = ContextProvider;
exports.Element = Element;
exports.ForwardRef = ForwardRef;
exports.Fragment = Fragment;
exports.Lazy = Lazy;
exports.Memo = Memo;
exports.Portal = Portal;
exports.Profiler = Profiler;
exports.StrictMode = StrictMode;
exports.Suspense = Suspense;
exports.SuspenseList = SuspenseList;
exports.isAsyncMode = isAsyncMode;
exports.isConcurrentMode = isConcurrentMode;
exports.isContextConsumer = isContextConsumer;
exports.isContextProvider = isContextProvider;
exports.isElement = isElement;
exports.isForwardRef = isForwardRef;
exports.isFragment = isFragment;
exports.isLazy = isLazy;
exports.isMemo = isMemo;
exports.isPortal = isPortal;
exports.isProfiler = isProfiler;
exports.isStrictMode = isStrictMode;
exports.isSuspense = isSuspense;
exports.isSuspenseList = isSuspenseList;
exports.isValidElementType = isValidElementType;
exports.typeOf = typeOf;
  })();
}
});

var reactIs = createCommonjsModule(function (module) {

if (process.env.NODE_ENV === 'production') {
  module.exports = reactIs_production_min;
} else {
  module.exports = reactIs_development;
}
});

/**
 * Flatten list of react nodes removing fragments and adding keys.
 * based on: https://github.com/grrowl/react-keyed-flatten-children/blob/5d421644a449765ddd62b659946196b4b5d7b135/index.ts
 *
 * @param  children React nodes to flatten.
 * @return Flattened react nodes.
 * @deprecated This function was used to develop keyboard navigation in the List component,
 *    but we will replace this with a better method that does not require to hack the children inspection
 *
 * TODO: remove List keyboard navigation once we have provided alternative component (Menu, Combobox, etc.),
 *       remove this function and then remove react-is from the dependencies
 */
function flattenChildren(children) {
  function recur(nodes) {
    let keys = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    return Children.toArray(nodes).reduce((acc, node, index) => {
      var _get;
      const nodeKeys = keys.concat((_get = get(node, 'key')) !== null && _get !== void 0 ? _get : index);
      if (reactIs.isFragment(node)) {
        acc.push(...recur(node.props.children, nodeKeys));
      } else if ( /*#__PURE__*/isValidElement(node)) {
        acc.push( /*#__PURE__*/cloneElement(node, {
          key: nodeKeys.join('.')
        }));
      } else if (typeof node === 'string' || typeof node === 'number') {
        acc.push(node);
      }
      return acc;
    }, []);
  }
  return recur(children);
}

const INITIAL_INDEX$1 = -1;
function onKeyboardFocus(props, handler) {
  let isMouseDown = false;
  return {
    onFocus(evt) {
      var _props$onFocus;
      (_props$onFocus = props.onFocus) === null || _props$onFocus === void 0 ? void 0 : _props$onFocus.call(props, evt);
      if (!isMouseDown) {
        handler(evt);
      }
    },
    onMouseDown(evt) {
      var _props$onMouseDown;
      (_props$onMouseDown = props.onMouseDown) === null || _props$onMouseDown === void 0 ? void 0 : _props$onMouseDown.call(props, evt);
      isMouseDown = true;
    },
    onMouseUp(evt) {
      var _props$onMouseUp;
      (_props$onMouseUp = props.onMouseUp) === null || _props$onMouseUp === void 0 ? void 0 : _props$onMouseUp.call(props, evt);
      isMouseDown = false;
    }
  };
}
const isNavigableItem = node => {
  return isComponent('ListItem')(node) && isClickable(node.props) && !node.props.isDisabled;
};

/**
 * This custom hook detects clickable list item in a list and make them navigable with the keyboard.
 *
 * @param  options See {@link Options}
 * @return See {@link Output}
 */
const useInteractiveList = options => {
  const {
    ref,
    onListItemSelected,
    onListItemNavigated
  } = options;
  const items = useMemo(() => flattenChildren(options.items), [options.items]);
  const [activeItemIndex, setActiveItemIndex] = useState(INITIAL_INDEX$1);

  // Attach listeners to the list.
  useEffect(() => {
    const {
      current: listElement
    } = ref;
    if (!listElement) {
      return undefined;
    }

    /**
     * This function calculates the next index in the list to be active
     * @param  index current index
     * @param  code  key code pressed
     * @return next index
     */
    const getNextIndex = (index, key) => {
      switch (key) {
        case 'ArrowDown':
          return index + 1 < items.length ? index + 1 : 0;
        case 'ArrowUp':
          return index - 1 >= 0 ? index - 1 : items.length - 1;
        default:
          return INITIAL_INDEX$1;
      }
    };

    /**
     * Resets the active index to the initial state
     */
    const resetActiveIndex = () => {
      setActiveItemIndex(INITIAL_INDEX$1);
    };

    /**
     * Handles navigation with the arrows using the keyboard
     * @param evt Key event
     */
    const onArrowPressed = evt => {
      const {
        key
      } = evt;
      if (key !== 'ArrowUp' && key !== 'ArrowDown') {
        return;
      }
      let nextIndex = activeItemIndex;
      let iterations = 0;

      // Move to next navigable item.
      do {
        nextIndex = getNextIndex(nextIndex, key);
        iterations += 1;
      } while (nextIndex !== INITIAL_INDEX$1 && nextIndex !== activeItemIndex && !isNavigableItem(items[nextIndex]) && iterations < items.length);
      setActiveItemIndex(nextIndex);
      evt.preventDefault();
      evt.stopPropagation();
      onListItemNavigated === null || onListItemNavigated === void 0 ? void 0 : onListItemNavigated(nextIndex, get(items, [nextIndex, 'key']));
    };

    /**
     * Reset active list item index when focusing outside the list.
     * @param evt Focus out event
     */
    const onFocusOut = evt => {
      if (!evt.relatedTarget || !listElement.contains(evt.relatedTarget)) {
        resetActiveIndex();
      }
    };
    listElement.addEventListener('focusout', onFocusOut);
    listElement.addEventListener('keydown', onArrowPressed);
    return () => {
      listElement.removeEventListener('focusout', onFocusOut);
      listElement.removeEventListener('keydown', onArrowPressed);
    };
  }, [ref, activeItemIndex, items, onListItemNavigated]);
  return useMemo(() => {
    let hasClickableItem = false;
    const transformedItems = items.map((item, index) => {
      var _item$props$isHighlig;
      // Ignore if list not clickable or item is not a simple list item.
      if (!isNavigableItem(item)) {
        return item;
      }
      hasClickableItem = true;
      const isHighlighted = index === activeItemIndex;

      // Clone list item: inject ref, add tab index and active state.
      return /*#__PURE__*/cloneElement(item, _objectSpread2(_objectSpread2({}, item.props), {}, {
        isHighlighted: (_item$props$isHighlig = item.props.isHighlighted) !== null && _item$props$isHighlig !== void 0 ? _item$props$isHighlig : isHighlighted,
        linkRef: mergeRefs(item.props.linkRef, element => {
          if (isHighlighted) {
            element === null || element === void 0 ? void 0 : element.focus();
          }
        }),
        onItemSelected(evt) {
          var _item$props$onItemSel, _item$props;
          (_item$props$onItemSel = (_item$props = item.props).onItemSelected) === null || _item$props$onItemSel === void 0 ? void 0 : _item$props$onItemSel.call(_item$props);
          onListItemSelected === null || onListItemSelected === void 0 ? void 0 : onListItemSelected(index, item.key, evt);
        }
      }, onKeyboardFocus(item.props, () => {
        setActiveItemIndex(index);
      })));
    });
    return {
      items: transformedItems,
      hasClickableItem
    };
  }, [items, activeItemIndex, onListItemSelected]);
};

const _excluded$l = ["children", "className", "isClickable", "itemPadding", "onListItemSelected", "tabIndex"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$k = 'List';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$h = getRootClassName(COMPONENT_NAME$k);

/**
 * Component default props.
 */
const DEFAULT_PROPS$f = {
  tabIndex: -1
};

/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/**
 * List component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const InternalList = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      isClickable,
      itemPadding,
      onListItemSelected,
      tabIndex
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$l);
  const listElementRef = useRef(null);
  const {
    items,
    hasClickableItem
  } = useInteractiveList({
    items: children,
    ref: listElementRef,
    onListItemSelected
  });
  const clickable = hasClickableItem || isClickable;
  return /*#__PURE__*/React.createElement("ul", _extends({}, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$h,
      itemPadding: itemPadding !== null && itemPadding !== void 0 ? itemPadding : clickable ? Size.big : undefined
    })),
    tabIndex: tabIndex,
    ref: mergeRefs(ref, listElementRef)
  }), items);
});
InternalList.displayName = COMPONENT_NAME$k;
InternalList.className = CLASSNAME$h;
InternalList.defaultProps = DEFAULT_PROPS$f;
const List = Object.assign(InternalList, {
  useKeyboardListNavigation
});

/**
 * HOC component wrapping a component to skip render if predicate return falsy
 */
const skipRender = (predicate, Component) => {
  const Wrapper = /*#__PURE__*/React.forwardRef((props, ref) => {
    if (!DOCUMENT) {
      // Can't render in SSR.
      return null;
    }
    return /*#__PURE__*/React.createElement(Component, _extends({
      ref: ref
    }, props));
  });
  Wrapper.displayName = Component.displayName;
  return Wrapper;
};

/**
 * Helper component using useLayoutEffect to trigger a callback on before unmount.
 *
 * The callback must be wrapped in a React ref to avoid updating the `useLayoutEffect` before the un-mount
 */
const OnBeforeUnmount = _ref => {
  let {
    callbackRef
  } = _ref;
  useLayoutEffect(() => {
    return () => {
      var _callbackRef$current;
      // On unmount
      // eslint-disable-next-line react-hooks/exhaustive-deps
      (_callbackRef$current = callbackRef.current) === null || _callbackRef$current === void 0 ? void 0 : _callbackRef$current.call(callbackRef);
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  return null;
};

/**
 * Provides an unmount sentinel to inject in the popover to detect when it closes and restore the focus to the
 * anchor if needed.
 */
function useRestoreFocusOnClose(_ref, popoverElement) {
  let {
    focusAnchorOnClose,
    anchorRef,
    parentElement
  } = _ref;
  const onBeforeUnmountRef = React.useRef();
  React.useEffect(() => {
    if (!popoverElement || !focusAnchorOnClose) {
      onBeforeUnmountRef.current = undefined;
      return;
    }
    onBeforeUnmountRef.current = () => {
      const isFocusWithin = popoverElement === null || popoverElement === void 0 ? void 0 : popoverElement.contains(document.activeElement);
      if (!isFocusWithin) return;

      // On next render
      setTimeout(() => {
        const anchor = anchorRef.current;
        const elementToFocus =
        // Provided parent element
        (parentElement === null || parentElement === void 0 ? void 0 : parentElement.current) || (
        // Or first focusable element in anchor
        anchor ? getFirstAndLastFocusable(anchor).first : undefined) ||
        // Fallback to anchor
        anchor;
        elementToFocus === null || elementToFocus === void 0 ? void 0 : elementToFocus.focus({
          preventScroll: true
        });
      }, 0);
    };
  }, [anchorRef, focusAnchorOnClose, parentElement, popoverElement]);
  return /*#__PURE__*/React.createElement(OnBeforeUnmount, {
    callbackRef: onBeforeUnmountRef
  });
}

/**
 * Simple ponyfill for Object.fromEntries
 */

var fromEntries = function fromEntries(entries) {
  return entries.reduce(function (acc, _ref) {
    var key = _ref[0],
        value = _ref[1];
    acc[key] = value;
    return acc;
  }, {});
};
/**
 * Small wrapper around `useLayoutEffect` to get rid of the warning on SSR envs
 */

var useIsomorphicLayoutEffect = typeof window !== 'undefined' && window.document && window.document.createElement ? useLayoutEffect : useEffect;

var top = 'top';
var bottom = 'bottom';
var right = 'right';
var left = 'left';
var auto = 'auto';
var basePlacements = [top, bottom, right, left];
var start = 'start';
var end = 'end';
var clippingParents = 'clippingParents';
var viewport = 'viewport';
var popper = 'popper';
var reference = 'reference';
var variationPlacements = /*#__PURE__*/basePlacements.reduce(function (acc, placement) {
  return acc.concat([placement + "-" + start, placement + "-" + end]);
}, []);
var placements = /*#__PURE__*/[].concat(basePlacements, [auto]).reduce(function (acc, placement) {
  return acc.concat([placement, placement + "-" + start, placement + "-" + end]);
}, []); // modifiers that need to read the DOM

var beforeRead = 'beforeRead';
var read = 'read';
var afterRead = 'afterRead'; // pure-logic modifiers

var beforeMain = 'beforeMain';
var main = 'main';
var afterMain = 'afterMain'; // modifier with the purpose to write to the DOM (or write into a framework state)

var beforeWrite = 'beforeWrite';
var write = 'write';
var afterWrite = 'afterWrite';
var modifierPhases = [beforeRead, read, afterRead, beforeMain, main, afterMain, beforeWrite, write, afterWrite];

function getNodeName(element) {
  return element ? (element.nodeName || '').toLowerCase() : null;
}

/*:: import type { Window } from '../types'; */

/*:: declare function getWindow(node: Node | Window): Window; */
function getWindow(node) {
  if (node.toString() !== '[object Window]') {
    var ownerDocument = node.ownerDocument;
    return ownerDocument ? ownerDocument.defaultView || window : window;
  }

  return node;
}

/*:: declare function isElement(node: mixed): boolean %checks(node instanceof
  Element); */

function isElement$1(node) {
  var OwnElement = getWindow(node).Element;
  return node instanceof OwnElement || node instanceof Element;
}
/*:: declare function isHTMLElement(node: mixed): boolean %checks(node instanceof
  HTMLElement); */


function isHTMLElement(node) {
  var OwnElement = getWindow(node).HTMLElement;
  return node instanceof OwnElement || node instanceof HTMLElement;
}
/*:: declare function isShadowRoot(node: mixed): boolean %checks(node instanceof
  ShadowRoot); */


function isShadowRoot(node) {
  var OwnElement = getWindow(node).ShadowRoot;
  return node instanceof OwnElement || node instanceof ShadowRoot;
}

// and applies them to the HTMLElements such as popper and arrow

function applyStyles(_ref) {
  var state = _ref.state;
  Object.keys(state.elements).forEach(function (name) {
    var style = state.styles[name] || {};
    var attributes = state.attributes[name] || {};
    var element = state.elements[name]; // arrow is optional + virtual elements

    if (!isHTMLElement(element) || !getNodeName(element)) {
      return;
    } // Flow doesn't support to extend this property, but it's the most
    // effective way to apply styles to an HTMLElement
    // $FlowFixMe


    Object.assign(element.style, style);
    Object.keys(attributes).forEach(function (name) {
      var value = attributes[name];

      if (value === false) {
        element.removeAttribute(name);
      } else {
        element.setAttribute(name, value === true ? '' : value);
      }
    });
  });
}

function effect(_ref2) {
  var state = _ref2.state;
  var initialStyles = {
    popper: {
      position: state.options.strategy,
      left: '0',
      top: '0',
      margin: '0'
    },
    arrow: {
      position: 'absolute'
    },
    reference: {}
  };
  Object.assign(state.elements.popper.style, initialStyles.popper);

  if (state.elements.arrow) {
    Object.assign(state.elements.arrow.style, initialStyles.arrow);
  }

  return function () {
    Object.keys(state.elements).forEach(function (name) {
      var element = state.elements[name];
      var attributes = state.attributes[name] || {};
      var styleProperties = Object.keys(state.styles.hasOwnProperty(name) ? state.styles[name] : initialStyles[name]); // Set all values to an empty string to unset them

      var style = styleProperties.reduce(function (style, property) {
        style[property] = '';
        return style;
      }, {}); // arrow is optional + virtual elements

      if (!isHTMLElement(element) || !getNodeName(element)) {
        return;
      } // Flow doesn't support to extend this property, but it's the most
      // effective way to apply styles to an HTMLElement
      // $FlowFixMe


      Object.assign(element.style, style);
      Object.keys(attributes).forEach(function (attribute) {
        element.removeAttribute(attribute);
      });
    });
  };
} // eslint-disable-next-line import/no-unused-modules


var applyStyles$1 = {
  name: 'applyStyles',
  enabled: true,
  phase: 'write',
  fn: applyStyles,
  effect: effect,
  requires: ['computeStyles']
};

function getBasePlacement(placement) {
  return placement.split('-')[0];
}

// Returns the layout rect of an element relative to its offsetParent. Layout
// means it doesn't take into account transforms.
function getLayoutRect(element) {
  return {
    x: element.offsetLeft,
    y: element.offsetTop,
    width: element.offsetWidth,
    height: element.offsetHeight
  };
}

function contains(parent, child) {
  var rootNode = child.getRootNode && child.getRootNode(); // First, attempt with faster native method

  if (parent.contains(child)) {
    return true;
  } // then fallback to custom implementation with Shadow DOM support
  else if (rootNode && isShadowRoot(rootNode)) {
      var next = child;

      do {
        if (next && parent.isSameNode(next)) {
          return true;
        } // $FlowFixMe: need a better way to handle this...


        next = next.parentNode || next.host;
      } while (next);
    } // Give up, the result is false


  return false;
}

function getComputedStyle(element) {
  return getWindow(element).getComputedStyle(element);
}

function isTableElement(element) {
  return ['table', 'td', 'th'].indexOf(getNodeName(element)) >= 0;
}

function getDocumentElement(element) {
  // $FlowFixMe: assume body is always available
  return ((isElement$1(element) ? element.ownerDocument : element.document) || window.document).documentElement;
}

function getParentNode(element) {
  if (getNodeName(element) === 'html') {
    return element;
  }

  return (// $FlowFixMe: this is a quicker (but less type safe) way to save quite some bytes from the bundle
    element.assignedSlot || // step into the shadow DOM of the parent of a slotted node
    element.parentNode || // DOM Element detected
    // $FlowFixMe: need a better way to handle this...
    element.host || // ShadowRoot detected
    // $FlowFixMe: HTMLElement is a Node
    getDocumentElement(element) // fallback

  );
}

function getTrueOffsetParent(element) {
  if (!isHTMLElement(element) || // https://github.com/popperjs/popper-core/issues/837
  getComputedStyle(element).position === 'fixed') {
    return null;
  }

  var offsetParent = element.offsetParent;

  if (offsetParent) {
    var html = getDocumentElement(offsetParent);

    if (getNodeName(offsetParent) === 'body' && getComputedStyle(offsetParent).position === 'static' && getComputedStyle(html).position !== 'static') {
      return html;
    }
  }

  return offsetParent;
} // `.offsetParent` reports `null` for fixed elements, while absolute elements
// return the containing block


function getContainingBlock(element) {
  var currentNode = getParentNode(element);

  while (isHTMLElement(currentNode) && ['html', 'body'].indexOf(getNodeName(currentNode)) < 0) {
    var css = getComputedStyle(currentNode); // This is non-exhaustive but covers the most common CSS properties that
    // create a containing block.

    if (css.transform !== 'none' || css.perspective !== 'none' || css.willChange && css.willChange !== 'auto') {
      return currentNode;
    } else {
      currentNode = currentNode.parentNode;
    }
  }

  return null;
} // Gets the closest ancestor positioned element. Handles some edge cases,
// such as table ancestors and cross browser bugs.


function getOffsetParent(element) {
  var window = getWindow(element);
  var offsetParent = getTrueOffsetParent(element);

  while (offsetParent && isTableElement(offsetParent) && getComputedStyle(offsetParent).position === 'static') {
    offsetParent = getTrueOffsetParent(offsetParent);
  }

  if (offsetParent && getNodeName(offsetParent) === 'body' && getComputedStyle(offsetParent).position === 'static') {
    return window;
  }

  return offsetParent || getContainingBlock(element) || window;
}

function getMainAxisFromPlacement(placement) {
  return ['top', 'bottom'].indexOf(placement) >= 0 ? 'x' : 'y';
}

function within(min, value, max) {
  return Math.max(min, Math.min(value, max));
}

function getFreshSideObject() {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  };
}

function mergePaddingObject(paddingObject) {
  return Object.assign(Object.assign({}, getFreshSideObject()), paddingObject);
}

function expandToHashMap(value, keys) {
  return keys.reduce(function (hashMap, key) {
    hashMap[key] = value;
    return hashMap;
  }, {});
}

function arrow(_ref) {
  var _state$modifiersData$;

  var state = _ref.state,
      name = _ref.name;
  var arrowElement = state.elements.arrow;
  var popperOffsets = state.modifiersData.popperOffsets;
  var basePlacement = getBasePlacement(state.placement);
  var axis = getMainAxisFromPlacement(basePlacement);
  var isVertical = [left, right].indexOf(basePlacement) >= 0;
  var len = isVertical ? 'height' : 'width';

  if (!arrowElement || !popperOffsets) {
    return;
  }

  var paddingObject = state.modifiersData[name + "#persistent"].padding;
  var arrowRect = getLayoutRect(arrowElement);
  var minProp = axis === 'y' ? top : left;
  var maxProp = axis === 'y' ? bottom : right;
  var endDiff = state.rects.reference[len] + state.rects.reference[axis] - popperOffsets[axis] - state.rects.popper[len];
  var startDiff = popperOffsets[axis] - state.rects.reference[axis];
  var arrowOffsetParent = getOffsetParent(arrowElement);
  var clientSize = arrowOffsetParent ? axis === 'y' ? arrowOffsetParent.clientHeight || 0 : arrowOffsetParent.clientWidth || 0 : 0;
  var centerToReference = endDiff / 2 - startDiff / 2; // Make sure the arrow doesn't overflow the popper if the center point is
  // outside of the popper bounds

  var min = paddingObject[minProp];
  var max = clientSize - arrowRect[len] - paddingObject[maxProp];
  var center = clientSize / 2 - arrowRect[len] / 2 + centerToReference;
  var offset = within(min, center, max); // Prevents breaking syntax highlighting...

  var axisProp = axis;
  state.modifiersData[name] = (_state$modifiersData$ = {}, _state$modifiersData$[axisProp] = offset, _state$modifiersData$.centerOffset = offset - center, _state$modifiersData$);
}

function effect$1(_ref2) {
  var state = _ref2.state,
      options = _ref2.options,
      name = _ref2.name;
  var _options$element = options.element,
      arrowElement = _options$element === void 0 ? '[data-popper-arrow]' : _options$element,
      _options$padding = options.padding,
      padding = _options$padding === void 0 ? 0 : _options$padding;

  if (arrowElement == null) {
    return;
  } // CSS selector


  if (typeof arrowElement === 'string') {
    arrowElement = state.elements.popper.querySelector(arrowElement);

    if (!arrowElement) {
      return;
    }
  }

  if (process.env.NODE_ENV !== "production") {
    if (!isHTMLElement(arrowElement)) {
      console.error(['Popper: "arrow" element must be an HTMLElement (not an SVGElement).', 'To use an SVG arrow, wrap it in an HTMLElement that will be used as', 'the arrow.'].join(' '));
    }
  }

  if (!contains(state.elements.popper, arrowElement)) {
    if (process.env.NODE_ENV !== "production") {
      console.error(['Popper: "arrow" modifier\'s `element` must be a child of the popper', 'element.'].join(' '));
    }

    return;
  }

  state.elements.arrow = arrowElement;
  state.modifiersData[name + "#persistent"] = {
    padding: mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements))
  };
} // eslint-disable-next-line import/no-unused-modules


var arrow$1 = {
  name: 'arrow',
  enabled: true,
  phase: 'main',
  fn: arrow,
  effect: effect$1,
  requires: ['popperOffsets'],
  requiresIfExists: ['preventOverflow']
};

var unsetSides = {
  top: 'auto',
  right: 'auto',
  bottom: 'auto',
  left: 'auto'
}; // Round the offsets to the nearest suitable subpixel based on the DPR.
// Zooming can change the DPR, but it seems to report a value that will
// cleanly divide the values into the appropriate subpixels.

function roundOffsets(_ref) {
  var x = _ref.x,
      y = _ref.y;
  var win = window;
  var dpr = win.devicePixelRatio || 1;
  return {
    x: Math.round(x * dpr) / dpr || 0,
    y: Math.round(y * dpr) / dpr || 0
  };
}

function mapToStyles(_ref2) {
  var _Object$assign2;

  var popper = _ref2.popper,
      popperRect = _ref2.popperRect,
      placement = _ref2.placement,
      offsets = _ref2.offsets,
      position = _ref2.position,
      gpuAcceleration = _ref2.gpuAcceleration,
      adaptive = _ref2.adaptive;

  var _roundOffsets = roundOffsets(offsets),
      x = _roundOffsets.x,
      y = _roundOffsets.y;

  var hasX = offsets.hasOwnProperty('x');
  var hasY = offsets.hasOwnProperty('y');
  var sideX = left;
  var sideY = top;
  var win = window;

  if (adaptive) {
    var offsetParent = getOffsetParent(popper);

    if (offsetParent === getWindow(popper)) {
      offsetParent = getDocumentElement(popper);
    } // $FlowFixMe: force type refinement, we compare offsetParent with window above, but Flow doesn't detect it

    /*:: offsetParent = (offsetParent: Element); */


    if (placement === top) {
      sideY = bottom;
      y -= offsetParent.clientHeight - popperRect.height;
      y *= gpuAcceleration ? 1 : -1;
    }

    if (placement === left) {
      sideX = right;
      x -= offsetParent.clientWidth - popperRect.width;
      x *= gpuAcceleration ? 1 : -1;
    }
  }

  var commonStyles = Object.assign({
    position: position
  }, adaptive && unsetSides);

  if (gpuAcceleration) {
    var _Object$assign;

    return Object.assign(Object.assign({}, commonStyles), {}, (_Object$assign = {}, _Object$assign[sideY] = hasY ? '0' : '', _Object$assign[sideX] = hasX ? '0' : '', _Object$assign.transform = (win.devicePixelRatio || 1) < 2 ? "translate(" + x + "px, " + y + "px)" : "translate3d(" + x + "px, " + y + "px, 0)", _Object$assign));
  }

  return Object.assign(Object.assign({}, commonStyles), {}, (_Object$assign2 = {}, _Object$assign2[sideY] = hasY ? y + "px" : '', _Object$assign2[sideX] = hasX ? x + "px" : '', _Object$assign2.transform = '', _Object$assign2));
}

function computeStyles(_ref3) {
  var state = _ref3.state,
      options = _ref3.options;
  var _options$gpuAccelerat = options.gpuAcceleration,
      gpuAcceleration = _options$gpuAccelerat === void 0 ? true : _options$gpuAccelerat,
      _options$adaptive = options.adaptive,
      adaptive = _options$adaptive === void 0 ? true : _options$adaptive;

  if (process.env.NODE_ENV !== "production") {
    var transitionProperty = getComputedStyle(state.elements.popper).transitionProperty || '';

    if (adaptive && ['transform', 'top', 'right', 'bottom', 'left'].some(function (property) {
      return transitionProperty.indexOf(property) >= 0;
    })) {
      console.warn(['Popper: Detected CSS transitions on at least one of the following', 'CSS properties: "transform", "top", "right", "bottom", "left".', '\n\n', 'Disable the "computeStyles" modifier\'s `adaptive` option to allow', 'for smooth transitions, or remove these properties from the CSS', 'transition declaration on the popper element if only transitioning', 'opacity or background-color for example.', '\n\n', 'We recommend using the popper element as a wrapper around an inner', 'element that can have any CSS property transitioned for animations.'].join(' '));
    }
  }

  var commonStyles = {
    placement: getBasePlacement(state.placement),
    popper: state.elements.popper,
    popperRect: state.rects.popper,
    gpuAcceleration: gpuAcceleration
  };

  if (state.modifiersData.popperOffsets != null) {
    state.styles.popper = Object.assign(Object.assign({}, state.styles.popper), mapToStyles(Object.assign(Object.assign({}, commonStyles), {}, {
      offsets: state.modifiersData.popperOffsets,
      position: state.options.strategy,
      adaptive: adaptive
    })));
  }

  if (state.modifiersData.arrow != null) {
    state.styles.arrow = Object.assign(Object.assign({}, state.styles.arrow), mapToStyles(Object.assign(Object.assign({}, commonStyles), {}, {
      offsets: state.modifiersData.arrow,
      position: 'absolute',
      adaptive: false
    })));
  }

  state.attributes.popper = Object.assign(Object.assign({}, state.attributes.popper), {}, {
    'data-popper-placement': state.placement
  });
} // eslint-disable-next-line import/no-unused-modules


var computeStyles$1 = {
  name: 'computeStyles',
  enabled: true,
  phase: 'beforeWrite',
  fn: computeStyles,
  data: {}
};

var passive = {
  passive: true
};

function effect$2(_ref) {
  var state = _ref.state,
      instance = _ref.instance,
      options = _ref.options;
  var _options$scroll = options.scroll,
      scroll = _options$scroll === void 0 ? true : _options$scroll,
      _options$resize = options.resize,
      resize = _options$resize === void 0 ? true : _options$resize;
  var window = getWindow(state.elements.popper);
  var scrollParents = [].concat(state.scrollParents.reference, state.scrollParents.popper);

  if (scroll) {
    scrollParents.forEach(function (scrollParent) {
      scrollParent.addEventListener('scroll', instance.update, passive);
    });
  }

  if (resize) {
    window.addEventListener('resize', instance.update, passive);
  }

  return function () {
    if (scroll) {
      scrollParents.forEach(function (scrollParent) {
        scrollParent.removeEventListener('scroll', instance.update, passive);
      });
    }

    if (resize) {
      window.removeEventListener('resize', instance.update, passive);
    }
  };
} // eslint-disable-next-line import/no-unused-modules


var eventListeners = {
  name: 'eventListeners',
  enabled: true,
  phase: 'write',
  fn: function fn() {},
  effect: effect$2,
  data: {}
};

var hash = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom'
};
function getOppositePlacement(placement) {
  return placement.replace(/left|right|bottom|top/g, function (matched) {
    return hash[matched];
  });
}

var hash$1 = {
  start: 'end',
  end: 'start'
};
function getOppositeVariationPlacement(placement) {
  return placement.replace(/start|end/g, function (matched) {
    return hash$1[matched];
  });
}

function getBoundingClientRect(element) {
  var rect = element.getBoundingClientRect();
  return {
    width: rect.width,
    height: rect.height,
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left,
    x: rect.left,
    y: rect.top
  };
}

function getWindowScroll(node) {
  var win = getWindow(node);
  var scrollLeft = win.pageXOffset;
  var scrollTop = win.pageYOffset;
  return {
    scrollLeft: scrollLeft,
    scrollTop: scrollTop
  };
}

function getWindowScrollBarX(element) {
  // If <html> has a CSS width greater than the viewport, then this will be
  // incorrect for RTL.
  // Popper 1 is broken in this case and never had a bug report so let's assume
  // it's not an issue. I don't think anyone ever specifies width on <html>
  // anyway.
  // Browsers where the left scrollbar doesn't cause an issue report `0` for
  // this (e.g. Edge 2019, IE11, Safari)
  return getBoundingClientRect(getDocumentElement(element)).left + getWindowScroll(element).scrollLeft;
}

function getViewportRect(element) {
  var win = getWindow(element);
  var html = getDocumentElement(element);
  var visualViewport = win.visualViewport;
  var width = html.clientWidth;
  var height = html.clientHeight;
  var x = 0;
  var y = 0; // NB: This isn't supported on iOS <= 12. If the keyboard is open, the popper
  // can be obscured underneath it.
  // Also, `html.clientHeight` adds the bottom bar height in Safari iOS, even
  // if it isn't open, so if this isn't available, the popper will be detected
  // to overflow the bottom of the screen too early.

  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height; // Uses Layout Viewport (like Chrome; Safari does not currently)
    // In Chrome, it returns a value very close to 0 (+/-) but contains rounding
    // errors due to floating point numbers, so we need to check precision.
    // Safari returns a number <= 0, usually < -1 when pinch-zoomed
    // Feature detection fails in mobile emulation mode in Chrome.
    // Math.abs(win.innerWidth / visualViewport.scale - visualViewport.width) <
    // 0.001
    // Fallback here: "Not Safari" userAgent

    if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }

  return {
    width: width,
    height: height,
    x: x + getWindowScrollBarX(element),
    y: y
  };
}

// of the `<html>` and `<body>` rect bounds if horizontally scrollable

function getDocumentRect(element) {
  var html = getDocumentElement(element);
  var winScroll = getWindowScroll(element);
  var body = element.ownerDocument.body;
  var width = Math.max(html.scrollWidth, html.clientWidth, body ? body.scrollWidth : 0, body ? body.clientWidth : 0);
  var height = Math.max(html.scrollHeight, html.clientHeight, body ? body.scrollHeight : 0, body ? body.clientHeight : 0);
  var x = -winScroll.scrollLeft + getWindowScrollBarX(element);
  var y = -winScroll.scrollTop;

  if (getComputedStyle(body || html).direction === 'rtl') {
    x += Math.max(html.clientWidth, body ? body.clientWidth : 0) - width;
  }

  return {
    width: width,
    height: height,
    x: x,
    y: y
  };
}

function isScrollParent(element) {
  // Firefox wants us to check `-x` and `-y` variations as well
  var _getComputedStyle = getComputedStyle(element),
      overflow = _getComputedStyle.overflow,
      overflowX = _getComputedStyle.overflowX,
      overflowY = _getComputedStyle.overflowY;

  return /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX);
}

function getScrollParent(node) {
  if (['html', 'body', '#document'].indexOf(getNodeName(node)) >= 0) {
    // $FlowFixMe: assume body is always available
    return node.ownerDocument.body;
  }

  if (isHTMLElement(node) && isScrollParent(node)) {
    return node;
  }

  return getScrollParent(getParentNode(node));
}

/*
given a DOM element, return the list of all scroll parents, up the list of ancesors
until we get to the top window object. This list is what we attach scroll listeners
to, because if any of these parent elements scroll, we'll need to re-calculate the 
reference element's position.
*/

function listScrollParents(element, list) {
  if (list === void 0) {
    list = [];
  }

  var scrollParent = getScrollParent(element);
  var isBody = getNodeName(scrollParent) === 'body';
  var win = getWindow(scrollParent);
  var target = isBody ? [win].concat(win.visualViewport || [], isScrollParent(scrollParent) ? scrollParent : []) : scrollParent;
  var updatedList = list.concat(target);
  return isBody ? updatedList : // $FlowFixMe: isBody tells us target will be an HTMLElement here
  updatedList.concat(listScrollParents(getParentNode(target)));
}

function rectToClientRect(rect) {
  return Object.assign(Object.assign({}, rect), {}, {
    left: rect.x,
    top: rect.y,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  });
}

function getInnerBoundingClientRect(element) {
  var rect = getBoundingClientRect(element);
  rect.top = rect.top + element.clientTop;
  rect.left = rect.left + element.clientLeft;
  rect.bottom = rect.top + element.clientHeight;
  rect.right = rect.left + element.clientWidth;
  rect.width = element.clientWidth;
  rect.height = element.clientHeight;
  rect.x = rect.left;
  rect.y = rect.top;
  return rect;
}

function getClientRectFromMixedType(element, clippingParent) {
  return clippingParent === viewport ? rectToClientRect(getViewportRect(element)) : isHTMLElement(clippingParent) ? getInnerBoundingClientRect(clippingParent) : rectToClientRect(getDocumentRect(getDocumentElement(element)));
} // A "clipping parent" is an overflowable container with the characteristic of
// clipping (or hiding) overflowing elements with a position different from
// `initial`


function getClippingParents(element) {
  var clippingParents = listScrollParents(getParentNode(element));
  var canEscapeClipping = ['absolute', 'fixed'].indexOf(getComputedStyle(element).position) >= 0;
  var clipperElement = canEscapeClipping && isHTMLElement(element) ? getOffsetParent(element) : element;

  if (!isElement$1(clipperElement)) {
    return [];
  } // $FlowFixMe: https://github.com/facebook/flow/issues/1414


  return clippingParents.filter(function (clippingParent) {
    return isElement$1(clippingParent) && contains(clippingParent, clipperElement) && getNodeName(clippingParent) !== 'body';
  });
} // Gets the maximum area that the element is visible in due to any number of
// clipping parents


function getClippingRect(element, boundary, rootBoundary) {
  var mainClippingParents = boundary === 'clippingParents' ? getClippingParents(element) : [].concat(boundary);
  var clippingParents = [].concat(mainClippingParents, [rootBoundary]);
  var firstClippingParent = clippingParents[0];
  var clippingRect = clippingParents.reduce(function (accRect, clippingParent) {
    var rect = getClientRectFromMixedType(element, clippingParent);
    accRect.top = Math.max(rect.top, accRect.top);
    accRect.right = Math.min(rect.right, accRect.right);
    accRect.bottom = Math.min(rect.bottom, accRect.bottom);
    accRect.left = Math.max(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromMixedType(element, firstClippingParent));
  clippingRect.width = clippingRect.right - clippingRect.left;
  clippingRect.height = clippingRect.bottom - clippingRect.top;
  clippingRect.x = clippingRect.left;
  clippingRect.y = clippingRect.top;
  return clippingRect;
}

function getVariation(placement) {
  return placement.split('-')[1];
}

function computeOffsets(_ref) {
  var reference = _ref.reference,
      element = _ref.element,
      placement = _ref.placement;
  var basePlacement = placement ? getBasePlacement(placement) : null;
  var variation = placement ? getVariation(placement) : null;
  var commonX = reference.x + reference.width / 2 - element.width / 2;
  var commonY = reference.y + reference.height / 2 - element.height / 2;
  var offsets;

  switch (basePlacement) {
    case top:
      offsets = {
        x: commonX,
        y: reference.y - element.height
      };
      break;

    case bottom:
      offsets = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;

    case right:
      offsets = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;

    case left:
      offsets = {
        x: reference.x - element.width,
        y: commonY
      };
      break;

    default:
      offsets = {
        x: reference.x,
        y: reference.y
      };
  }

  var mainAxis = basePlacement ? getMainAxisFromPlacement(basePlacement) : null;

  if (mainAxis != null) {
    var len = mainAxis === 'y' ? 'height' : 'width';

    switch (variation) {
      case start:
        offsets[mainAxis] = Math.floor(offsets[mainAxis]) - Math.floor(reference[len] / 2 - element[len] / 2);
        break;

      case end:
        offsets[mainAxis] = Math.floor(offsets[mainAxis]) + Math.ceil(reference[len] / 2 - element[len] / 2);
        break;
    }
  }

  return offsets;
}

function detectOverflow(state, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      _options$placement = _options.placement,
      placement = _options$placement === void 0 ? state.placement : _options$placement,
      _options$boundary = _options.boundary,
      boundary = _options$boundary === void 0 ? clippingParents : _options$boundary,
      _options$rootBoundary = _options.rootBoundary,
      rootBoundary = _options$rootBoundary === void 0 ? viewport : _options$rootBoundary,
      _options$elementConte = _options.elementContext,
      elementContext = _options$elementConte === void 0 ? popper : _options$elementConte,
      _options$altBoundary = _options.altBoundary,
      altBoundary = _options$altBoundary === void 0 ? false : _options$altBoundary,
      _options$padding = _options.padding,
      padding = _options$padding === void 0 ? 0 : _options$padding;
  var paddingObject = mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
  var altContext = elementContext === popper ? reference : popper;
  var referenceElement = state.elements.reference;
  var popperRect = state.rects.popper;
  var element = state.elements[altBoundary ? altContext : elementContext];
  var clippingClientRect = getClippingRect(isElement$1(element) ? element : element.contextElement || getDocumentElement(state.elements.popper), boundary, rootBoundary);
  var referenceClientRect = getBoundingClientRect(referenceElement);
  var popperOffsets = computeOffsets({
    reference: referenceClientRect,
    element: popperRect,
    strategy: 'absolute',
    placement: placement
  });
  var popperClientRect = rectToClientRect(Object.assign(Object.assign({}, popperRect), popperOffsets));
  var elementClientRect = elementContext === popper ? popperClientRect : referenceClientRect; // positive = overflowing the clipping rect
  // 0 or negative = within the clipping rect

  var overflowOffsets = {
    top: clippingClientRect.top - elementClientRect.top + paddingObject.top,
    bottom: elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom,
    left: clippingClientRect.left - elementClientRect.left + paddingObject.left,
    right: elementClientRect.right - clippingClientRect.right + paddingObject.right
  };
  var offsetData = state.modifiersData.offset; // Offsets can be applied only to the popper element

  if (elementContext === popper && offsetData) {
    var offset = offsetData[placement];
    Object.keys(overflowOffsets).forEach(function (key) {
      var multiply = [right, bottom].indexOf(key) >= 0 ? 1 : -1;
      var axis = [top, bottom].indexOf(key) >= 0 ? 'y' : 'x';
      overflowOffsets[key] += offset[axis] * multiply;
    });
  }

  return overflowOffsets;
}

/*:: type OverflowsMap = { [ComputedPlacement]: number }; */

/*;; type OverflowsMap = { [key in ComputedPlacement]: number }; */
function computeAutoPlacement(state, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      placement = _options.placement,
      boundary = _options.boundary,
      rootBoundary = _options.rootBoundary,
      padding = _options.padding,
      flipVariations = _options.flipVariations,
      _options$allowedAutoP = _options.allowedAutoPlacements,
      allowedAutoPlacements = _options$allowedAutoP === void 0 ? placements : _options$allowedAutoP;
  var variation = getVariation(placement);
  var placements$1 = variation ? flipVariations ? variationPlacements : variationPlacements.filter(function (placement) {
    return getVariation(placement) === variation;
  }) : basePlacements; // $FlowFixMe

  var allowedPlacements = placements$1.filter(function (placement) {
    return allowedAutoPlacements.indexOf(placement) >= 0;
  });

  if (allowedPlacements.length === 0) {
    allowedPlacements = placements$1;

    if (process.env.NODE_ENV !== "production") {
      console.error(['Popper: The `allowedAutoPlacements` option did not allow any', 'placements. Ensure the `placement` option matches the variation', 'of the allowed placements.', 'For example, "auto" cannot be used to allow "bottom-start".', 'Use "auto-start" instead.'].join(' '));
    }
  } // $FlowFixMe: Flow seems to have problems with two array unions...


  var overflows = allowedPlacements.reduce(function (acc, placement) {
    acc[placement] = detectOverflow(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding
    })[getBasePlacement(placement)];
    return acc;
  }, {});
  return Object.keys(overflows).sort(function (a, b) {
    return overflows[a] - overflows[b];
  });
}

function getExpandedFallbackPlacements(placement) {
  if (getBasePlacement(placement) === auto) {
    return [];
  }

  var oppositePlacement = getOppositePlacement(placement);
  return [getOppositeVariationPlacement(placement), oppositePlacement, getOppositeVariationPlacement(oppositePlacement)];
}

function flip(_ref) {
  var state = _ref.state,
      options = _ref.options,
      name = _ref.name;

  if (state.modifiersData[name]._skip) {
    return;
  }

  var _options$mainAxis = options.mainAxis,
      checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
      _options$altAxis = options.altAxis,
      checkAltAxis = _options$altAxis === void 0 ? true : _options$altAxis,
      specifiedFallbackPlacements = options.fallbackPlacements,
      padding = options.padding,
      boundary = options.boundary,
      rootBoundary = options.rootBoundary,
      altBoundary = options.altBoundary,
      _options$flipVariatio = options.flipVariations,
      flipVariations = _options$flipVariatio === void 0 ? true : _options$flipVariatio,
      allowedAutoPlacements = options.allowedAutoPlacements;
  var preferredPlacement = state.options.placement;
  var basePlacement = getBasePlacement(preferredPlacement);
  var isBasePlacement = basePlacement === preferredPlacement;
  var fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipVariations ? [getOppositePlacement(preferredPlacement)] : getExpandedFallbackPlacements(preferredPlacement));
  var placements = [preferredPlacement].concat(fallbackPlacements).reduce(function (acc, placement) {
    return acc.concat(getBasePlacement(placement) === auto ? computeAutoPlacement(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding,
      flipVariations: flipVariations,
      allowedAutoPlacements: allowedAutoPlacements
    }) : placement);
  }, []);
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var checksMap = new Map();
  var makeFallbackChecks = true;
  var firstFittingPlacement = placements[0];

  for (var i = 0; i < placements.length; i++) {
    var placement = placements[i];

    var _basePlacement = getBasePlacement(placement);

    var isStartVariation = getVariation(placement) === start;
    var isVertical = [top, bottom].indexOf(_basePlacement) >= 0;
    var len = isVertical ? 'width' : 'height';
    var overflow = detectOverflow(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      altBoundary: altBoundary,
      padding: padding
    });
    var mainVariationSide = isVertical ? isStartVariation ? right : left : isStartVariation ? bottom : top;

    if (referenceRect[len] > popperRect[len]) {
      mainVariationSide = getOppositePlacement(mainVariationSide);
    }

    var altVariationSide = getOppositePlacement(mainVariationSide);
    var checks = [];

    if (checkMainAxis) {
      checks.push(overflow[_basePlacement] <= 0);
    }

    if (checkAltAxis) {
      checks.push(overflow[mainVariationSide] <= 0, overflow[altVariationSide] <= 0);
    }

    if (checks.every(function (check) {
      return check;
    })) {
      firstFittingPlacement = placement;
      makeFallbackChecks = false;
      break;
    }

    checksMap.set(placement, checks);
  }

  if (makeFallbackChecks) {
    // `2` may be desired in some cases – research later
    var numberOfChecks = flipVariations ? 3 : 1;

    var _loop = function _loop(_i) {
      var fittingPlacement = placements.find(function (placement) {
        var checks = checksMap.get(placement);

        if (checks) {
          return checks.slice(0, _i).every(function (check) {
            return check;
          });
        }
      });

      if (fittingPlacement) {
        firstFittingPlacement = fittingPlacement;
        return "break";
      }
    };

    for (var _i = numberOfChecks; _i > 0; _i--) {
      var _ret = _loop(_i);

      if (_ret === "break") break;
    }
  }

  if (state.placement !== firstFittingPlacement) {
    state.modifiersData[name]._skip = true;
    state.placement = firstFittingPlacement;
    state.reset = true;
  }
} // eslint-disable-next-line import/no-unused-modules


var flip$1 = {
  name: 'flip',
  enabled: true,
  phase: 'main',
  fn: flip,
  requiresIfExists: ['offset'],
  data: {
    _skip: false
  }
};

function getSideOffsets(overflow, rect, preventedOffsets) {
  if (preventedOffsets === void 0) {
    preventedOffsets = {
      x: 0,
      y: 0
    };
  }

  return {
    top: overflow.top - rect.height - preventedOffsets.y,
    right: overflow.right - rect.width + preventedOffsets.x,
    bottom: overflow.bottom - rect.height + preventedOffsets.y,
    left: overflow.left - rect.width - preventedOffsets.x
  };
}

function isAnySideFullyClipped(overflow) {
  return [top, right, bottom, left].some(function (side) {
    return overflow[side] >= 0;
  });
}

function hide(_ref) {
  var state = _ref.state,
      name = _ref.name;
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var preventedOffsets = state.modifiersData.preventOverflow;
  var referenceOverflow = detectOverflow(state, {
    elementContext: 'reference'
  });
  var popperAltOverflow = detectOverflow(state, {
    altBoundary: true
  });
  var referenceClippingOffsets = getSideOffsets(referenceOverflow, referenceRect);
  var popperEscapeOffsets = getSideOffsets(popperAltOverflow, popperRect, preventedOffsets);
  var isReferenceHidden = isAnySideFullyClipped(referenceClippingOffsets);
  var hasPopperEscaped = isAnySideFullyClipped(popperEscapeOffsets);
  state.modifiersData[name] = {
    referenceClippingOffsets: referenceClippingOffsets,
    popperEscapeOffsets: popperEscapeOffsets,
    isReferenceHidden: isReferenceHidden,
    hasPopperEscaped: hasPopperEscaped
  };
  state.attributes.popper = Object.assign(Object.assign({}, state.attributes.popper), {}, {
    'data-popper-reference-hidden': isReferenceHidden,
    'data-popper-escaped': hasPopperEscaped
  });
} // eslint-disable-next-line import/no-unused-modules


var hide$1 = {
  name: 'hide',
  enabled: true,
  phase: 'main',
  requiresIfExists: ['preventOverflow'],
  fn: hide
};

function distanceAndSkiddingToXY(placement, rects, offset) {
  var basePlacement = getBasePlacement(placement);
  var invertDistance = [left, top].indexOf(basePlacement) >= 0 ? -1 : 1;

  var _ref = typeof offset === 'function' ? offset(Object.assign(Object.assign({}, rects), {}, {
    placement: placement
  })) : offset,
      skidding = _ref[0],
      distance = _ref[1];

  skidding = skidding || 0;
  distance = (distance || 0) * invertDistance;
  return [left, right].indexOf(basePlacement) >= 0 ? {
    x: distance,
    y: skidding
  } : {
    x: skidding,
    y: distance
  };
}

function offset(_ref2) {
  var state = _ref2.state,
      options = _ref2.options,
      name = _ref2.name;
  var _options$offset = options.offset,
      offset = _options$offset === void 0 ? [0, 0] : _options$offset;
  var data = placements.reduce(function (acc, placement) {
    acc[placement] = distanceAndSkiddingToXY(placement, state.rects, offset);
    return acc;
  }, {});
  var _data$state$placement = data[state.placement],
      x = _data$state$placement.x,
      y = _data$state$placement.y;

  if (state.modifiersData.popperOffsets != null) {
    state.modifiersData.popperOffsets.x += x;
    state.modifiersData.popperOffsets.y += y;
  }

  state.modifiersData[name] = data;
} // eslint-disable-next-line import/no-unused-modules


var offset$1 = {
  name: 'offset',
  enabled: true,
  phase: 'main',
  requires: ['popperOffsets'],
  fn: offset
};

function popperOffsets(_ref) {
  var state = _ref.state,
      name = _ref.name;
  // Offsets are the actual position the popper needs to have to be
  // properly positioned near its reference element
  // This is the most basic placement, and will be adjusted by
  // the modifiers in the next step
  state.modifiersData[name] = computeOffsets({
    reference: state.rects.reference,
    element: state.rects.popper,
    strategy: 'absolute',
    placement: state.placement
  });
} // eslint-disable-next-line import/no-unused-modules


var popperOffsets$1 = {
  name: 'popperOffsets',
  enabled: true,
  phase: 'read',
  fn: popperOffsets,
  data: {}
};

function getAltAxis(axis) {
  return axis === 'x' ? 'y' : 'x';
}

function preventOverflow(_ref) {
  var state = _ref.state,
      options = _ref.options,
      name = _ref.name;
  var _options$mainAxis = options.mainAxis,
      checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
      _options$altAxis = options.altAxis,
      checkAltAxis = _options$altAxis === void 0 ? false : _options$altAxis,
      boundary = options.boundary,
      rootBoundary = options.rootBoundary,
      altBoundary = options.altBoundary,
      padding = options.padding,
      _options$tether = options.tether,
      tether = _options$tether === void 0 ? true : _options$tether,
      _options$tetherOffset = options.tetherOffset,
      tetherOffset = _options$tetherOffset === void 0 ? 0 : _options$tetherOffset;
  var overflow = detectOverflow(state, {
    boundary: boundary,
    rootBoundary: rootBoundary,
    padding: padding,
    altBoundary: altBoundary
  });
  var basePlacement = getBasePlacement(state.placement);
  var variation = getVariation(state.placement);
  var isBasePlacement = !variation;
  var mainAxis = getMainAxisFromPlacement(basePlacement);
  var altAxis = getAltAxis(mainAxis);
  var popperOffsets = state.modifiersData.popperOffsets;
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var tetherOffsetValue = typeof tetherOffset === 'function' ? tetherOffset(Object.assign(Object.assign({}, state.rects), {}, {
    placement: state.placement
  })) : tetherOffset;
  var data = {
    x: 0,
    y: 0
  };

  if (!popperOffsets) {
    return;
  }

  if (checkMainAxis) {
    var mainSide = mainAxis === 'y' ? top : left;
    var altSide = mainAxis === 'y' ? bottom : right;
    var len = mainAxis === 'y' ? 'height' : 'width';
    var offset = popperOffsets[mainAxis];
    var min = popperOffsets[mainAxis] + overflow[mainSide];
    var max = popperOffsets[mainAxis] - overflow[altSide];
    var additive = tether ? -popperRect[len] / 2 : 0;
    var minLen = variation === start ? referenceRect[len] : popperRect[len];
    var maxLen = variation === start ? -popperRect[len] : -referenceRect[len]; // We need to include the arrow in the calculation so the arrow doesn't go
    // outside the reference bounds

    var arrowElement = state.elements.arrow;
    var arrowRect = tether && arrowElement ? getLayoutRect(arrowElement) : {
      width: 0,
      height: 0
    };
    var arrowPaddingObject = state.modifiersData['arrow#persistent'] ? state.modifiersData['arrow#persistent'].padding : getFreshSideObject();
    var arrowPaddingMin = arrowPaddingObject[mainSide];
    var arrowPaddingMax = arrowPaddingObject[altSide]; // If the reference length is smaller than the arrow length, we don't want
    // to include its full size in the calculation. If the reference is small
    // and near the edge of a boundary, the popper can overflow even if the
    // reference is not overflowing as well (e.g. virtual elements with no
    // width or height)

    var arrowLen = within(0, referenceRect[len], arrowRect[len]);
    var minOffset = isBasePlacement ? referenceRect[len] / 2 - additive - arrowLen - arrowPaddingMin - tetherOffsetValue : minLen - arrowLen - arrowPaddingMin - tetherOffsetValue;
    var maxOffset = isBasePlacement ? -referenceRect[len] / 2 + additive + arrowLen + arrowPaddingMax + tetherOffsetValue : maxLen + arrowLen + arrowPaddingMax + tetherOffsetValue;
    var arrowOffsetParent = state.elements.arrow && getOffsetParent(state.elements.arrow);
    var clientOffset = arrowOffsetParent ? mainAxis === 'y' ? arrowOffsetParent.clientTop || 0 : arrowOffsetParent.clientLeft || 0 : 0;
    var offsetModifierValue = state.modifiersData.offset ? state.modifiersData.offset[state.placement][mainAxis] : 0;
    var tetherMin = popperOffsets[mainAxis] + minOffset - offsetModifierValue - clientOffset;
    var tetherMax = popperOffsets[mainAxis] + maxOffset - offsetModifierValue;
    var preventedOffset = within(tether ? Math.min(min, tetherMin) : min, offset, tether ? Math.max(max, tetherMax) : max);
    popperOffsets[mainAxis] = preventedOffset;
    data[mainAxis] = preventedOffset - offset;
  }

  if (checkAltAxis) {
    var _mainSide = mainAxis === 'x' ? top : left;

    var _altSide = mainAxis === 'x' ? bottom : right;

    var _offset = popperOffsets[altAxis];

    var _min = _offset + overflow[_mainSide];

    var _max = _offset - overflow[_altSide];

    var _preventedOffset = within(_min, _offset, _max);

    popperOffsets[altAxis] = _preventedOffset;
    data[altAxis] = _preventedOffset - _offset;
  }

  state.modifiersData[name] = data;
} // eslint-disable-next-line import/no-unused-modules


var preventOverflow$1 = {
  name: 'preventOverflow',
  enabled: true,
  phase: 'main',
  fn: preventOverflow,
  requiresIfExists: ['offset']
};

function getHTMLElementScroll(element) {
  return {
    scrollLeft: element.scrollLeft,
    scrollTop: element.scrollTop
  };
}

function getNodeScroll(node) {
  if (node === getWindow(node) || !isHTMLElement(node)) {
    return getWindowScroll(node);
  } else {
    return getHTMLElementScroll(node);
  }
}

// Composite means it takes into account transforms as well as layout.

function getCompositeRect(elementOrVirtualElement, offsetParent, isFixed) {
  if (isFixed === void 0) {
    isFixed = false;
  }

  var documentElement = getDocumentElement(offsetParent);
  var rect = getBoundingClientRect(elementOrVirtualElement);
  var isOffsetParentAnElement = isHTMLElement(offsetParent);
  var scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  var offsets = {
    x: 0,
    y: 0
  };

  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || // https://github.com/popperjs/popper-core/issues/1078
    isScrollParent(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }

    if (isHTMLElement(offsetParent)) {
      offsets = getBoundingClientRect(offsetParent);
      offsets.x += offsetParent.clientLeft;
      offsets.y += offsetParent.clientTop;
    } else if (documentElement) {
      offsets.x = getWindowScrollBarX(documentElement);
    }
  }

  return {
    x: rect.left + scroll.scrollLeft - offsets.x,
    y: rect.top + scroll.scrollTop - offsets.y,
    width: rect.width,
    height: rect.height
  };
}

function order(modifiers) {
  var map = new Map();
  var visited = new Set();
  var result = [];
  modifiers.forEach(function (modifier) {
    map.set(modifier.name, modifier);
  }); // On visiting object, check for its dependencies and visit them recursively

  function sort(modifier) {
    visited.add(modifier.name);
    var requires = [].concat(modifier.requires || [], modifier.requiresIfExists || []);
    requires.forEach(function (dep) {
      if (!visited.has(dep)) {
        var depModifier = map.get(dep);

        if (depModifier) {
          sort(depModifier);
        }
      }
    });
    result.push(modifier);
  }

  modifiers.forEach(function (modifier) {
    if (!visited.has(modifier.name)) {
      // check for visited object
      sort(modifier);
    }
  });
  return result;
}

function orderModifiers(modifiers) {
  // order based on dependencies
  var orderedModifiers = order(modifiers); // order based on phase

  return modifierPhases.reduce(function (acc, phase) {
    return acc.concat(orderedModifiers.filter(function (modifier) {
      return modifier.phase === phase;
    }));
  }, []);
}

function debounce(fn) {
  var pending;
  return function () {
    if (!pending) {
      pending = new Promise(function (resolve) {
        Promise.resolve().then(function () {
          pending = undefined;
          resolve(fn());
        });
      });
    }

    return pending;
  };
}

function format(str) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  return [].concat(args).reduce(function (p, c) {
    return p.replace(/%s/, c);
  }, str);
}

var INVALID_MODIFIER_ERROR = 'Popper: modifier "%s" provided an invalid %s property, expected %s but got %s';
var MISSING_DEPENDENCY_ERROR = 'Popper: modifier "%s" requires "%s", but "%s" modifier is not available';
var VALID_PROPERTIES = ['name', 'enabled', 'phase', 'fn', 'effect', 'requires', 'options'];
function validateModifiers(modifiers) {
  modifiers.forEach(function (modifier) {
    Object.keys(modifier).forEach(function (key) {
      switch (key) {
        case 'name':
          if (typeof modifier.name !== 'string') {
            console.error(format(INVALID_MODIFIER_ERROR, String(modifier.name), '"name"', '"string"', "\"" + String(modifier.name) + "\""));
          }

          break;

        case 'enabled':
          if (typeof modifier.enabled !== 'boolean') {
            console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"enabled"', '"boolean"', "\"" + String(modifier.enabled) + "\""));
          }

        case 'phase':
          if (modifierPhases.indexOf(modifier.phase) < 0) {
            console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"phase"', "either " + modifierPhases.join(', '), "\"" + String(modifier.phase) + "\""));
          }

          break;

        case 'fn':
          if (typeof modifier.fn !== 'function') {
            console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"fn"', '"function"', "\"" + String(modifier.fn) + "\""));
          }

          break;

        case 'effect':
          if (typeof modifier.effect !== 'function') {
            console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"effect"', '"function"', "\"" + String(modifier.fn) + "\""));
          }

          break;

        case 'requires':
          if (!Array.isArray(modifier.requires)) {
            console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"requires"', '"array"', "\"" + String(modifier.requires) + "\""));
          }

          break;

        case 'requiresIfExists':
          if (!Array.isArray(modifier.requiresIfExists)) {
            console.error(format(INVALID_MODIFIER_ERROR, modifier.name, '"requiresIfExists"', '"array"', "\"" + String(modifier.requiresIfExists) + "\""));
          }

          break;

        case 'options':
        case 'data':
          break;

        default:
          console.error("PopperJS: an invalid property has been provided to the \"" + modifier.name + "\" modifier, valid properties are " + VALID_PROPERTIES.map(function (s) {
            return "\"" + s + "\"";
          }).join(', ') + "; but \"" + key + "\" was provided.");
      }

      modifier.requires && modifier.requires.forEach(function (requirement) {
        if (modifiers.find(function (mod) {
          return mod.name === requirement;
        }) == null) {
          console.error(format(MISSING_DEPENDENCY_ERROR, String(modifier.name), requirement, requirement));
        }
      });
    });
  });
}

function uniqueBy(arr, fn) {
  var identifiers = new Set();
  return arr.filter(function (item) {
    var identifier = fn(item);

    if (!identifiers.has(identifier)) {
      identifiers.add(identifier);
      return true;
    }
  });
}

function mergeByName(modifiers) {
  var merged = modifiers.reduce(function (merged, current) {
    var existing = merged[current.name];
    merged[current.name] = existing ? Object.assign(Object.assign(Object.assign({}, existing), current), {}, {
      options: Object.assign(Object.assign({}, existing.options), current.options),
      data: Object.assign(Object.assign({}, existing.data), current.data)
    }) : current;
    return merged;
  }, {}); // IE11 does not support Object.values

  return Object.keys(merged).map(function (key) {
    return merged[key];
  });
}

var INVALID_ELEMENT_ERROR = 'Popper: Invalid reference or popper argument provided. They must be either a DOM element or virtual element.';
var INFINITE_LOOP_ERROR = 'Popper: An infinite loop in the modifiers cycle has been detected! The cycle has been interrupted to prevent a browser crash.';
var DEFAULT_OPTIONS = {
  placement: 'bottom',
  modifiers: [],
  strategy: 'absolute'
};

function areValidElements() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return !args.some(function (element) {
    return !(element && typeof element.getBoundingClientRect === 'function');
  });
}

function popperGenerator(generatorOptions) {
  if (generatorOptions === void 0) {
    generatorOptions = {};
  }

  var _generatorOptions = generatorOptions,
      _generatorOptions$def = _generatorOptions.defaultModifiers,
      defaultModifiers = _generatorOptions$def === void 0 ? [] : _generatorOptions$def,
      _generatorOptions$def2 = _generatorOptions.defaultOptions,
      defaultOptions = _generatorOptions$def2 === void 0 ? DEFAULT_OPTIONS : _generatorOptions$def2;
  return function createPopper(reference, popper, options) {
    if (options === void 0) {
      options = defaultOptions;
    }

    var state = {
      placement: 'bottom',
      orderedModifiers: [],
      options: Object.assign(Object.assign({}, DEFAULT_OPTIONS), defaultOptions),
      modifiersData: {},
      elements: {
        reference: reference,
        popper: popper
      },
      attributes: {},
      styles: {}
    };
    var effectCleanupFns = [];
    var isDestroyed = false;
    var instance = {
      state: state,
      setOptions: function setOptions(options) {
        cleanupModifierEffects();
        state.options = Object.assign(Object.assign(Object.assign({}, defaultOptions), state.options), options);
        state.scrollParents = {
          reference: isElement$1(reference) ? listScrollParents(reference) : reference.contextElement ? listScrollParents(reference.contextElement) : [],
          popper: listScrollParents(popper)
        }; // Orders the modifiers based on their dependencies and `phase`
        // properties

        var orderedModifiers = orderModifiers(mergeByName([].concat(defaultModifiers, state.options.modifiers))); // Strip out disabled modifiers

        state.orderedModifiers = orderedModifiers.filter(function (m) {
          return m.enabled;
        }); // Validate the provided modifiers so that the consumer will get warned
        // if one of the modifiers is invalid for any reason

        if (process.env.NODE_ENV !== "production") {
          var modifiers = uniqueBy([].concat(orderedModifiers, state.options.modifiers), function (_ref) {
            var name = _ref.name;
            return name;
          });
          validateModifiers(modifiers);

          if (getBasePlacement(state.options.placement) === auto) {
            var flipModifier = state.orderedModifiers.find(function (_ref2) {
              var name = _ref2.name;
              return name === 'flip';
            });

            if (!flipModifier) {
              console.error(['Popper: "auto" placements require the "flip" modifier be', 'present and enabled to work.'].join(' '));
            }
          }

          var _getComputedStyle = getComputedStyle(popper),
              marginTop = _getComputedStyle.marginTop,
              marginRight = _getComputedStyle.marginRight,
              marginBottom = _getComputedStyle.marginBottom,
              marginLeft = _getComputedStyle.marginLeft; // We no longer take into account `margins` on the popper, and it can
          // cause bugs with positioning, so we'll warn the consumer


          if ([marginTop, marginRight, marginBottom, marginLeft].some(function (margin) {
            return parseFloat(margin);
          })) {
            console.warn(['Popper: CSS "margin" styles cannot be used to apply padding', 'between the popper and its reference element or boundary.', 'To replicate margin, use the `offset` modifier, as well as', 'the `padding` option in the `preventOverflow` and `flip`', 'modifiers.'].join(' '));
          }
        }

        runModifierEffects();
        return instance.update();
      },
      // Sync update – it will always be executed, even if not necessary. This
      // is useful for low frequency updates where sync behavior simplifies the
      // logic.
      // For high frequency updates (e.g. `resize` and `scroll` events), always
      // prefer the async Popper#update method
      forceUpdate: function forceUpdate() {
        if (isDestroyed) {
          return;
        }

        var _state$elements = state.elements,
            reference = _state$elements.reference,
            popper = _state$elements.popper; // Don't proceed if `reference` or `popper` are not valid elements
        // anymore

        if (!areValidElements(reference, popper)) {
          if (process.env.NODE_ENV !== "production") {
            console.error(INVALID_ELEMENT_ERROR);
          }

          return;
        } // Store the reference and popper rects to be read by modifiers


        state.rects = {
          reference: getCompositeRect(reference, getOffsetParent(popper), state.options.strategy === 'fixed'),
          popper: getLayoutRect(popper)
        }; // Modifiers have the ability to reset the current update cycle. The
        // most common use case for this is the `flip` modifier changing the
        // placement, which then needs to re-run all the modifiers, because the
        // logic was previously ran for the previous placement and is therefore
        // stale/incorrect

        state.reset = false;
        state.placement = state.options.placement; // On each update cycle, the `modifiersData` property for each modifier
        // is filled with the initial data specified by the modifier. This means
        // it doesn't persist and is fresh on each update.
        // To ensure persistent data, use `${name}#persistent`

        state.orderedModifiers.forEach(function (modifier) {
          return state.modifiersData[modifier.name] = Object.assign({}, modifier.data);
        });
        var __debug_loops__ = 0;

        for (var index = 0; index < state.orderedModifiers.length; index++) {
          if (process.env.NODE_ENV !== "production") {
            __debug_loops__ += 1;

            if (__debug_loops__ > 100) {
              console.error(INFINITE_LOOP_ERROR);
              break;
            }
          }

          if (state.reset === true) {
            state.reset = false;
            index = -1;
            continue;
          }

          var _state$orderedModifie = state.orderedModifiers[index],
              fn = _state$orderedModifie.fn,
              _state$orderedModifie2 = _state$orderedModifie.options,
              _options = _state$orderedModifie2 === void 0 ? {} : _state$orderedModifie2,
              name = _state$orderedModifie.name;

          if (typeof fn === 'function') {
            state = fn({
              state: state,
              options: _options,
              name: name,
              instance: instance
            }) || state;
          }
        }
      },
      // Async and optimistically optimized update – it will not be executed if
      // not necessary (debounced to run at most once-per-tick)
      update: debounce(function () {
        return new Promise(function (resolve) {
          instance.forceUpdate();
          resolve(state);
        });
      }),
      destroy: function destroy() {
        cleanupModifierEffects();
        isDestroyed = true;
      }
    };

    if (!areValidElements(reference, popper)) {
      if (process.env.NODE_ENV !== "production") {
        console.error(INVALID_ELEMENT_ERROR);
      }

      return instance;
    }

    instance.setOptions(options).then(function (state) {
      if (!isDestroyed && options.onFirstUpdate) {
        options.onFirstUpdate(state);
      }
    }); // Modifiers have the ability to execute arbitrary code before the first
    // update cycle runs. They will be executed in the same order as the update
    // cycle. This is useful when a modifier adds some persistent data that
    // other modifiers need to use, but the modifier is run after the dependent
    // one.

    function runModifierEffects() {
      state.orderedModifiers.forEach(function (_ref3) {
        var name = _ref3.name,
            _ref3$options = _ref3.options,
            options = _ref3$options === void 0 ? {} : _ref3$options,
            effect = _ref3.effect;

        if (typeof effect === 'function') {
          var cleanupFn = effect({
            state: state,
            name: name,
            instance: instance,
            options: options
          });

          var noopFn = function noopFn() {};

          effectCleanupFns.push(cleanupFn || noopFn);
        }
      });
    }

    function cleanupModifierEffects() {
      effectCleanupFns.forEach(function (fn) {
        return fn();
      });
      effectCleanupFns = [];
    }

    return instance;
  };
}

var defaultModifiers = [eventListeners, popperOffsets$1, computeStyles$1, applyStyles$1, offset$1, flip$1, preventOverflow$1, arrow$1, hide$1];
var createPopper = /*#__PURE__*/popperGenerator({
  defaultModifiers: defaultModifiers
}); // eslint-disable-next-line import/no-unused-modules

/* global Map:readonly, Set:readonly, ArrayBuffer:readonly */

var hasElementType = typeof Element !== 'undefined';
var hasMap = typeof Map === 'function';
var hasSet = typeof Set === 'function';
var hasArrayBuffer = typeof ArrayBuffer === 'function' && !!ArrayBuffer.isView;

// Note: We **don't** need `envHasBigInt64Array` in fde es6/index.js

function equal(a, b) {
  // START: fast-deep-equal es6/index.js 3.1.1
  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
    if (a.constructor !== b.constructor) return false;

    var length, i, keys;
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (!equal(a[i], b[i])) return false;
      return true;
    }

    // START: Modifications:
    // 1. Extra `has<Type> &&` helpers in initial condition allow es6 code
    //    to co-exist with es5.
    // 2. Replace `for of` with es5 compliant iteration using `for`.
    //    Basically, take:
    //
    //    ```js
    //    for (i of a.entries())
    //      if (!b.has(i[0])) return false;
    //    ```
    //
    //    ... and convert to:
    //
    //    ```js
    //    it = a.entries();
    //    while (!(i = it.next()).done)
    //      if (!b.has(i.value[0])) return false;
    //    ```
    //
    //    **Note**: `i` access switches to `i.value`.
    var it;
    if (hasMap && (a instanceof Map) && (b instanceof Map)) {
      if (a.size !== b.size) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!b.has(i.value[0])) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!equal(i.value[1], b.get(i.value[0]))) return false;
      return true;
    }

    if (hasSet && (a instanceof Set) && (b instanceof Set)) {
      if (a.size !== b.size) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!b.has(i.value[0])) return false;
      return true;
    }
    // END: Modifications

    if (hasArrayBuffer && ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (a[i] !== b[i]) return false;
      return true;
    }

    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
    if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();

    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- !== 0;)
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
    // END: fast-deep-equal

    // START: react-fast-compare
    // custom handling for DOM elements
    if (hasElementType && a instanceof Element) return false;

    // custom handling for React/Preact
    for (i = length; i-- !== 0;) {
      if ((keys[i] === '_owner' || keys[i] === '__v' || keys[i] === '__o') && a.$$typeof) {
        // React-specific: avoid traversing React elements' _owner
        // Preact-specific: avoid traversing Preact elements' __v and __o
        //    __v = $_original / $_vnode
        //    __o = $_owner
        // These properties contain circular references and are not needed when
        // comparing the actual elements (and not their owners)
        // .$$typeof and ._store on just reasonable markers of elements

        continue;
      }

      // all other properties should be traversed as usual
      if (!equal(a[keys[i]], b[keys[i]])) return false;
    }
    // END: react-fast-compare

    // START: fast-deep-equal
    return true;
  }

  return a !== a && b !== b;
}
// end fast-deep-equal

var reactFastCompare = function isEqual(a, b) {
  try {
    return equal(a, b);
  } catch (error) {
    if (((error.message || '').match(/stack|recursion/i))) {
      // warn on circular references, don't crash
      // browsers give this different errors name and messages:
      // chrome/safari: "RangeError", "Maximum call stack size exceeded"
      // firefox: "InternalError", too much recursion"
      // edge: "Error", "Out of stack space"
      console.warn('react-fast-compare cannot handle circular refs');
      return false;
    }
    // some other error. we should definitely know about these
    throw error;
  }
};

var EMPTY_MODIFIERS = [];
var usePopper = function usePopper(referenceElement, popperElement, options) {
  if (options === void 0) {
    options = {};
  }

  var prevOptions = useRef(null);
  var optionsWithDefaults = {
    onFirstUpdate: options.onFirstUpdate,
    placement: options.placement || 'bottom',
    strategy: options.strategy || 'absolute',
    modifiers: options.modifiers || EMPTY_MODIFIERS
  };

  var _React$useState = useState({
    styles: {
      popper: {
        position: optionsWithDefaults.strategy,
        left: '0',
        top: '0'
      }
    },
    attributes: {}
  }),
      state = _React$useState[0],
      setState = _React$useState[1];

  var updateStateModifier = useMemo(function () {
    return {
      name: 'updateState',
      enabled: true,
      phase: 'write',
      fn: function fn(_ref) {
        var state = _ref.state;
        var elements = Object.keys(state.elements);
        setState({
          styles: fromEntries(elements.map(function (element) {
            return [element, state.styles[element] || {}];
          })),
          attributes: fromEntries(elements.map(function (element) {
            return [element, state.attributes[element]];
          }))
        });
      },
      requires: ['computeStyles']
    };
  }, []);
  var popperOptions = useMemo(function () {
    var newOptions = {
      onFirstUpdate: optionsWithDefaults.onFirstUpdate,
      placement: optionsWithDefaults.placement,
      strategy: optionsWithDefaults.strategy,
      modifiers: [].concat(optionsWithDefaults.modifiers, [updateStateModifier, {
        name: 'applyStyles',
        enabled: false
      }])
    };

    if (reactFastCompare(prevOptions.current, newOptions)) {
      return prevOptions.current || newOptions;
    } else {
      prevOptions.current = newOptions;
      return newOptions;
    }
  }, [optionsWithDefaults.onFirstUpdate, optionsWithDefaults.placement, optionsWithDefaults.strategy, optionsWithDefaults.modifiers, updateStateModifier]);
  var popperInstanceRef = useRef();
  useIsomorphicLayoutEffect(function () {
    if (popperInstanceRef.current) {
      popperInstanceRef.current.setOptions(popperOptions);
    }
  }, [popperOptions]);
  useIsomorphicLayoutEffect(function () {
    if (referenceElement == null || popperElement == null) {
      return;
    }

    var createPopper$1 = options.createPopper || createPopper;
    var popperInstance = createPopper$1(referenceElement, popperElement, popperOptions);
    popperInstanceRef.current = popperInstance;
    return function () {
      popperInstance.destroy();
      popperInstanceRef.current = null;
    };
  }, [referenceElement, popperElement, options.createPopper]);
  return {
    state: popperInstanceRef.current ? popperInstanceRef.current.state : null,
    styles: state.styles,
    attributes: state.attributes,
    update: popperInstanceRef.current ? popperInstanceRef.current.update : null,
    forceUpdate: popperInstanceRef.current ? popperInstanceRef.current.forceUpdate : null
  };
};

/**
 * Different possible placements for the popover.
 */
const Placement = {
  AUTO: 'auto',
  AUTO_END: 'auto-end',
  AUTO_START: 'auto-start',
  TOP: 'top',
  TOP_END: 'top-end',
  TOP_START: 'top-start',
  RIGHT: 'right',
  RIGHT_END: 'right-end',
  RIGHT_START: 'right-start',
  BOTTOM: 'bottom',
  BOTTOM_END: 'bottom-end',
  BOTTOM_START: 'bottom-start',
  LEFT: 'left',
  LEFT_END: 'left-end',
  LEFT_START: 'left-start'
};

/**
 * Offset of the popover.
 */

/**
 * Popover elevation index.
 */

/**
 * Popover fit anchor width options.
 */
const FitAnchorWidth = {
  MAX_WIDTH: 'maxWidth',
  MIN_WIDTH: 'minWidth',
  WIDTH: 'width'
};
/**
 * Arrow size (in pixel).
 */
const ARROW_SIZE = 14;

/**
 * Popper js modifier to fit popover min width to the anchor width.
 */
const sameWidth = memoize(anchorWidthOption => ({
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn(_ref) {
    let {
      state
    } = _ref;
    // eslint-disable-next-line no-param-reassign
    state.styles.popper[anchorWidthOption] = `${state.rects.reference.width}px`;
  },
  effect(_ref2) {
    let {
      state
    } = _ref2;
    // eslint-disable-next-line no-param-reassign
    state.elements.popper.style[anchorWidthOption] = `${state.elements.reference.offsetWidth}px`;
  }
}));

/**
 * Popper js modifier to compute max size of the popover.
 */
const maxSize = {
  name: 'maxSize',
  enabled: true,
  phase: 'main',
  requiresIfExists: ['offset', 'preventOverflow', 'flip'],
  fn(_ref3) {
    let {
      state,
      name,
      options
    } = _ref3;
    const overflow = detectOverflow(state, options);
    const {
      y = 0,
      x = 0
    } = state.modifiersData.preventOverflow;
    const {
      width,
      height
    } = state.rects.popper;
    const [basePlacement] = state.placement.split('-');
    const widthProp = basePlacement === 'left' ? 'left' : 'right';
    const heightProp = basePlacement === 'top' ? 'top' : 'bottom';
    // eslint-disable-next-line no-param-reassign
    state.modifiersData[name] = {
      width: width - overflow[widthProp] - x,
      height: height - overflow[heightProp] - y
    };
  }
};

/**
 * Popper js modifier to apply the max height.
 */
const applyMaxHeight = {
  name: 'applyMaxHeight',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['maxSize'],
  fn(_ref4) {
    let {
      state
    } = _ref4;
    const {
      height
    } = state.modifiersData.maxSize;
    // eslint-disable-next-line no-param-reassign
    state.elements.popper.style.maxHeight = `${height - ARROW_SIZE}px`;
  }
};
function usePopoverStyle(_ref5) {
  var _offset$along, _offset$away, _state$placement, _state$rects$popper$y, _state$rects, _state$rects$popper;
  let {
    offset,
    hasArrow,
    fitToAnchorWidth,
    fitWithinViewportHeight,
    boundaryRef,
    anchorRef,
    children,
    placement,
    style,
    zIndex
  } = _ref5;
  const [popperElement, setPopperElement] = useState(null);
  if (navigator.userAgent.includes('jsdom')) {
    // Skip all logic; we don't need popover positioning in jsdom.
    return {
      styles: {},
      attributes: {},
      isPositioned: true,
      popperElement,
      setPopperElement
    };
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [arrowElement, setArrowElement] = useState(null);
  const actualOffset = [(_offset$along = offset === null || offset === void 0 ? void 0 : offset.along) !== null && _offset$along !== void 0 ? _offset$along : 0, ((_offset$away = offset === null || offset === void 0 ? void 0 : offset.away) !== null && _offset$away !== void 0 ? _offset$away : 0) + (hasArrow ? ARROW_SIZE : 0)];
  const modifiers = [{
    name: 'offset',
    options: {
      offset: actualOffset
    }
  }];
  if (hasArrow && arrowElement) {
    modifiers.push({
      name: 'arrow',
      options: {
        element: arrowElement,
        padding: ARROW_SIZE / 2
      }
    });
  }
  if (fitToAnchorWidth) {
    const fitWidth = typeof fitToAnchorWidth === 'string' ? fitToAnchorWidth : FitAnchorWidth.MIN_WIDTH;
    modifiers.push(sameWidth(fitWidth));
  }
  if (fitWithinViewportHeight) {
    modifiers.push(_objectSpread2(_objectSpread2({}, maxSize), {}, {
      options: {
        boundary: boundaryRef === null || boundaryRef === void 0 ? void 0 : boundaryRef.current
      }
    }), applyMaxHeight);
  }
  if (boundaryRef) {
    modifiers.push({
      name: 'flip',
      options: {
        boundary: boundaryRef.current
      }
    }, {
      name: 'preventOverflow',
      options: {
        boundary: boundaryRef.current
      }
    });
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {
    styles,
    attributes,
    state,
    update
  } = usePopper(anchorRef.current, popperElement, {
    placement,
    modifiers
  });
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    update === null || update === void 0 ? void 0 : update();
  }, [children, update]);
  const position = (_state$placement = state === null || state === void 0 ? void 0 : state.placement) !== null && _state$placement !== void 0 ? _state$placement : placement;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const popoverStyle = useMemo(() => {
    const newStyles = _objectSpread2(_objectSpread2(_objectSpread2({}, style), styles.popper), {}, {
      zIndex
    });
    if (fitWithinViewportHeight && !newStyles.maxHeight) {
      newStyles.maxHeight = (WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.innerHeight) || (DOCUMENT === null || DOCUMENT === void 0 ? void 0 : DOCUMENT.documentElement.clientHeight);
    }
    return newStyles;
  }, [style, styles.popper, zIndex, fitWithinViewportHeight]);
  return {
    styles: {
      arrow: styles.arrow,
      popover: popoverStyle
    },
    attributes,
    isPositioned: ((_state$rects$popper$y = state === null || state === void 0 ? void 0 : (_state$rects = state.rects) === null || _state$rects === void 0 ? void 0 : (_state$rects$popper = _state$rects.popper) === null || _state$rects$popper === void 0 ? void 0 : _state$rects$popper.y) !== null && _state$rects$popper$y !== void 0 ? _state$rects$popper$y : -1) >= 0,
    position,
    setArrowElement,
    setPopperElement,
    popperElement
  };
}

const _excluded$m = ["anchorRef", "as", "children", "className", "closeOnClickAway", "closeOnEscape", "elevation", "focusElement", "hasArrow", "isOpen", "onClose", "parentElement", "usePortal", "focusAnchorOnClose", "withFocusTrap", "boundaryRef", "fitToAnchorWidth", "fitWithinViewportHeight", "focusTrapZoneElement", "offset", "placement", "style", "theme", "zIndex"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$l = 'Popover';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$i = getRootClassName(COMPONENT_NAME$l);

/**
 * Component default props.
 */
const DEFAULT_PROPS$g = {
  elevation: 3,
  placement: Placement.AUTO,
  focusAnchorOnClose: true,
  usePortal: true,
  zIndex: 9999
};

/** Method to render the popover inside a portal if usePortal is true */
const renderPopover = (children, usePortal) => {
  return usePortal ? /*#__PURE__*/createPortal(children, document.body) : children;
};

// Inner component (must be wrapped before export)
const _InnerPopover = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      anchorRef,
      as: Component = 'div',
      children,
      className,
      closeOnClickAway,
      closeOnEscape,
      elevation,
      focusElement,
      hasArrow,
      isOpen,
      onClose,
      parentElement,
      usePortal,
      focusAnchorOnClose,
      withFocusTrap,
      boundaryRef,
      fitToAnchorWidth,
      fitWithinViewportHeight,
      focusTrapZoneElement,
      offset,
      placement,
      style,
      theme,
      zIndex
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$m);
  const popoverRef = useRef(null);
  const {
    styles,
    attributes,
    isPositioned,
    position,
    setArrowElement,
    setPopperElement,
    popperElement
  } = usePopoverStyle({
    offset,
    hasArrow,
    fitToAnchorWidth,
    fitWithinViewportHeight,
    boundaryRef,
    anchorRef,
    children,
    placement,
    style,
    zIndex
  });
  const unmountSentinel = useRestoreFocusOnClose({
    focusAnchorOnClose,
    anchorRef,
    parentElement
  }, popperElement);
  const focusZoneElement = (focusTrapZoneElement === null || focusTrapZoneElement === void 0 ? void 0 : focusTrapZoneElement.current) || (popoverRef === null || popoverRef === void 0 ? void 0 : popoverRef.current);
  useCallbackOnEscape(onClose, isOpen && closeOnEscape);

  /** Only set focus within if the focus trap is disabled as they interfere with one another. */
  useFocus(focusElement === null || focusElement === void 0 ? void 0 : focusElement.current, !withFocusTrap && isOpen && isPositioned);
  useFocusTrap(withFocusTrap && isOpen && focusZoneElement, focusElement === null || focusElement === void 0 ? void 0 : focusElement.current);
  const clickAwayRefs = useRef([popoverRef, anchorRef]);
  const mergedRefs = useMergeRefs(setPopperElement, ref, popoverRef);
  return isOpen ? renderPopover( /*#__PURE__*/React.createElement(Component, _extends({}, forwardedProps, {
    ref: mergedRefs,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$i,
      theme,
      elevation: Math.min(elevation || 0, 5),
      position
    })),
    style: styles.popover
  }, attributes.popper), unmountSentinel, /*#__PURE__*/React.createElement(ClickAwayProvider, {
    callback: closeOnClickAway && onClose,
    childrenRefs: clickAwayRefs
  }, hasArrow && /*#__PURE__*/React.createElement("div", {
    ref: setArrowElement,
    className: `${CLASSNAME$i}__arrow`,
    style: styles.arrow
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 14 14",
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement("path", {
    d: "M8 3.49C7.62 2.82 6.66 2.82 6.27 3.48L.04 14 14.04 14 8 3.49Z"
  }))), children)), usePortal) : null;
});
_InnerPopover.displayName = COMPONENT_NAME$l;

/**
 * Popover component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Popover = skipRender(
// Skip render in SSR
() => Boolean(DOCUMENT), _InnerPopover);
Popover.displayName = COMPONENT_NAME$l;
Popover.className = CLASSNAME$i;
Popover.defaultProps = DEFAULT_PROPS$g;

// The error margin in px we want to have for triggering infinite scroll
const SCROLL_TRIGGER_MARGIN = 5;

/**
 * Listen to clicks away from a given element and callback the passed in function.
 *
 * @param  ref               A reference to the element on which you want to listen scroll event.
 * @param  [callback]        A callback function to call when the bottom of the reference element is reached.
 * @param  [callbackOnMount] A callback function to call when the component is mounted.
 */
const useInfiniteScroll = function (ref, callback) {
  let callbackOnMount = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  let scrollTriggerMargin = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : SCROLL_TRIGGER_MARGIN;
  useEffect(() => {
    const {
      current
    } = ref;
    if (!callback || !current) {
      return undefined;
    }
    const isAtBottom = () => Boolean(current && current.scrollHeight - (current.scrollTop + current.clientHeight) <= scrollTriggerMargin);
    const onScroll = e => {
      if (isAtBottom()) {
        callback(e);
      }
    };
    if (isAtBottom()) {
      onScroll();
    }
    current.addEventListener('scroll', onScroll);
    current.addEventListener('resize', onScroll);
    return () => {
      current.removeEventListener('scroll', onScroll);
      current.removeEventListener('resize', onScroll);
    };
  }, [ref, callback, scrollTriggerMargin]);
  useEffect(() => {
    if (callback && callbackOnMount) {
      callback();
    }
  }, [callback, callbackOnMount]);
};

const _excluded$n = ["anchorRef", "children", "className", "closeOnClick", "closeOnClickAway", "closeOnEscape", "fitToAnchorWidth", "fitWithinViewportHeight", "isOpen", "offset", "focusAnchorOnClose", "onClose", "onInfiniteScroll", "placement", "shouldFocusOnOpen", "zIndex"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$m = 'Dropdown';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$j = getRootClassName(COMPONENT_NAME$m);

/**
 * Component default props.
 */
const DEFAULT_PROPS$h = {
  closeOnClick: true,
  closeOnClickAway: true,
  closeOnEscape: true,
  fitToAnchorWidth: true,
  fitWithinViewportHeight: true,
  placement: Placement.BOTTOM_START,
  shouldFocusOnOpen: true,
  focusAnchorOnClose: true
};

/**
 * Dropdown component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Dropdown = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      anchorRef,
      children,
      className,
      closeOnClick,
      closeOnClickAway,
      closeOnEscape,
      fitToAnchorWidth,
      fitWithinViewportHeight,
      isOpen,
      offset,
      focusAnchorOnClose,
      onClose,
      onInfiniteScroll,
      placement,
      shouldFocusOnOpen,
      zIndex
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$n);
  const innerRef = useRef(null);
  const listElement = useRef(null);
  useInfiniteScroll(innerRef, onInfiniteScroll);
  const popperElement = useMemo(() => {
    return !Array.isArray(children) && isComponent(List)(children) ? /*#__PURE__*/cloneElement(children, _objectSpread2(_objectSpread2({}, children.props), {}, {
      ref: listElement,
      onClick(evt) {
        var _children$props$onCli, _children$props;
        (_children$props$onCli = (_children$props = children.props).onClick) === null || _children$props$onCli === void 0 ? void 0 : _children$props$onCli.call(_children$props, evt);
        if (closeOnClick) {
          onClose === null || onClose === void 0 ? void 0 : onClose();
        }
      },
      isClickable: true
    })) : children;
  }, [children, closeOnClick, onClose]);
  return isOpen ? /*#__PURE__*/React.createElement(Popover, _extends({
    ref: ref
  }, forwardedProps, {
    focusAnchorOnClose: focusAnchorOnClose,
    anchorRef: anchorRef,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$j
    })),
    elevation: 0,
    closeOnClickAway: closeOnClickAway,
    closeOnEscape: closeOnEscape,
    fitToAnchorWidth: fitToAnchorWidth,
    fitWithinViewportHeight: fitWithinViewportHeight,
    focusElement: shouldFocusOnOpen ? listElement : undefined,
    isOpen: isOpen,
    offset: offset,
    onClose: onClose,
    placement: placement,
    zIndex: zIndex
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$j}__menu`,
    ref: innerRef
  }, popperElement)) : null;
});
Dropdown.displayName = COMPONENT_NAME$m;
Dropdown.className = CLASSNAME$j;
Dropdown.defaultProps = DEFAULT_PROPS$h;

const _excluded$o = ["className", "children", "hasBackground", "hasHeaderDivider", "isOpen", "label", "onClose", "onOpen", "onToggleOpen", "theme", "toggleButtonProps"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$n = 'ExpansionPanel';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$k = getRootClassName(COMPONENT_NAME$n);

/**
 * Component default props.
 */
const DEFAULT_PROPS$i = {
  theme: Theme.light
};
const isDragHandle = isComponent(DragHandle);
const isHeader$1 = isComponent('header');
const isFooter$1 = isComponent('footer');

/**
 * ExpansionPanel component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ExpansionPanel = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      children: anyChildren,
      hasBackground,
      hasHeaderDivider,
      isOpen,
      label,
      onClose,
      onOpen,
      onToggleOpen,
      theme,
      toggleButtonProps
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$o);
  const children = Children.toArray(anyChildren);

  // Partition children by types.
  const [[dragHandle], [header], [footer], content] = partitionMulti(children, [isDragHandle, isHeader$1, isFooter$1]);

  // Either take the header in children or create one with the label.
  const headerProps = /*#__PURE__*/React.isValidElement(header) ? header.props : {};
  const headerContent = !isEmpty(headerProps.children) ? headerProps.children : /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$k}__label`
  }, label);
  const toggleOpen = event => {
    const shouldOpen = !isOpen;
    if (isFunction(onOpen) && shouldOpen) {
      onOpen(event);
    }
    if (isFunction(onClose) && !shouldOpen) {
      onClose(event);
    }
    if (isFunction(onToggleOpen)) {
      onToggleOpen(shouldOpen, event);
    }
  };
  const color = theme === Theme.dark ? ColorPalette.light : ColorPalette.dark;
  const rootClassName = classnames(className, handleBasicClasses({
    hasBackground,
    hasHeader: Boolean(!isEmpty(headerProps.children)),
    hasHeaderDivider,
    isClose: !isOpen,
    isDraggable: Boolean(dragHandle),
    isOpen,
    prefix: CLASSNAME$k,
    theme
  }));
  const wrapperRef = useRef(null);
  const isContentVisible = () => get(wrapperRef.current, 'clientHeight', 0) > 0;

  // Switch max height on/off to activate the CSS transition (updates when children changes).
  const [maxHeight, setMaxHeight] = useState('0');
  useEffect(() => {
    const height = isOpen ? get(wrapperRef.current, 'offsetHeight', 0) : 0;
    setMaxHeight(`${height}px`);
  }, [children, isOpen]);
  return /*#__PURE__*/React.createElement("section", _extends({
    ref: ref
  }, forwardedProps, {
    className: rootClassName
  }), /*#__PURE__*/React.createElement("header", {
    className: `${CLASSNAME$k}__header`,
    onClick: toggleOpen
  }, dragHandle && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$k}__header-drag`
  }, dragHandle), /*#__PURE__*/React.createElement("div", _extends({}, headerProps, {
    className: `${CLASSNAME$k}__header-content`
  }), headerContent), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$k}__header-toggle`
  }, /*#__PURE__*/React.createElement(IconButton, _extends({}, toggleButtonProps, {
    color: color,
    emphasis: Emphasis.low,
    icon: isOpen ? mdiChevronUp : mdiChevronDown,
    "aria-expanded": isOpen || 'false'
  })))), (isOpen || isContentVisible()) && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$k}__wrapper`,
    style: {
      maxHeight
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$k}__container`,
    ref: wrapperRef
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$k}__content`
  }, content), footer && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$k}__footer`
  }, footer))));
});
ExpansionPanel.displayName = COMPONENT_NAME$n;
ExpansionPanel.className = CLASSNAME$k;
ExpansionPanel.defaultProps = DEFAULT_PROPS$i;

const _excluded$p = ["label", "icon", "color", "className", "theme", "truncate"];
const COMPONENT_NAME$o = 'Flag';
const CLASSNAME$l = getRootClassName(COMPONENT_NAME$o);
const DEFAULT_PROPS$j = {
  theme: Theme.light
};

/**
 * Flag component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Flag = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      label,
      icon,
      color,
      className,
      theme,
      truncate
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$p);
  const flagColor = color || (theme === Theme.light ? ColorPalette.dark : ColorPalette.light);
  const isTruncated = !!truncate;
  return /*#__PURE__*/React.createElement("div", _extends({}, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$l,
      color: flagColor,
      isTruncated
    })),
    ref: ref
  }), icon && /*#__PURE__*/React.createElement(Icon, {
    icon: icon,
    size: Size.xxs,
    className: `${CLASSNAME$l}__icon`
  }), /*#__PURE__*/React.createElement(Text, {
    as: "span",
    truncate: isTruncated,
    typography: "overline",
    className: `${CLASSNAME$l}__label`
  }, label));
});
Flag.displayName = COMPONENT_NAME$o;
Flag.className = CLASSNAME$l;
Flag.defaultProps = DEFAULT_PROPS$j;

const _excluded$q = ["as", "children", "className", "fillSpace", "gap", "hAlign", "marginAuto", "noShrink", "orientation", "vAlign", "wrap"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$p = 'FlexBox';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$m = getRootClassName(COMPONENT_NAME$p);

/**
 * FlexBox component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const FlexBox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      as: Component = 'div',
      children,
      className,
      fillSpace,
      gap,
      hAlign,
      marginAuto,
      noShrink,
      orientation,
      vAlign,
      wrap
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$q);
  return /*#__PURE__*/React.createElement(Component, _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$m,
      orientation: orientation !== null && orientation !== void 0 ? orientation : wrap || hAlign || vAlign ? Orientation.horizontal : null,
      vAlign,
      hAlign,
      gap
    }), wrap && `${CLASSNAME$m}--wrap`, fillSpace && `${CLASSNAME$m}--fill-space`, noShrink && `${CLASSNAME$m}--no-shrink`, marginAuto && castArray(marginAuto).map(align => `${CLASSNAME$m}--margin-auto-${align}`))
  }), children);
});
FlexBox.displayName = COMPONENT_NAME$p;
FlexBox.className = CLASSNAME$m;

const _excluded$r = ["className", "figure", "figureProps", "children", "actions", "actionsProps", "gap", "orientation", "contentProps"];
/**
 * Component display name.
 */
const COMPONENT_NAME$q = 'GenericBlock';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$n = getRootClassName(COMPONENT_NAME$q);

/**
 * Component default props.
 */
const DEFAULT_PROPS$k = {
  gap: Size.big,
  orientation: Orientation.horizontal
};
const Figure = noop.bind({});
const isFigure = isComponentType(Figure);
const Content = noop.bind({});
const isContent = isComponentType(Content);
const Actions = noop.bind({});
const isActions = isComponentType(Actions);

/**
 * The GenericBlock is a layout component made of 3 sections that can be
 * displayed either horizontally of vertically with the same gap between each section.
 *
 * The sections are:
 * - `Figure` => A visual element to display before the main content.
 * - `Content` => The main content displayed
 * - `Actions` => One or more actions to set after the element.
 *
 * @see https://www.figma.com/file/lzzrQmsfaXRaOyRfoEogPZ/DS%3A-playground?node-id=1%3A4076
 */
const BaseGenericBlock = /*#__PURE__*/forwardRef((props, ref) => {
  var _sections$figureChild, _sections$figureChild2, _sections$figureChild3, _sections$figureChild4, _sections$contentChil, _sections$contentChil2, _sections$contentChil3, _sections$contentChil4, _sections$actionsChil, _sections$actionsChil2, _sections$actionsChil3, _sections$actionsChil4;
  const {
      className,
      figure,
      figureProps,
      children,
      actions,
      actionsProps,
      gap,
      orientation,
      contentProps
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$r);
  const sections = React.useMemo(() => {
    // Split children by section type
    const [[figureChild], [contentChild], [actionsChild], ...otherChildren] = partitionMulti(Children.toArray(children), [isFigure, isContent, isActions]);
    return {
      figureChild,
      figureChildProps: figureChild === null || figureChild === void 0 ? void 0 : figureChild.props,
      contentChild,
      contentChildProps: contentChild === null || contentChild === void 0 ? void 0 : contentChild.props,
      actionsChild,
      actionsChildProps: actionsChild === null || actionsChild === void 0 ? void 0 : actionsChild.props,
      otherChildren: otherChildren.filter(child => !isEmpty(child))
    };
  }, [children]);
  return /*#__PURE__*/React.createElement(FlexBox, _extends({
    ref: ref,
    className: classnames(className, CLASSNAME$n),
    gap: gap,
    orientation: orientation
  }, forwardedProps), (figure || ((_sections$figureChild = sections.figureChildProps) === null || _sections$figureChild === void 0 ? void 0 : _sections$figureChild.children)) && /*#__PURE__*/React.createElement(FlexBox, _extends({
    ref: (_sections$figureChild2 = sections.figureChild) === null || _sections$figureChild2 === void 0 ? void 0 : _sections$figureChild2.ref,
    vAlign: forwardedProps.vAlign,
    hAlign: forwardedProps.hAlign
  }, figureProps, sections.figureChildProps, {
    className: classnames(figureProps === null || figureProps === void 0 ? void 0 : figureProps.className, (_sections$figureChild3 = sections.figureChildProps) === null || _sections$figureChild3 === void 0 ? void 0 : _sections$figureChild3.className, `${CLASSNAME$n}__figure`)
  }), figure, (_sections$figureChild4 = sections.figureChildProps) === null || _sections$figureChild4 === void 0 ? void 0 : _sections$figureChild4.children), (((_sections$contentChil = sections.contentChildProps) === null || _sections$contentChil === void 0 ? void 0 : _sections$contentChil.children) || sections.otherChildren.length > 0) && /*#__PURE__*/React.createElement(FlexBox, _extends({
    ref: (_sections$contentChil2 = sections.contentChild) === null || _sections$contentChil2 === void 0 ? void 0 : _sections$contentChil2.ref,
    orientation: Orientation.vertical,
    fillSpace: true,
    vAlign: forwardedProps.vAlign,
    hAlign: forwardedProps.hAlign
  }, contentProps, sections.contentChildProps, {
    className: classnames(contentProps === null || contentProps === void 0 ? void 0 : contentProps.className, (_sections$contentChil3 = sections.contentChildProps) === null || _sections$contentChil3 === void 0 ? void 0 : _sections$contentChil3.className, `${CLASSNAME$n}__content`)
  }), (_sections$contentChil4 = sections.contentChildProps) === null || _sections$contentChil4 === void 0 ? void 0 : _sections$contentChil4.children, sections.otherChildren), (actions || ((_sections$actionsChil = sections.actionsChildProps) === null || _sections$actionsChil === void 0 ? void 0 : _sections$actionsChil.children)) && /*#__PURE__*/React.createElement(FlexBox, _extends({
    ref: (_sections$actionsChil2 = sections.actionsChild) === null || _sections$actionsChil2 === void 0 ? void 0 : _sections$actionsChil2.ref,
    vAlign: forwardedProps.vAlign,
    hAlign: forwardedProps.hAlign
  }, actionsProps, sections.actionsChildProps, {
    className: classnames(actionsProps === null || actionsProps === void 0 ? void 0 : actionsProps.className, (_sections$actionsChil3 = sections.actionsChildProps) === null || _sections$actionsChil3 === void 0 ? void 0 : _sections$actionsChil3.className, `${CLASSNAME$n}__actions`)
  }), actions, (_sections$actionsChil4 = sections.actionsChildProps) === null || _sections$actionsChil4 === void 0 ? void 0 : _sections$actionsChil4.children));
});
BaseGenericBlock.displayName = COMPONENT_NAME$q;
BaseGenericBlock.className = CLASSNAME$n;
BaseGenericBlock.defaultProps = DEFAULT_PROPS$k;
const GenericBlock = Object.assign(BaseGenericBlock, {
  Figure,
  Content,
  Actions
});

/**
 * Accepted gap sizes for the generic block.
 */
const GenericBlockGapSize = pick(Size, ['tiny', 'regular', 'medium', 'big', 'huge']);

/** Empty object */

const DEFAULT_VALUE = {};

/**
 * Tooltip react context simply used as a marker to know when parent react node have a tooltip
 */
const TooltipContext = /*#__PURE__*/React.createContext(undefined);
const TooltipContextProvider = _ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(TooltipContext.Provider, {
    value: DEFAULT_VALUE
  }, children);
};
const useTooltipContext = () => React.useContext(TooltipContext);

/**
 * Compute a tooltip label based on a label element `innerText` if the text overflows.
 *
 * Warning: only works on first render, does not update on label element resize.
 */
const useOverflowTooltipLabel = () => {
  const parentTooltip = useTooltipContext();
  const [tooltipLabel, setTooltipLabel] = React.useState(undefined);
  const labelRef = React.useCallback(labelElement => {
    if (!labelElement || !!parentTooltip) {
      // Skip if label element is unknown
      // Skip if the parent has a tooltip
      return;
    }

    // Label overflowing
    if (labelElement.offsetWidth < labelElement.scrollWidth) {
      setTooltipLabel(labelElement.innerText);
    }
  }, [parentTooltip]);
  return {
    labelRef,
    tooltipLabel
  };
};

const _excluded$s = ["as", "children", "className", "color", "colorVariant", "noWrap", "typography", "truncate", "whiteSpace", "style"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$r = 'Text';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$o = getRootClassName(COMPONENT_NAME$r);

/**
 * Component default props.
 */
const DEFAULT_PROPS$l = {};

/**
 * Text component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Text = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      as: Component,
      children,
      className,
      color,
      colorVariant,
      noWrap,
      typography,
      truncate,
      whiteSpace,
      style
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$s);
  const colorClass = color && getFontColorClassName(color, colorVariant);
  const typographyClass = typography && getTypographyClassName(typography);

  // Truncate mode
  const truncateLinesStyle = typeof truncate === 'object' && truncate.lines > 1 && {
    '--lumx-text-truncate-lines': truncate.lines
  };
  const isTruncatedMultiline = !!truncateLinesStyle;
  const isTruncated = !!truncate;

  /**
   * Add custom white-space style if specified
   * Disabled if noWrap is specified
   * Disabled if truncated on one-line
   * */
  const whiteSpaceStyle = !noWrap && !(isTruncated && !isTruncatedMultiline) && whiteSpace && {
    '--lumx-text-white-space': whiteSpace
  };
  const {
    tooltipLabel,
    labelRef
  } = useOverflowTooltipLabel();
  return /*#__PURE__*/React.createElement(Component, _extends({
    ref: useMergeRefs(ref, labelRef),
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$o,
      isTruncated: isTruncated && !isTruncatedMultiline,
      isTruncatedMultiline
    }), typographyClass, colorClass, noWrap && `${CLASSNAME$o}--no-wrap`),
    title: tooltipLabel,
    style: _objectSpread2(_objectSpread2(_objectSpread2({}, truncateLinesStyle), whiteSpaceStyle), style)
  }, forwardedProps), children && Children.toArray(children).map((child, index) => {
    // Force wrap spaces around icons to make sure they are never stuck against text.
    if (isComponent(Icon)(child)) {
      return /*#__PURE__*/React.createElement(Fragment$1, {
        key: child.key || index
      }, " ", child, " ");
    }
    return child;
  }));
});
Text.displayName = COMPONENT_NAME$r;
Text.className = CLASSNAME$o;
Text.defaultProps = DEFAULT_PROPS$l;

/** The maximum authorized heading level. */
const MAX_HEADING_LEVEL = 6;

/**
 * Typography to use by default depending on the heading level.
 */
const DEFAULT_TYPOGRAPHY_BY_LEVEL = {
  h1: Typography.display1,
  h2: Typography.headline,
  h3: Typography.title,
  h4: Typography.subtitle2,
  h5: Typography.subtitle1,
  h6: Typography.body2
};

const defaultContext = {
  level: 1,
  headingElement: 'h1'
};
const HeadingLevelContext = /*#__PURE__*/createContext(defaultContext);

const useHeadingLevel = () => {
  const {
    level = 1,
    headingElement = 'h1'
  } = React.useContext(HeadingLevelContext);
  return {
    level,
    headingElement
  };
};

const _excluded$t = ["children", "as", "className"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$s = 'Heading';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$p = getRootClassName(COMPONENT_NAME$s);

/**
 * Component default props.
 */
const DEFAULT_PROPS$m = {};

/**
 * Renders a heading component.
 * Extends the `Text` Component with the heading level automatically computed based on
 * the current level provided by the context.
 */
const Heading = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      as,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$t);
  const {
    headingElement
  } = useHeadingLevel();
  const computedHeadingElement = as || headingElement;
  return /*#__PURE__*/React.createElement(Text, _extends({
    ref: ref,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$p
    })),
    as: computedHeadingElement,
    typography: DEFAULT_TYPOGRAPHY_BY_LEVEL[computedHeadingElement]
  }, forwardedProps), children);
});
Heading.displayName = COMPONENT_NAME$s;
Heading.className = CLASSNAME$p;
Heading.defaultProps = DEFAULT_PROPS$m;

/**
 * Provide a new heading level context.
 */
const HeadingLevelProvider = _ref => {
  let {
    children,
    level
  } = _ref;
  const {
    level: contextLevel
  } = useHeadingLevel();
  const incrementedLevel = level || contextLevel + 1;
  /** Don't allow a level beyond the maximum level. */
  const nextLevel = incrementedLevel > MAX_HEADING_LEVEL ? MAX_HEADING_LEVEL : incrementedLevel;
  const headingElement = `h${nextLevel}`;
  return /*#__PURE__*/React.createElement(HeadingLevelContext.Provider, {
    value: {
      level: nextLevel,
      headingElement
    }
  }, children);
};

const _excluded$u = ["children", "className", "gutter", "hAlign", "orientation", "vAlign", "wrap"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$t = 'Grid';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$q = getRootClassName(COMPONENT_NAME$t);

/**
 * Component default props.
 */
const DEFAULT_PROPS$n = {
  orientation: Orientation.horizontal,
  wrap: 'nowrap'
};

/**
 * Grid component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Grid = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      gutter,
      hAlign,
      orientation,
      vAlign,
      wrap
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$u);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, `${CLASSNAME$q}-container`, {
      [`${CLASSNAME$q}--h-align-${hAlign}`]: hAlign
    }, {
      [`${CLASSNAME$q}--v-align-${vAlign}`]: vAlign
    }, handleBasicClasses({
      prefix: CLASSNAME$q,
      orientation,
      wrap,
      gutter
    }))
  }), children);
});
Grid.displayName = COMPONENT_NAME$t;
Grid.className = CLASSNAME$q;
Grid.defaultProps = DEFAULT_PROPS$n;

const _excluded$v = ["children", "className", "width", "align", "order"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$u = 'GridItem';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$r = getRootClassName(COMPONENT_NAME$u);

/**
 * GridItem component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const GridItem = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      width,
      align,
      order
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$v);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$r,
      width,
      order,
      align
    }))
  }), children);
});
GridItem.displayName = COMPONENT_NAME$u;
GridItem.className = CLASSNAME$r;

const _excluded$w = ["as", "gap", "maxColumns", "itemMinWidth", "children", "className", "style"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$v = 'GridColumn';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$s = getRootClassName(COMPONENT_NAME$v);

/**
 * Component default props.
 */
const DEFAULT_PROPS$o = {};

/**
 * The GridColumn is a layout component that can display children in a grid
 * with custom display properties. It also comes with a responsive design,
 * with a number of column that reduce when there is not enough space for each item.
 *
 * @param props Component props.
 * @param ref Component ref.
 * @return React element.
 */
const GridColumn = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      as: Component = 'div',
      gap,
      maxColumns,
      itemMinWidth,
      children,
      className,
      style = {}
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$w);
  return /*#__PURE__*/React.createElement(Component, _extends({}, forwardedProps, {
    ref: ref,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$s
    })),
    style: _objectSpread2(_objectSpread2({}, style), {}, {
      ['--lumx-grid-column-item-min-width']: isInteger(itemMinWidth) && `${itemMinWidth}px`,
      ['--lumx-grid-column-columns']: maxColumns,
      ['--lumx-grid-column-gap']: gap && `var(--lumx-spacing-unit-${gap})`
    })
  }), children);
});
GridColumn.displayName = COMPONENT_NAME$v;
GridColumn.className = CLASSNAME$s;
GridColumn.defaultProps = DEFAULT_PROPS$o;

const _excluded$x = ["className", "color", "colorVariant", "hasShape", "icon", "size", "theme", "alt"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$w = 'Icon';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$t = getRootClassName(COMPONENT_NAME$w);

/**
 * Component default props.
 */
const DEFAULT_PROPS$p = {};

/**
 * Icon component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Icon = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      color,
      colorVariant,
      hasShape,
      icon,
      size,
      theme,
      alt
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$x);

  // Color
  let iconColor = color;
  if (!iconColor && (hasShape || theme)) {
    iconColor = theme === Theme.dark ? ColorPalette.light : ColorPalette.dark;
  }

  // Color variant
  let iconColorVariant = colorVariant;
  if (!iconColorVariant && hasShape && iconColor === ColorPalette.dark) {
    iconColorVariant = 'L2';
  }

  // Size
  let iconSize = size;
  if (size && hasShape) {
    if (size === Size.xxs || size === Size.xs) {
      iconSize = Size.s;
    } else if (size === Size.xxl) {
      iconSize = Size.xl;
    }
  } else if (hasShape) {
    iconSize = Size.m;
  }
  return /*#__PURE__*/React.createElement("i", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      color: iconColor,
      colorVariant: iconColorVariant,
      hasShape,
      prefix: CLASSNAME$t,
      theme,
      size: iconSize
    }), !hasShape && `${CLASSNAME$t}--no-shape`, !hasShape && iconColor === ColorPalette.yellow && icon === mdiAlertCircle && `${CLASSNAME$t}--has-dark-layer`, `${CLASSNAME$t}--path`)
  }), /*#__PURE__*/React.createElement("svg", {
    "aria-hidden": alt ? undefined : 'true',
    role: alt ? 'img' : undefined,
    "aria-label": alt,
    height: "1em",
    preserveAspectRatio: "xMidYMid meet",
    style: {
      verticalAlign: '-0.125em'
    },
    viewBox: "0 0 24 24",
    width: "1em"
  }, /*#__PURE__*/React.createElement("path", {
    d: icon,
    fill: "currentColor"
  })));
});
Icon.displayName = COMPONENT_NAME$w;
Icon.className = CLASSNAME$t;
Icon.defaultProps = DEFAULT_PROPS$p;

/** Internal component used to render image captions */
const ImageCaption = props => {
  const {
    baseClassName,
    theme,
    as = 'figcaption',
    title,
    titleProps,
    description,
    descriptionProps,
    tags,
    captionStyle,
    align,
    truncate
  } = props;
  if (!title && !description && !tags) return null;
  const titleColor = {
    color: theme === 'dark' ? 'light' : 'dark'
  };
  const baseColor = {
    color: theme === 'dark' ? 'light' : 'dark',
    colorVariant: 'L2'
  };

  // Display description as string or HTML
  const descriptionContent = typeof description === 'string' ? {
    children: description
  } : {
    dangerouslySetInnerHTML: description
  };
  return /*#__PURE__*/React.createElement(FlexBox, {
    as: as,
    className: classnames(baseClassName && `${baseClassName}__wrapper`),
    style: captionStyle,
    orientation: "vertical",
    vAlign: align,
    hAlign: align === 'center' ? align : undefined,
    gap: "regular"
  }, (title || description) && /*#__PURE__*/React.createElement(Text, _extends({
    as: "p",
    className: classnames(baseClassName && `${baseClassName}__caption`),
    truncate: truncate
  }, baseColor), title && /*#__PURE__*/React.createElement(Text, _extends({}, titleProps, {
    as: "span",
    className: classnames(titleProps === null || titleProps === void 0 ? void 0 : titleProps.className, baseClassName && `${baseClassName}__title`),
    typography: "subtitle1"
  }, titleColor), title), ' ', description && /*#__PURE__*/React.createElement(Text, _extends({}, descriptionProps, {
    as: "span",
    className: classnames(descriptionProps === null || descriptionProps === void 0 ? void 0 : descriptionProps.className, baseClassName && `${baseClassName}__description`),
    typography: "body1"
  }, descriptionContent))), tags && /*#__PURE__*/React.createElement(FlexBox, {
    className: classnames(baseClassName && `${baseClassName}__tags`),
    orientation: "horizontal",
    vAlign: align
  }, tags));
};

const _excluded$y = ["actions", "align", "alt", "captionPosition", "captionStyle", "className", "description", "descriptionProps", "fillHeight", "image", "size", "tags", "theme", "thumbnailProps", "title", "titleProps"];

/**
 * Image block variants.
 */
const ImageBlockCaptionPosition = {
  below: 'below',
  over: 'over'
};

/**
 *  Image block sizes.
 */

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$x = 'ImageBlock';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$u = getRootClassName(COMPONENT_NAME$x);

/**
 * Component default props.
 */
const DEFAULT_PROPS$q = {
  captionPosition: ImageBlockCaptionPosition.below,
  theme: Theme.light,
  align: Alignment.left
};

/**
 * ImageBlock component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ImageBlock = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      actions,
      align,
      alt,
      captionPosition,
      captionStyle,
      className,
      description,
      descriptionProps,
      fillHeight,
      image,
      size,
      tags,
      theme,
      thumbnailProps,
      title,
      titleProps
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$y);
  return /*#__PURE__*/React.createElement("figure", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$u,
      captionPosition,
      align,
      size,
      theme
    }), fillHeight && `${CLASSNAME$u}--fill-height`)
  }), /*#__PURE__*/React.createElement(Thumbnail, _extends({}, thumbnailProps, {
    className: classnames(`${CLASSNAME$u}__image`, thumbnailProps === null || thumbnailProps === void 0 ? void 0 : thumbnailProps.className),
    fillHeight: fillHeight,
    align: align,
    image: image,
    size: size,
    theme: theme,
    alt: alt || title
  })), /*#__PURE__*/React.createElement(ImageCaption, {
    as: "figcaption",
    baseClassName: CLASSNAME$u,
    theme: theme,
    title: title,
    titleProps: titleProps,
    description: description,
    descriptionProps: descriptionProps,
    tags: tags,
    captionStyle: captionStyle,
    align: align,
    truncate: captionPosition === 'over'
  }), actions && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$u}__actions`
  }, actions));
});
ImageBlock.displayName = COMPONENT_NAME$x;
ImageBlock.className = CLASSNAME$u;
ImageBlock.defaultProps = DEFAULT_PROPS$q;

/**
 * Component display name.
 */
const COMPONENT_NAME$y = 'ImageLightbox';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$v = getRootClassName(COMPONENT_NAME$y);

/**
 * Observe element size (only works if it's size depends on the window size).
 *
 * (Not using ResizeObserver for better browser backward compat)
 *
 * @param elementRef Element to observe
 * @return the size and a manual update callback
 */
function useSizeOnWindowResize(elementRef) {
  const [size, setSize] = React.useState(null);
  const updateSize = React.useMemo(() => throttle(() => {
    var _elementRef$current;
    const newSize = (_elementRef$current = elementRef.current) === null || _elementRef$current === void 0 ? void 0 : _elementRef$current.getBoundingClientRect();
    if (newSize) setSize(newSize);
  }, 10), [elementRef]);
  React.useEffect(() => {
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [updateSize]);
  return [size, updateSize];
}

/** Get natural image size after load. */
function useImageSize(imgRef, getInitialSize) {
  const [imageSize, setImageSize] = React.useState(getInitialSize || null);
  React.useEffect(() => {
    const {
      current: img
    } = imgRef;
    if (!img) {
      return undefined;
    }
    const onLoad = () => setImageSize({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
    img.addEventListener('load', onLoad);
    return () => img.removeEventListener('load', onLoad);
  }, [imgRef]);
  return imageSize;
}

/** Check if user prefers reduced motion */
function getPrefersReducedMotion() {
  var _WINDOW$matchMedia;
  return WINDOW === null || WINDOW === void 0 ? void 0 : (_WINDOW$matchMedia = WINDOW.matchMedia) === null || _WINDOW$matchMedia === void 0 ? void 0 : _WINDOW$matchMedia.call(WINDOW, '(prefers-reduced-motion: reduce)').matches;
}

/** Minimal recursive deep equal of JS values */
function isEqual(obj1, obj2) {
  if (obj1 === obj2) return true;
  if (typeof obj1 === 'object' && typeof obj2 === 'object') {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every(key1 => isEqual(obj1[key1], obj2[key1]));
  }
  return false;
}

/**
 * Listen to mouse wheel + ctrl and multi-pointer pinch to zoom
 */
function usePointerZoom(scrollAreaRef, onScaleChange, animateScroll) {
  const [isPointerZooming, setPointerZooming] = React.useState(false);
  React.useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea || !onScaleChange) {
      return undefined;
    }
    let animationFrame;
    let zoomStateTimeoutId;
    function updateScaleOnNextFrame(newScale, mousePosition) {
      // Cancel previously scheduled frame
      if (animationFrame) cancelAnimationFrame(animationFrame);

      // Cancel previously scheduled zoom state change
      if (zoomStateTimeoutId) clearTimeout(zoomStateTimeoutId);
      function nextFrame() {
        setPointerZooming(true);
        onScaleChange === null || onScaleChange === void 0 ? void 0 : onScaleChange(newScale);
        animationFrame = null;
        // Wait a bit before indicating the pointer zooming is finished
        zoomStateTimeoutId = setTimeout(() => setPointerZooming(false), 100);
      }
      animationFrame = requestAnimationFrame(nextFrame);

      // Animate scroll in parallel (centering on the current mouse position)
      animateScroll(mousePosition, {
        width: scrollArea.scrollWidth,
        height: scrollArea.scrollHeight
      });
    }
    function onWheel(event) {
      if (!event.ctrlKey) {
        return;
      }
      event.preventDefault();
      const newScale = Math.exp(-event.deltaY / 50);

      // Update scale on next frame (focused on the mouse position)
      updateScaleOnNextFrame(newScale, {
        x: event.pageX,
        y: event.pageY
      });
    }
    const activePointers = {};
    let prevDistance = null;
    let previousCenterPoint = null;
    function onPointerDown(event) {
      activePointers[event.pointerId] = event;
    }
    function onPointerMove(event) {
      // Update pointer in cache
      if (activePointers[event.pointerId]) {
        activePointers[event.pointerId] = event;
      }
      const pointers = Object.values(activePointers);

      // Make sure we run computation on one of the pointer in the group
      if (pointers[0].pointerId !== event.pointerId) {
        return;
      }

      // Centered point between all pointers
      const centerPoint = {
        x: pointers.reduce((x, _ref) => {
          let {
            clientX
          } = _ref;
          return x + clientX;
        }, 0) / pointers.length,
        y: pointers.reduce((y, _ref2) => {
          let {
            clientY
          } = _ref2;
          return y + clientY;
        }, 0) / pointers.length
      };

      // Movement of the center point
      const deltaCenterPoint = previousCenterPoint && {
        left: previousCenterPoint.x - centerPoint.x,
        top: previousCenterPoint.y - centerPoint.y
      };

      // Pan X & Y
      if (deltaCenterPoint) {
        // Apply movement of the center point to the scroll
        scrollArea.scrollBy({
          top: deltaCenterPoint.top / 2,
          left: deltaCenterPoint.left / 2
        });
      }

      // Pinch to zoom
      if (pointers.length === 2) {
        const [pointer1, pointer2] = pointers;
        const distance = Math.hypot(pointer2.clientX - pointer1.clientX, pointer2.clientY - pointer1.clientY);
        if (prevDistance && deltaCenterPoint) {
          const delta = prevDistance - distance;
          const absDelta = Math.abs(delta);

          // Zoom only if we are "pinching" more than we are moving the pointers
          if (absDelta > Math.abs(deltaCenterPoint.left) && absDelta > Math.abs(deltaCenterPoint.top)) {
            // Update scale on next frame (focused on the center point between the two pointers)
            const newScale = Math.exp(-delta / 100);
            updateScaleOnNextFrame(newScale, centerPoint);
          }
        }
        prevDistance = distance;
      }
      previousCenterPoint = centerPoint;
    }
    function onPointerUp(event) {
      prevDistance = null;
      previousCenterPoint = null;
      delete activePointers[event.pointerId];
    }
    scrollArea.addEventListener('wheel', onWheel, {
      passive: false
    });
    const isMultiTouch = navigator.maxTouchPoints >= 2;
    if (isMultiTouch) {
      scrollArea.addEventListener('pointerdown', onPointerDown);
      scrollArea.addEventListener('pointermove', onPointerMove);
      scrollArea.addEventListener('pointerup', onPointerUp);
    }
    return () => {
      scrollArea.removeEventListener('wheel', onWheel);
      if (isMultiTouch) {
        scrollArea.removeEventListener('pointerdown', onPointerDown);
        scrollArea.removeEventListener('pointermove', onPointerMove);
        scrollArea.removeEventListener('pointerup', onPointerUp);
      }
    };
  }, [animateScroll, onScaleChange, scrollAreaRef]);
  return isPointerZooming;
}

/** Maintains the scroll position centered relative to the original scroll area's dimensions when the content scales. */
function useAnimateScroll(scrollAreaRef) {
  return React.useMemo(() => {
    let animationFrame = null;
    return function animate(centerPoint, initialScrollAreaSize) {
      const scrollArea = scrollAreaRef.current;
      if (!scrollArea) {
        return;
      }

      // Cancel previously running animation
      if (animationFrame) cancelAnimationFrame(animationFrame);

      // Center on the given point or else on the scroll area visual center
      const clientHeightRatio = centerPoint !== null && centerPoint !== void 0 && centerPoint.y ? centerPoint.y / scrollArea.clientHeight : 0.5;
      const clientWidthRatio = centerPoint !== null && centerPoint !== void 0 && centerPoint.x ? centerPoint.x / scrollArea.clientWidth : 0.5;
      const initialScrollHeight = (initialScrollAreaSize === null || initialScrollAreaSize === void 0 ? void 0 : initialScrollAreaSize.height) || scrollArea.scrollHeight;
      const initialScrollWidth = (initialScrollAreaSize === null || initialScrollAreaSize === void 0 ? void 0 : initialScrollAreaSize.width) || scrollArea.scrollWidth;
      const heightCenter = scrollArea.scrollTop + scrollArea.clientHeight * clientHeightRatio;
      const heightRatio = heightCenter / initialScrollHeight;
      const widthCenter = scrollArea.scrollLeft + scrollArea.clientWidth * clientWidthRatio;
      const widthRatio = widthCenter / initialScrollWidth;
      let prevScrollHeight = 0;
      let prevScrollWidth = 0;
      function nextFrame() {
        const {
          scrollHeight,
          scrollWidth,
          clientHeight,
          clientWidth
        } = scrollArea;

        // Scroll area stopped expanding => stop animation
        if (scrollHeight === prevScrollHeight && scrollWidth === prevScrollWidth) {
          animationFrame = null;
          return;
        }

        // Compute next scroll position
        const top = heightRatio * scrollHeight - clientHeight * clientHeightRatio;
        const left = widthRatio * scrollWidth - clientWidth * clientWidthRatio;
        scrollArea.scrollTo({
          top,
          left
        });
        prevScrollHeight = scrollHeight;
        prevScrollWidth = scrollWidth;
        animationFrame = requestAnimationFrame(nextFrame);
      }
      animationFrame = requestAnimationFrame(nextFrame);
    };
  }, [scrollAreaRef]);
}

/** Internal image slide component for ImageLightbox */
const ImageSlide = /*#__PURE__*/React.memo(props => {
  const {
    isActive,
    scale,
    onScaleChange,
    image: {
      image,
      imgRef: propImgRef,
      imgProps,
      alt,
      loadingPlaceholderImageRef
    }
  } = props;

  // Get scroll area size
  const scrollAreaRef = React.useRef(null);
  const [scrollAreaSize, updateSize] = useSizeOnWindowResize(scrollAreaRef);
  React.useEffect(() => {
    // Update size when active
    if (isActive) updateSize();
  }, [isActive, updateSize]);

  // Get image size
  const imgRef = React.useRef(null);
  const imageSize = useImageSize(imgRef, () => {
    const width = Number.parseInt(imgProps === null || imgProps === void 0 ? void 0 : imgProps.width, 10);
    const height = Number.parseInt(imgProps === null || imgProps === void 0 ? void 0 : imgProps.height, 10);
    return width && height ? {
      width,
      height
    } : null;
  });

  // Calculate new image size with scale
  const scaledImageSize = React.useMemo(() => {
    if (!scrollAreaSize || !imageSize) {
      return null;
    }
    const horizontalScale = scrollAreaSize.width / imageSize.width;
    const verticalScale = scrollAreaSize.height / imageSize.height;
    const baseScale = Math.min(1, Math.min(horizontalScale, verticalScale));
    return {
      width: imageSize.width * baseScale * (scale !== null && scale !== void 0 ? scale : 1),
      height: imageSize.height * baseScale * (scale !== null && scale !== void 0 ? scale : 1)
    };
  }, [scrollAreaSize, imageSize, scale]);

  // Animate scroll to preserve the center of the current visible window in the scroll area
  const animateScroll = useAnimateScroll(scrollAreaRef);

  // Zoom via mouse wheel or multi-touch pinch zoom
  const isPointerZooming = usePointerZoom(scrollAreaRef, onScaleChange, animateScroll);

  // Animate scroll on scale change
  React.useLayoutEffect(() => {
    if (scale && !isPointerZooming) {
      animateScroll();
    }
  }, [isPointerZooming, scale, animateScroll]);
  const isScrollable = scaledImageSize && scrollAreaSize && (scaledImageSize.width > scrollAreaSize.width || scaledImageSize.height > scrollAreaSize.height);
  return /*#__PURE__*/React.createElement(SlideshowItem, {
    ref: scrollAreaRef
    // Make it accessible to keyboard nav when the zone is scrollable
    ,
    tabIndex: isScrollable ? 0 : undefined,
    className: `${CLASSNAME$v}__image-slide`
  }, /*#__PURE__*/React.createElement(Thumbnail, {
    imgRef: useMergeRefs(imgRef, propImgRef),
    image: image,
    alt: alt,
    className: `${CLASSNAME$v}__thumbnail`,
    imgProps: _objectSpread2(_objectSpread2({}, imgProps), {}, {
      style: _objectSpread2(_objectSpread2(_objectSpread2({}, imgProps === null || imgProps === void 0 ? void 0 : imgProps.style), scaledImageSize || {
        maxHeight: scrollAreaSize === null || scrollAreaSize === void 0 ? void 0 : scrollAreaSize.height,
        maxWidth: scrollAreaSize === null || scrollAreaSize === void 0 ? void 0 : scrollAreaSize.width
      }), {}, {
        // Only animate when scale is set, and we are not pointer zooming and the user does not prefer reduced motion
        transition: scale && !isPointerZooming && !getPrefersReducedMotion() ? 'all 250ms' : undefined
      })
    }),
    loadingPlaceholderImageRef: loadingPlaceholderImageRef
  }));
}, isEqual);

const _excluded$z = ["image", "imgRef"];
/** Internal image slideshow component for ImageLightbox */
const ImageSlideshow = _ref => {
  var _images$activeIndex, _images$activeIndex2, _images$activeIndex3;
  let {
    activeImageIndex,
    images,
    slideGroupLabel,
    zoomInButtonProps,
    zoomOutButtonProps,
    slideshowControlsProps,
    currentPaginationItemRef,
    footerRef,
    activeImageRef
  } = _ref;
  const {
    activeIndex,
    slideshowId,
    setSlideshow,
    slideshowSlidesId,
    slidesCount,
    onNextClick,
    onPaginationClick,
    onPreviousClick,
    toggleAutoPlay
  } = SlideshowControls.useSlideshowControls({
    itemsCount: images.length,
    activeIndex: activeImageIndex
  });

  // Image metadata (caption)
  const title = (_images$activeIndex = images[activeIndex]) === null || _images$activeIndex === void 0 ? void 0 : _images$activeIndex.title;
  const description = (_images$activeIndex2 = images[activeIndex]) === null || _images$activeIndex2 === void 0 ? void 0 : _images$activeIndex2.description;
  const tags = (_images$activeIndex3 = images[activeIndex]) === null || _images$activeIndex3 === void 0 ? void 0 : _images$activeIndex3.tags;
  const metadata = title || description || tags ? /*#__PURE__*/React.createElement(ImageCaption, {
    theme: "dark",
    as: "div",
    title: title,
    description: description,
    tags: tags,
    align: "center"
  }) : null;

  // Slideshow controls
  const slideShowControls = slidesCount > 1 && slideshowControlsProps ? /*#__PURE__*/React.createElement(SlideshowControls, _extends({
    theme: "dark",
    activeIndex: activeIndex,
    slidesCount: slidesCount,
    onNextClick: onNextClick,
    onPreviousClick: onPreviousClick,
    onPaginationClick: onPaginationClick
  }, slideshowControlsProps, {
    paginationItemProps: index => {
      var _slideshowControlsPro;
      const props = (slideshowControlsProps === null || slideshowControlsProps === void 0 ? void 0 : (_slideshowControlsPro = slideshowControlsProps.paginationItemProps) === null || _slideshowControlsPro === void 0 ? void 0 : _slideshowControlsPro.call(slideshowControlsProps, index)) || {};
      return _objectSpread2(_objectSpread2({}, props), {}, {
        ref: mergeRefs(props === null || props === void 0 ? void 0 : props.ref,
        // Focus the active pagination item once on mount
        activeIndex === index ? currentPaginationItemRef : undefined)
      });
    }
  })) : null;

  // Zoom controls
  const [scale, setScale] = React.useState(undefined);
  const zoomEnabled = zoomInButtonProps && zoomOutButtonProps;
  const onScaleChange = React.useMemo(() => {
    if (!zoomEnabled) return undefined;
    return newScale => {
      setScale(function () {
        let prevScale = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
        return Math.max(1, newScale * prevScale);
      });
    };
  }, [zoomEnabled]);
  const zoomIn = React.useCallback(() => onScaleChange === null || onScaleChange === void 0 ? void 0 : onScaleChange(1.5), [onScaleChange]);
  const zoomOut = React.useCallback(() => onScaleChange === null || onScaleChange === void 0 ? void 0 : onScaleChange(0.5), [onScaleChange]);
  React.useEffect(() => {
    // Reset scale on slide change
    if (activeIndex) setScale(undefined);
  }, [activeIndex]);
  const zoomControls = zoomEnabled && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IconButton, _extends({}, zoomInButtonProps, {
    theme: "dark",
    emphasis: "low",
    icon: mdiMagnifyPlusOutline,
    onClick: zoomIn
  })), /*#__PURE__*/React.createElement(IconButton, _extends({}, zoomOutButtonProps, {
    theme: "dark",
    emphasis: "low",
    isDisabled: !scale || scale <= 1,
    icon: mdiMagnifyMinusOutline,
    onClick: zoomOut
  })));
  const getImgRef = React.useMemo(() => memoize((index, isActive) => {
    return mergeRefs(images === null || images === void 0 ? void 0 : images[index].imgRef, isActive ? activeImageRef : undefined);
  },
  // memoize based on both arguments
  function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return args.join();
  }), [images, activeImageRef]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Slides, {
    activeIndex: activeIndex,
    theme: "dark",
    slideGroupLabel: slideGroupLabel,
    fillHeight: true,
    id: slideshowId,
    ref: setSlideshow,
    slidesId: slideshowSlidesId,
    toggleAutoPlay: toggleAutoPlay
  }, images.map((_ref2, index) => {
    let {
        image,
        imgRef
      } = _ref2,
      imageProps = _objectWithoutProperties(_ref2, _excluded$z);
    const isActive = index === activeIndex;
    return /*#__PURE__*/React.createElement(ImageSlide, {
      isActive: isActive,
      key: image,
      image: _objectSpread2(_objectSpread2({}, imageProps), {}, {
        image,
        imgRef: getImgRef(index, isActive)
      }),
      scale: isActive ? scale : undefined,
      onScaleChange: onScaleChange
    });
  })), (metadata || slideShowControls || zoomControls) && /*#__PURE__*/React.createElement(FlexBox, {
    ref: footerRef,
    className: `${CLASSNAME$v}__footer`,
    orientation: "vertical",
    vAlign: "center",
    gap: "big"
  }, metadata, /*#__PURE__*/React.createElement(FlexBox, {
    className: `${CLASSNAME$v}__footer-actions`,
    orientation: "horizontal",
    gap: "regular"
  }, slideShowControls, zoomControls)));
};

/** Unref a react ref or element */
function unref(maybeElement) {
  if (maybeElement instanceof HTMLElement) return maybeElement;
  return maybeElement === null || maybeElement === void 0 ? void 0 : maybeElement.current;
}

function setTransitionViewName(elementRef, name) {
  const element = unref(elementRef);
  if (element) element.style.viewTransitionName = name;
}

/**
 * Wrapper around the `document.startViewTransition` handling browser incompatibilities, react DOM flush and
 * user preference.
 *
 * @param changes                callback containing the changes to apply within the view transition.
 * @param setViewTransitionName  set the `viewTransitionName` style on a `source` & `target` to morph these elements.
 */
async function startViewTransition(_ref) {
  var _document, _document$startViewTr;
  let {
    changes,
    viewTransitionName
  } = _ref;
  const start = (_document = document) === null || _document === void 0 ? void 0 : (_document$startViewTr = _document.startViewTransition) === null || _document$startViewTr === void 0 ? void 0 : _document$startViewTr.bind(document);
  const prefersReducedMotion = getPrefersReducedMotion();
  const {
    flushSync
  } = ReactDOM;
  if (prefersReducedMotion || !start || !flushSync || !(viewTransitionName !== null && viewTransitionName !== void 0 && viewTransitionName.source) || !(viewTransitionName !== null && viewTransitionName !== void 0 && viewTransitionName.target)) {
    // Skip, apply changes without a transition
    changes();
    return;
  }

  // Set transition name on source element
  setTransitionViewName(viewTransitionName.source, viewTransitionName.name);

  // Start view transition, apply changes & flush to DOM
  await start(() => {
    // Un-set transition name on source element
    setTransitionViewName(viewTransitionName.source, null);
    flushSync(changes);

    // Set transition name on target element
    setTransitionViewName(viewTransitionName.target, viewTransitionName.name);
  }).updateCallbackDone;

  // Un-set transition name on target element
  setTransitionViewName(viewTransitionName.target, null);
}

/** Find image in element including the element */
const findImage = element => element !== null && element !== void 0 && element.matches('img') ? element : (element === null || element === void 0 ? void 0 : element.querySelector('img')) || null;

const _excluded$A = ["images"];

/** Subset of the ImageLightboxProps managed by the useImageLightbox hook */

const EMPTY_PROPS = {
  isOpen: false,
  images: [],
  parentElement: /*#__PURE__*/React.createRef()
};
/**
 * Set up an ImageLightbox with images and triggers.
 *
 * - Associate a trigger with the lightbox to properly focus the trigger on close
 * - Associate a trigger with an image to display on open
 * - Automatically provide a view transition between an image trigger and the displayed image on open & close
 *
 * @param initialProps Images to display in the image lightbox
 */
function useImageLightbox(initialProps) {
  const {
      images = []
    } = initialProps,
    otherProps = _objectWithoutProperties(initialProps, _excluded$A);
  const imagesPropsRef = React.useRef(images);
  React.useEffect(() => {
    imagesPropsRef.current = images.map(props => _objectSpread2({
      imgRef: /*#__PURE__*/React.createRef()
    }, props));
  }, [images]);
  const currentImageRef = React.useRef(null);
  const [imageLightboxProps, setImageLightboxProps] = React.useState(() => _objectSpread2(_objectSpread2({}, EMPTY_PROPS), otherProps));
  const getTriggerProps = React.useMemo(() => {
    const triggerImageRefs = {};
    async function close() {
      const currentImage = currentImageRef.current;
      if (!currentImage) {
        return;
      }
      const currentIndex = imagesPropsRef.current.findIndex(_ref => {
        let {
          imgRef
        } = _ref;
        return (imgRef === null || imgRef === void 0 ? void 0 : imgRef.current) === currentImage;
      });
      await startViewTransition({
        changes() {
          // Close lightbox
          setImageLightboxProps(prevProps => _objectSpread2(_objectSpread2({}, prevProps), {}, {
            isOpen: false
          }));
        },
        // Morph from the image in lightbox to the image in trigger
        viewTransitionName: {
          source: currentImageRef,
          target: triggerImageRefs[currentIndex],
          name: CLASSNAME$v
        }
      });
    }
    async function open(triggerElement) {
      var _triggerImageRefs;
      let {
        activeImageIndex
      } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      // If we find an image inside the trigger, animate it in transition with the opening image
      const triggerImage = ((_triggerImageRefs = triggerImageRefs[activeImageIndex]) === null || _triggerImageRefs === void 0 ? void 0 : _triggerImageRefs.current) || findImage(triggerElement);

      // Inject the trigger image as loading placeholder for better loading state
      const imagesWithFallbackSize = imagesPropsRef.current.map((image, idx) => {
        if (triggerImage && idx === activeImageIndex && !image.loadingPlaceholderImageRef) {
          return _objectSpread2(_objectSpread2({}, image), {}, {
            loadingPlaceholderImageRef: {
              current: triggerImage
            }
          });
        }
        return image;
      });
      await startViewTransition({
        changes: () => {
          // Open lightbox with setup props
          setImageLightboxProps(prevProps => _objectSpread2(_objectSpread2({}, prevProps), {}, {
            activeImageRef: currentImageRef,
            parentElement: {
              current: triggerElement
            },
            isOpen: true,
            onClose: () => {
              var _prevProps$onClose;
              close();
              prevProps === null || prevProps === void 0 ? void 0 : (_prevProps$onClose = prevProps.onClose) === null || _prevProps$onClose === void 0 ? void 0 : _prevProps$onClose.call(prevProps);
            },
            images: imagesWithFallbackSize,
            activeImageIndex: activeImageIndex || 0
          }));
        },
        // Morph from the image in trigger to the image in lightbox
        viewTransitionName: {
          source: triggerImage,
          target: currentImageRef,
          name: CLASSNAME$v
        }
      });
    }
    return memoize(options => ({
      ref(element) {
        // Track trigger image ref if any
        if ((options === null || options === void 0 ? void 0 : options.activeImageIndex) !== undefined && element) {
          triggerImageRefs[options.activeImageIndex] = {
            current: findImage(element)
          };
        }
      },
      onClick(e) {
        open(e.target, options);
      }
    }));
  }, []);
  return {
    getTriggerProps,
    imageLightboxProps
  };
}

const _excluded$B = ["className", "isOpen", "closeButtonProps", "onClose", "parentElement", "activeImageIndex", "slideshowControlsProps", "slideGroupLabel", "images", "zoomOutButtonProps", "zoomInButtonProps", "activeImageRef"];
const Inner = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      isOpen,
      closeButtonProps,
      onClose,
      parentElement,
      activeImageIndex,
      slideshowControlsProps,
      slideGroupLabel,
      images,
      zoomOutButtonProps,
      zoomInButtonProps,
      activeImageRef: propImageRef
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$B);
  const currentPaginationItemRef = React.useRef(null);
  const footerRef = React.useRef(null);
  const imageRef = React.useRef(null);
  const clickAwayChildrenRefs = React.useRef([imageRef, footerRef]);
  const onClickAway = React.useCallback(evt => {
    const targetElement = evt.target;
    if (!(targetElement instanceof HTMLElement) || !(evt instanceof MouseEvent)) return;

    // Skip click away if clicking on the scrollbar
    if (targetElement.clientWidth < evt.clientX || targetElement.clientHeight < evt.clientY) return;
    onClose === null || onClose === void 0 ? void 0 : onClose();
  }, [onClose]);
  return /*#__PURE__*/React.createElement(Lightbox, _extends({
    ref: ref,
    className: classnames(className, CLASSNAME$v),
    parentElement: parentElement,
    isOpen: isOpen,
    onClose: onClose,
    closeButtonProps: closeButtonProps,
    focusElement: currentPaginationItemRef
  }, forwardedProps), /*#__PURE__*/React.createElement(ClickAwayProvider, {
    childrenRefs: clickAwayChildrenRefs,
    callback: onClickAway
  }, /*#__PURE__*/React.createElement(ImageSlideshow, {
    activeImageIndex: activeImageIndex,
    slideGroupLabel: slideGroupLabel,
    slideshowControlsProps: slideshowControlsProps,
    images: images,
    zoomInButtonProps: zoomInButtonProps,
    zoomOutButtonProps: zoomOutButtonProps,
    footerRef: footerRef,
    activeImageRef: useMergeRefs(propImageRef, imageRef),
    currentPaginationItemRef: currentPaginationItemRef
  })));
});
Inner.displayName = COMPONENT_NAME$y;
Inner.className = CLASSNAME$v;

/**
 * ImageLightbox component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ImageLightbox = Object.assign(Inner, {
  useImageLightbox
});

const _excluded$C = ["className", "color", "colorVariant", "typography", "children", "wrap"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$z = 'InlineList';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$w = getRootClassName(COMPONENT_NAME$z);

/**
 * Component default props.
 */
const DEFAULT_PROPS$r = {};

/**
 * InlineList component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const InlineList = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      color,
      colorVariant,
      typography,
      children,
      wrap
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$C);
  const fontColorClassName = color && getFontColorClassName(color, colorVariant);
  const typographyClassName = typography && getTypographyClassName(typography);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    React.createElement("ul", _extends({}, forwardedProps, {
      ref: ref,
      className: classnames(className, CLASSNAME$w, wrap && `${CLASSNAME$w}--wrap`, fontColorClassName, typographyClassName)
      // Lists with removed bullet style can lose their a11y list role on some browsers
      ,
      role: "list"
    }), Children.toArray(children).map((child, index) => {
      const key = /*#__PURE__*/isValidElement(child) && child.key || index;
      return (
        /*#__PURE__*/
        // We need to item is set as display: contents which removes the semantic.
        // eslint-disable-next-line jsx-a11y/no-redundant-roles
        React.createElement("li", {
          key: key,
          role: "listitem",
          className: `${CLASSNAME$w}__item`
        }, index !== 0 && /*#__PURE__*/React.createElement("span", {
          className: `${CLASSNAME$w}__item-separator`,
          "aria-hidden": "true"
        }, '\u00A0•\u00A0'), child)
      );
    }))
  );
});
InlineList.displayName = COMPONENT_NAME$z;
InlineList.className = CLASSNAME$w;
InlineList.defaultProps = DEFAULT_PROPS$r;

const INPUT_HELPER_CONFIGURATION = {
  error: {
    color: 'red'
  },
  success: {
    color: 'green'
  },
  warning: {
    color: 'yellow'
  }
};

const _excluded$D = ["children", "className", "kind", "theme"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$A = 'InputHelper';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$x = getRootClassName(COMPONENT_NAME$A);

/**
 * Component default props.
 */
const DEFAULT_PROPS$s = {
  kind: Kind.info,
  theme: Theme.light
};

/**
 * InputHelper component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const InputHelper = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      kind,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$D);
  const {
    color
  } = INPUT_HELPER_CONFIGURATION[kind] || {};
  return /*#__PURE__*/React.createElement("p", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$x,
      color,
      theme
    }))
  }), children);
});
InputHelper.displayName = COMPONENT_NAME$A;
InputHelper.className = CLASSNAME$x;
InputHelper.defaultProps = DEFAULT_PROPS$s;

const _excluded$E = ["children", "className", "htmlFor", "isRequired", "theme"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$B = 'InputLabel';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$y = getRootClassName(COMPONENT_NAME$B);

/**
 * Component default props.
 */
const DEFAULT_PROPS$t = {
  theme: Theme.light
};

/**
 * InputLabel component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const InputLabel = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      htmlFor,
      isRequired,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$E);
  return /*#__PURE__*/React.createElement("label", _extends({
    ref: ref
  }, forwardedProps, {
    htmlFor: htmlFor,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$y,
      isRequired,
      theme
    }))
  }), children);
});
InputLabel.displayName = COMPONENT_NAME$B;
InputLabel.className = CLASSNAME$y;
InputLabel.defaultProps = DEFAULT_PROPS$t;

const _excluded$F = ["aria-labelledby", "ariaLabelledBy", "aria-label", "ariaLabel", "children", "className", "closeButtonProps", "isOpen", "onClose", "parentElement", "focusElement", "preventAutoClose", "theme", "zIndex"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$C = 'Lightbox';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$z = getRootClassName(COMPONENT_NAME$C);

/**
 * Lightbox component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Lightbox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      'aria-labelledby': propAriaLabelledBy,
      ariaLabelledBy = propAriaLabelledBy,
      'aria-label': propAriaLabel,
      ariaLabel = propAriaLabel,
      children,
      className,
      closeButtonProps,
      isOpen,
      onClose,
      parentElement,
      focusElement,
      preventAutoClose,
      theme,
      zIndex
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$F);
  if (!DOCUMENT) {
    // Can't render in SSR.
    return null;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const childrenRef = useRef(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const wrapperRef = useRef(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const closeButtonRef = useRef(null);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useDisableBodyScroll(isOpen && wrapperRef.current);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isVisible = useTransitionVisibility(wrapperRef, !!isOpen, DIALOG_TRANSITION_DURATION);

  // Handle focus trap.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useFocusTrap(
  // Focus trap zone
  isOpen && wrapperRef.current,
  // Focus element (fallback on close button and then on the dialog)
  (focusElement === null || focusElement === void 0 ? void 0 : focusElement.current) || closeButtonRef.current || wrapperRef.current);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const previousOpen = useRef(isOpen);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (isOpen !== previousOpen.current) {
      previousOpen.current = isOpen;

      // Focus the parent element on close.
      if (!isOpen && parentElement && parentElement.current) {
        parentElement.current.focus();
      }
    }
  }, [isOpen, parentElement]);

  // Close lightbox on escape key pressed.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useCallbackOnEscape(onClose);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const clickAwayRefs = useRef([wrapperRef]);
  if (!isOpen && !isVisible) return null;
  return /*#__PURE__*/createPortal(
  /*#__PURE__*/
  /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
  React.createElement("div", _extends({
    ref: mergeRefs(ref, wrapperRef)
  }, forwardedProps, {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-modal": "true",
    role: "dialog",
    tabIndex: -1,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$z,
      isHidden: !isOpen,
      isShown: isOpen || isVisible,
      theme
    })),
    style: {
      zIndex
    }
  }), closeButtonProps && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$z}__close`
  }, /*#__PURE__*/React.createElement(IconButton, _extends({}, closeButtonProps, {
    ref: closeButtonRef,
    emphasis: "low",
    hasBackground: true,
    icon: mdiClose,
    theme: "dark",
    type: "button",
    onClick: onClose
  }))), /*#__PURE__*/React.createElement(ClickAwayProvider, {
    callback: !preventAutoClose && onClose,
    childrenRefs: clickAwayRefs
  }, /*#__PURE__*/React.createElement("div", {
    ref: childrenRef,
    className: `${CLASSNAME$z}__wrapper`,
    role: "presentation"
  }, children))), document.body);
});
Lightbox.displayName = COMPONENT_NAME$C;
Lightbox.className = CLASSNAME$z;

const _excluded$G = ["children", "className", "color", "colorVariant", "disabled", "isDisabled", "href", "leftIcon", "linkAs", "rightIcon", "target", "typography"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$D = 'Link';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$A = getRootClassName(COMPONENT_NAME$D);
const getIconSize = typography => {
  switch (typography) {
    case Typography.display1:
      return Size.m;
    case Typography.headline:
    case Typography.title:
    case Typography.custom.title1:
    case Typography.custom.title2:
    case Typography.custom.title3:
    case Typography.custom.title4:
    case Typography.custom.title5:
    case Typography.custom.title6:
    case Typography.body2:
    case Typography.subtitle2:
      return Size.s;
    case Typography.body1:
    case Typography.subtitle1:
      return Size.xs;
    case Typography.caption:
    case Typography.overline:
      return Size.xxs;
    default:
      return Size.s;
  }
};

/**
 * Link component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Link = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      color,
      colorVariant,
      disabled,
      isDisabled = disabled,
      href,
      leftIcon,
      linkAs,
      rightIcon,
      target,
      typography
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$G);
  const renderedChildren = useMemo(() => /*#__PURE__*/React.createElement(React.Fragment, null, leftIcon && !isEmpty(leftIcon) && /*#__PURE__*/React.createElement(Icon, {
    icon: leftIcon,
    className: `${CLASSNAME$A}__left-icon`,
    size: getIconSize(typography)
  }), children && /*#__PURE__*/React.createElement("span", {
    className: classnames(`${CLASSNAME$A}__content`, {
      [`lumx-typography-${typography}`]: typography
    })
  }, children), rightIcon && !isEmpty(rightIcon) && /*#__PURE__*/React.createElement(Icon, {
    icon: rightIcon,
    className: `${CLASSNAME$A}__right-icon`,
    size: getIconSize(typography)
  })), [leftIcon, typography, children, rightIcon]);

  /**
   * If there is no linkAs prop and no href, we returned a <button> instead of a <a>.
   * If the component is disabled, we also returned a <button> since disabled is not compatible with <a>.
   */
  if (!linkAs && isEmpty(href) || isDisabled) {
    return /*#__PURE__*/React.createElement("button", _extends({
      type: "button"
    }, forwardedProps, {
      ref: ref,
      disabled: isDisabled,
      className: classnames(className, handleBasicClasses({
        prefix: CLASSNAME$A,
        color,
        colorVariant
      }))
    }), renderedChildren);
  }
  return renderLink(_objectSpread2(_objectSpread2({
    linkAs
  }, forwardedProps), {}, {
    href,
    target,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$A,
      color,
      colorVariant
    })),
    ref: ref
  }), renderedChildren);
});
Link.displayName = COMPONENT_NAME$D;
Link.className = CLASSNAME$A;

const _excluded$H = ["className", "description", "link", "linkAs", "linkProps", "size", "theme", "thumbnailProps", "title", "titleHeading"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$E = 'LinkPreview';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$B = getRootClassName(COMPONENT_NAME$E);

/**
 * Component default props.
 */
const DEFAULT_PROPS$u = {
  size: Size.regular,
  theme: Theme.light,
  titleHeading: 'h2'
};

/**
 * LinkPreview component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const LinkPreview = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      description,
      link,
      linkAs,
      linkProps,
      size,
      theme,
      thumbnailProps,
      title,
      titleHeading
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$H);
  // Use title heading as title wrapper (see DEFAULT_PROPS for the default value).
  const TitleHeading = titleHeading;
  return /*#__PURE__*/React.createElement("article", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$B,
      size: size === Size.big && thumbnailProps ? Size.big : Size.regular,
      theme
    }))
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$B}__wrapper`
  }, thumbnailProps && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$B}__thumbnail`
  }, /*#__PURE__*/React.createElement(Thumbnail, _extends({}, thumbnailProps, {
    linkAs: linkAs,
    linkProps: _objectSpread2(_objectSpread2({}, linkProps), {}, {
      href: link,
      target: '_blank',
      // Avoid redundant links in focus order
      tabIndex: -1
    }),
    aspectRatio: AspectRatio.free,
    fillHeight: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$B}__container`
  }, title && /*#__PURE__*/React.createElement(TitleHeading, {
    className: `${CLASSNAME$B}__title`
  }, /*#__PURE__*/React.createElement(Link, _extends({}, linkProps, {
    linkAs: linkAs,
    target: "_blank",
    href: link,
    color: theme === Theme.light ? ColorPalette.dark : ColorPalette.light,
    colorVariant: ColorVariant.N
  }), title)), description && /*#__PURE__*/React.createElement("p", {
    className: `${CLASSNAME$B}__description`
  }, description), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$B}__link`
  }, /*#__PURE__*/React.createElement(Link, _extends({}, linkProps, {
    linkAs: linkAs,
    className: classnames(`${CLASSNAME$B}__link`, linkProps === null || linkProps === void 0 ? void 0 : linkProps.className),
    target: "_blank",
    href: link,
    color: theme === Theme.light ? ColorPalette.primary : ColorPalette.light,
    colorVariant: ColorVariant.N
    // Avoid redundant links in focus order
    ,
    tabIndex: title ? '-1' : undefined
  }), link)))));
});
LinkPreview.displayName = COMPONENT_NAME$E;
LinkPreview.className = CLASSNAME$B;
LinkPreview.defaultProps = DEFAULT_PROPS$u;

const _excluded$I = ["className"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$F = 'ListDivider';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$C = getRootClassName(COMPONENT_NAME$F);

/**
 * ListDivider component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ListDivider = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$I);
  return /*#__PURE__*/React.createElement("li", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$C
    }))
  }));
});
ListDivider.displayName = COMPONENT_NAME$F;
ListDivider.className = CLASSNAME$C;

const _excluded$J = ["children", "className"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$G = 'ListSubheader';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$D = getRootClassName(COMPONENT_NAME$G);

/**
 * ListSubheader component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ListSubheader = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$J);
  return /*#__PURE__*/React.createElement("li", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$D
    }))
  }), children);
});
ListSubheader.displayName = COMPONENT_NAME$G;
ListSubheader.className = CLASSNAME$D;

const _excluded$K = ["children", "className", "hasBackground", "kind", "icon", "closeButtonProps"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$H = 'Message';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$E = getRootClassName(COMPONENT_NAME$H);

/**
 * Associative map from message kind to color and icon.
 */
const CONFIG$1 = {
  [Kind.error]: {
    color: ColorPalette.red,
    icon: mdiAlert
  },
  [Kind.info]: {
    color: ColorPalette.blue,
    icon: mdiInformation
  },
  [Kind.success]: {
    color: ColorPalette.green,
    icon: mdiCheckCircle
  },
  [Kind.warning]: {
    color: ColorPalette.yellow,
    icon: mdiAlertCircle
  }
};

/**
 * Message component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Message = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      hasBackground,
      kind,
      icon: customIcon,
      closeButtonProps
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$K);
  const {
    color,
    icon
  } = CONFIG$1[kind] || {};
  const {
    onClick,
    label: closeButtonLabel
  } = closeButtonProps || {};
  const isCloseButtonDisplayed = hasBackground && kind === 'info' && onClick && closeButtonLabel;
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref,
    className: classnames(className, handleBasicClasses({
      color,
      hasBackground,
      prefix: CLASSNAME$E
    }))
  }, forwardedProps), (customIcon || icon) && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$E}__icon`,
    icon: customIcon || icon,
    size: Size.xs,
    color: color
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$E}__text`
  }, children), isCloseButtonDisplayed && /*#__PURE__*/React.createElement(IconButton, {
    className: `${CLASSNAME$E}__close-button`,
    icon: mdiClose,
    onClick: onClick,
    label: closeButtonLabel,
    emphasis: Emphasis.low
  }));
});
Message.displayName = COMPONENT_NAME$H;
Message.className = CLASSNAME$E;

const _excluded$L = ["className", "theme", "thumbnails", "onImageClick"],
  _excluded2$1 = ["image", "onClick", "align"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$I = 'Mosaic';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$F = getRootClassName(COMPONENT_NAME$I);

/**
 * Component default props.
 */
const DEFAULT_PROPS$v = {
  theme: Theme.light
};

/**
 * Mosaic component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Mosaic = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      theme,
      thumbnails,
      onImageClick
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$L);
  const handleImageClick = useMemo(() => {
    if (!onImageClick) return undefined;
    return (index, onClick) => event => {
      onClick === null || onClick === void 0 ? void 0 : onClick(event);
      onImageClick === null || onImageClick === void 0 ? void 0 : onImageClick(index);
    };
  }, [onImageClick]);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$F,
      theme
    }), {
      [`${CLASSNAME$F}--has-1-thumbnail`]: (thumbnails === null || thumbnails === void 0 ? void 0 : thumbnails.length) === 1,
      [`${CLASSNAME$F}--has-2-thumbnails`]: (thumbnails === null || thumbnails === void 0 ? void 0 : thumbnails.length) === 2,
      [`${CLASSNAME$F}--has-3-thumbnails`]: (thumbnails === null || thumbnails === void 0 ? void 0 : thumbnails.length) === 3,
      [`${CLASSNAME$F}--has-4-thumbnails`]: (thumbnails === null || thumbnails === void 0 ? void 0 : thumbnails.length) >= 4
    })
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$F}__wrapper`
  }, take(thumbnails, 4).map((thumbnail, index) => {
    const {
        image,
        onClick,
        align
      } = thumbnail,
      thumbnailProps = _objectWithoutProperties(thumbnail, _excluded2$1);
    return /*#__PURE__*/React.createElement("div", {
      key: index,
      className: `${CLASSNAME$F}__thumbnail`
    }, /*#__PURE__*/React.createElement(Thumbnail, _extends({}, thumbnailProps, {
      align: align || Alignment.left,
      image: image,
      theme: theme,
      aspectRatio: AspectRatio.free,
      fillHeight: true,
      onClick: (handleImageClick === null || handleImageClick === void 0 ? void 0 : handleImageClick(index, onClick)) || onClick
    })), thumbnails.length > 4 && index === 3 && /*#__PURE__*/React.createElement("div", {
      className: `${CLASSNAME$F}__overlay`
    }, /*#__PURE__*/React.createElement("span", null, "+", thumbnails.length - 4)));
  })));
});
Mosaic.displayName = COMPONENT_NAME$I;
Mosaic.className = CLASSNAME$F;
Mosaic.defaultProps = DEFAULT_PROPS$v;

const ThemeContext = /*#__PURE__*/createContext(undefined);

/** Same as `React.forwardRef` but inferring Ref type from the `as` prop. */
function forwardRefPolymorphic(render) {
  return /*#__PURE__*/React.forwardRef(render);
}

const _excluded$M = ["className", "icon", "label", "isCurrentPage", "as"];

/** Make `href` required when `as` is `a` */

/**
 * Navigation item props
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$J = 'NavigationItem';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$G = getRootClassName(COMPONENT_NAME$J);
const NavigationItem = Object.assign(forwardRefPolymorphic((props, ref) => {
  const {
      className,
      icon,
      label,
      isCurrentPage,
      as: Element = 'a'
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$M);
  const theme = useContext(ThemeContext);
  const {
    tooltipLabel,
    labelRef
  } = useOverflowTooltipLabel();
  const buttonProps = Element === 'button' ? {
    type: 'button'
  } : {};
  return /*#__PURE__*/React.createElement("li", {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$G,
      theme
    }))
  }, /*#__PURE__*/React.createElement(Tooltip, {
    label: tooltipLabel,
    placement: Placement.TOP
  }, /*#__PURE__*/React.createElement(Element, _extends({
    className: handleBasicClasses({
      prefix: `${CLASSNAME$G}__link`,
      isSelected: isCurrentPage
    }),
    ref: ref,
    "aria-current": isCurrentPage ? 'page' : undefined
  }, buttonProps, forwardedProps), icon ? /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$G}__icon`,
    icon: icon,
    size: Size.xs,
    theme: theme
  }) : null, /*#__PURE__*/React.createElement(Text, {
    as: "span",
    truncate: true,
    className: `${CLASSNAME$G}__label`,
    ref: labelRef
  }, label))));
}), {
  displayName: COMPONENT_NAME$J,
  className: CLASSNAME$G
});

const NavigationContext = /*#__PURE__*/createContext({
  orientation: Orientation.vertical
});

const _excluded$N = ["children", "className", "label", "icon"];
/**
 * Component display name.
 */
const COMPONENT_NAME$K = 'NavigationSection';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$H = getRootClassName(COMPONENT_NAME$K);
const NavigationSection = Object.assign( /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      label,
      icon
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$N);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const sectionId = useId();
  const {
    orientation
  } = useContext(NavigationContext) || {};
  const theme = useContext(ThemeContext);
  const isDropdown = orientation === Orientation.horizontal;
  return /*#__PURE__*/React.createElement("li", _extends({
    className: classnames(className, CLASSNAME$H, CLASSNAME$G, handleBasicClasses({
      prefix: CLASSNAME$G,
      theme
    })),
    ref: ref
  }, forwardedProps), /*#__PURE__*/React.createElement("button", {
    "aria-controls": sectionId,
    "aria-expanded": isOpen,
    className: classnames(`${CLASSNAME$G}__link`),
    ref: buttonRef,
    onClick: event => {
      setIsOpen(!isOpen);
      event.stopPropagation();
    },
    type: "button"
  }, icon ? /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$G}__icon`,
    icon: icon,
    size: Size.xs
  }) : null, /*#__PURE__*/React.createElement(Text, {
    as: "span",
    truncate: true,
    className: `${CLASSNAME$G}__label`,
    ref: ref
  }, label), /*#__PURE__*/React.createElement(Icon, {
    className: classnames(`${CLASSNAME$G}__icon`, `${CLASSNAME$H}__chevron`),
    icon: isOpen ? mdiChevronUp : mdiChevronDown
  })), isOpen && (isDropdown ? /*#__PURE__*/React.createElement(Popover, {
    anchorRef: buttonRef,
    isOpen: isOpen,
    placement: Placement.BOTTOM_START,
    usePortal: false,
    closeOnClickAway: true,
    closeOnEscape: true,
    onClick: () => setIsOpen(false),
    onClose: () => setIsOpen(false),
    zIndex: 996
  }, /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: Theme.light
  }, /*#__PURE__*/React.createElement("ul", {
    className: `${CLASSNAME$H}__drawer--popover`,
    id: sectionId
  }, /*#__PURE__*/React.createElement(NavigationContext.Provider, {
    value: {
      orientation: Orientation.vertical
    }
  }, children)))) : /*#__PURE__*/React.createElement("ul", {
    className: `${CLASSNAME$H}__drawer`,
    id: sectionId
  }, children)));
}), {
  displayName: COMPONENT_NAME$K,
  className: CLASSNAME$H
});

const _excluded$O = ["children", "className", "theme", "orientation"];
/**
 * Component display name.
 */
const COMPONENT_NAME$L = 'Navigation';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$I = getRootClassName(COMPONENT_NAME$L);
const Navigation = Object.assign(
/*#__PURE__*/
// eslint-disable-next-line react/display-name
forwardRef((props, ref) => {
  const {
      children,
      className,
      theme,
      orientation
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$O);
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: theme
  }, /*#__PURE__*/React.createElement("nav", _extends({
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$I,
      theme,
      orientation
    })),
    ref: ref
  }, forwardedProps), /*#__PURE__*/React.createElement(NavigationContext.Provider, {
    value: {
      orientation
    }
  }, /*#__PURE__*/React.createElement("ul", {
    className: `${CLASSNAME$I}__list`
  }, children))));
}), {
  displayName: COMPONENT_NAME$L,
  className: CLASSNAME$I,
  defaultProps: {
    theme: Theme.light,
    orientation: Orientation.vertical
  },
  // Sub components
  Section: NavigationSection,
  Item: NavigationItem
});

/**
 * Notification icon and colors according to their type.
 */
const NOTIFICATION_CONFIGURATION = {
  error: {
    color: 'red',
    icon: mdiAlert
  },
  info: {
    color: 'blue',
    icon: mdiInformation
  },
  success: {
    color: 'green',
    icon: mdiCheckCircle
  },
  warning: {
    color: 'yellow',
    icon: mdiAlertCircle
  }
};

const _excluded$P = ["actionLabel", "className", "content", "isOpen", "onActionClick", "onClick", "theme", "type", "zIndex", "usePortal", "style"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$M = 'Notification';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$J = getRootClassName(COMPONENT_NAME$M);

/**
 * Component default props.
 */
const DEFAULT_PROPS$w = {
  theme: Theme.light,
  zIndex: 9999,
  usePortal: true
};

/* eslint-disable react-hooks/rules-of-hooks, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
/**
 * Notification component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Notification = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      actionLabel,
      className,
      content,
      isOpen,
      onActionClick,
      onClick,
      theme,
      type,
      zIndex,
      usePortal,
      style
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$P);
  if (!DOCUMENT) {
    // Can't render in SSR.
    return null;
  }
  const {
    color,
    icon
  } = NOTIFICATION_CONFIGURATION[type] || {};
  const rootRef = useRef(null);
  const isVisible = useTransitionVisibility(rootRef, !!isOpen, NOTIFICATION_TRANSITION_DURATION);
  const hasAction = Boolean(onActionClick) && Boolean(actionLabel);
  const handleCallback = evt => {
    if (isFunction(onActionClick)) {
      onActionClick();
    }
    evt.stopPropagation();
  };
  if (!type || !isVisible) {
    return null;
  }
  const notification =
  /*#__PURE__*/
  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
  React.createElement("div", _extends({
    ref: mergeRefs(ref, rootRef),
    role: "alert"
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      color,
      hasAction,
      isHidden: !isOpen,
      prefix: CLASSNAME$J
    })),
    onClick: onClick,
    style: _objectSpread2(_objectSpread2({}, style), {}, {
      zIndex
    })
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$J}__icon`
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: icon,
    size: Size.s
  })), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$J}__content`
  }, content), hasAction && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$J}__action`
  }, /*#__PURE__*/React.createElement(Button, {
    emphasis: Emphasis.medium,
    theme: theme,
    onClick: handleCallback
  }, /*#__PURE__*/React.createElement("span", null, actionLabel))));
  return usePortal ? /*#__PURE__*/createPortal(notification, document.body) : notification;
});
Notification.displayName = COMPONENT_NAME$M;
Notification.className = CLASSNAME$J;
Notification.defaultProps = DEFAULT_PROPS$w;

const _excluded$Q = ["children", "isOpen", "focusElement", "aria-label", "label", "className"];

/**
 * PopoverDialog props.
 * The PopoverDialog has the same props as the Popover but requires an accessible label.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$N = 'PopoverDialog';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$K = getRootClassName(COMPONENT_NAME$N);

/**
 * Component default props.
 */
const DEFAULT_PROPS$x = {};

/**
 * PopoverDialog component.
 * Defines a popover that acts like a dialog
 * * Has a dialog aria role
 * * Sets a focus trap within the popover
 * * Closes on click away and escape.
 */
const PopoverDialog = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      isOpen,
      focusElement,
      'aria-label': ariaLabel,
      label = ariaLabel,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$Q);
  return /*#__PURE__*/React.createElement(Popover, _extends({}, forwardedProps, {
    ref: ref,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$K
    })),
    role: "dialog",
    "aria-modal": "true"
    /**
     * If a label is set, set as aria-label.
     * If it is undefined, the label can be set using the `aria-label` and `aria-labelledby` props
     */,
    "aria-label": label,
    isOpen: isOpen,
    focusElement: focusElement,
    closeOnClickAway: true,
    closeOnEscape: true,
    withFocusTrap: true
  }), children);
});
PopoverDialog.displayName = COMPONENT_NAME$N;
PopoverDialog.className = CLASSNAME$K;
PopoverDialog.defaultProps = DEFAULT_PROPS$x;

const _excluded$R = ["actions", "attachments", "author", "className", "meta", "onClick", "orientation", "tags", "text", "theme", "thumbnailProps", "title"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$O = 'PostBlock';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$L = getRootClassName(COMPONENT_NAME$O);

/**
 * Component default props.
 */
const DEFAULT_PROPS$y = {
  orientation: Orientation.horizontal,
  theme: Theme.light
};

/**
 * PostBlock component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const PostBlock = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      actions,
      attachments,
      author,
      className,
      meta,
      onClick,
      orientation,
      tags,
      text,
      theme,
      thumbnailProps,
      title
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$R);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$L,
      orientation,
      theme
    }))
  }, forwardedProps), thumbnailProps && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$L}__thumbnail`
  }, /*#__PURE__*/React.createElement(Thumbnail, _extends({}, thumbnailProps, {
    theme: theme,
    variant: ThumbnailVariant.rounded
  }))), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$L}__wrapper`
  }, author && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$L}__author`
  }, author), title && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: `${CLASSNAME$L}__title`,
    onClick: onClick
  }, title), meta && /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$L}__meta`
  }, meta), isObject(text) && text.__html ?
  /*#__PURE__*/
  // eslint-disable-next-line react/no-danger
  React.createElement("p", {
    dangerouslySetInnerHTML: text,
    className: `${CLASSNAME$L}__text`
  }) : /*#__PURE__*/React.createElement("p", {
    className: `${CLASSNAME$L}__text`
  }, text), attachments && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$L}__attachments`
  }, attachments), (tags || actions) && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$L}__toolbar`
  }, tags && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$L}__tags`
  }, tags), actions && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$L}__actions`
  }, actions))));
});
PostBlock.displayName = COMPONENT_NAME$O;
PostBlock.className = CLASSNAME$L;
PostBlock.defaultProps = DEFAULT_PROPS$y;

const _excluded$S = ["className", "theme"];
/**
 * Component display name.
 */
const COMPONENT_NAME$P = 'ProgressLinear';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$M = getRootClassName(COMPONENT_NAME$P);

/**
 * Component default props.
 */
const DEFAULT_PROPS$z = {
  theme: Theme.light
};

/**
 * ProgressLinear component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ProgressLinear = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$S);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$M,
      theme
    }))
  }), /*#__PURE__*/React.createElement("div", {
    className: "lumx-progress-linear__line1"
  }), /*#__PURE__*/React.createElement("div", {
    className: "lumx-progress-linear__line2"
  }));
});
ProgressLinear.displayName = COMPONENT_NAME$P;
ProgressLinear.className = CLASSNAME$M;
ProgressLinear.defaultProps = DEFAULT_PROPS$z;

const _excluded$T = ["className", "theme", "size"];

/**
 * Progress sizes.
 */

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$Q = 'ProgressCircular';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$N = getRootClassName(COMPONENT_NAME$Q);

/**
 * Component default props.
 */
const DEFAULT_PROPS$A = {
  theme: Theme.light,
  size: Size.m
};

/**
 * ProgressCircularProps component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ProgressCircular = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      theme,
      size
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$T);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$N,
      theme,
      size
    }))
  }), /*#__PURE__*/React.createElement("div", {
    className: "lumx-progress-circular__double-bounce1"
  }), /*#__PURE__*/React.createElement("div", {
    className: "lumx-progress-circular__double-bounce2"
  }), /*#__PURE__*/React.createElement("svg", {
    className: "lumx-progress-circular__svg",
    viewBox: "25 25 50 50"
  }, /*#__PURE__*/React.createElement("circle", {
    className: "lumx-progress-circular__path",
    cx: "50",
    cy: "50",
    r: "20",
    fill: "none",
    strokeWidth: "5"
  })));
});
ProgressCircular.displayName = COMPONENT_NAME$Q;
ProgressCircular.className = CLASSNAME$N;
ProgressCircular.defaultProps = DEFAULT_PROPS$A;

const _excluded$U = ["className", "theme", "variant"];

/**
 * Progress variants.
 */
const ProgressVariant = {
  linear: 'linear',
  circular: 'circular'
};

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$R = 'Progress';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$O = getRootClassName(COMPONENT_NAME$R);

/**
 * Component default props.
 */
const DEFAULT_PROPS$B = {
  theme: Theme.light,
  variant: ProgressVariant.circular
};

/**
 * Progress component.
 *
 * @deprecated use `ProgressLinear` and `ProgressCircular` instead.
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Progress = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      theme,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$U);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$O,
      theme,
      variant
    }))
  }), variant === ProgressVariant.circular && /*#__PURE__*/React.createElement(ProgressCircular, {
    theme: theme
  }), variant === ProgressVariant.linear && /*#__PURE__*/React.createElement(ProgressLinear, {
    theme: theme
  }));
});
Progress.displayName = COMPONENT_NAME$R;
Progress.className = CLASSNAME$O;
Progress.defaultProps = DEFAULT_PROPS$B;

const INIT_STATE = {
  isLazy: true,
  shouldActivateOnFocus: false,
  activeTabIndex: 0,
  ids: {
    tab: [],
    tabPanel: []
  }
};
const reducer = (state, action) => {
  switch (action.type) {
    case 'update':
      return _objectSpread2(_objectSpread2({}, state), action.payload);
    case 'setActiveTabIndex':
      {
        if (state.activeTabIndex === action.payload) {
          return state;
        }
        // Change active tab index.
        return _objectSpread2(_objectSpread2({}, state), {}, {
          activeTabIndex: action.payload
        });
      }
    case 'register':
      {
        const {
          type,
          id
        } = action.payload;
        // Append tab/tabPanel id in state.
        return _objectSpread2(_objectSpread2({}, state), {}, {
          ids: _objectSpread2(_objectSpread2({}, state.ids), {}, {
            [type]: [...state.ids[type], id]
          })
        });
      }
    case 'unregister':
      {
        const {
          type,
          id
        } = action.payload;
        const index = state.ids[type].indexOf(id);
        if (index === -1) return state;
        // Remove tab & tab panel at index.
        const tabIds = [...state.ids.tab];
        tabIds.splice(index, 1);
        const tabPanelIds = [...state.ids.tabPanel];
        tabPanelIds.splice(index, 1);
        return _objectSpread2(_objectSpread2({}, state), {}, {
          ids: {
            tab: tabIds,
            tabPanel: tabPanelIds
          }
        });
      }
    default:
      return state;
  }
};
const TabProviderContext = /*#__PURE__*/createContext(null);
/* eslint-disable react-hooks/rules-of-hooks */
const useTabProviderContext = (type, originalId) => {
  const context = useContext(TabProviderContext);
  if (!context) {
    return undefined;
  }
  const [state, dispatch] = context;

  // Current tab or tab panel id.
  const generatedId = useId();
  const id = originalId || generatedId;
  useEffect(() => {
    // On mount: register tab or tab panel id.
    dispatch({
      type: 'register',
      payload: {
        type,
        id
      }
    });
    return () => {
      // On unmount: unregister tab or tab panel id.
      dispatch({
        type: 'unregister',
        payload: {
          type,
          id
        }
      });
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  // Find tab/tabPanel index using it's id.
  const index = useMemo(() => state.ids[type].indexOf(id), [state.ids, type, id]);
  const tabId = useMemo(() => state.ids.tab[index] || '', [state, index]);
  const tabPanelId = useMemo(() => state.ids.tabPanel[index] || '', [state, index]);
  const isActive = useMemo(() => state.activeTabIndex === index, [state, index]);
  const changeToTab = useCallback(() => dispatch({
    type: 'setActiveTabIndex',
    payload: index
  }), [dispatch, index]);
  return {
    isLazy: state.isLazy,
    shouldActivateOnFocus: state.shouldActivateOnFocus,
    tabId,
    tabPanelId,
    isActive,
    changeToTab
  };
};
const useTabProviderContextState = () => {
  const context = useContext(TabProviderContext);
  return context === null || context === void 0 ? void 0 : context[0];
};

const _excluded$V = ["children", "onChange"];
const DEFAULT_PROPS$C = {
  isLazy: INIT_STATE.isLazy,
  shouldActivateOnFocus: INIT_STATE.shouldActivateOnFocus
};

/**
 * This component provides a context in which steps can be defined and linked to their step panel.
 *
 * It does not produce any markup so you can wrap it around any React elements and then split the ProgressTracker and ProgressTrackerPanel
 * components in the react tree.
 *
 * This works exactly as TabProvider so it uses TabProviderContext and tabs state.
 *
 * @param  props React component props.
 * @return React element.
 */
const ProgressTrackerProvider = props => {
  const {
      children,
      onChange
    } = props,
    propState = _objectWithoutProperties(props, _excluded$V);
  const [state, dispatch] = useReducer(reducer, INIT_STATE);

  // On prop state change => dispatch update.
  useEffect(() => {
    dispatch({
      type: 'update',
      payload: _objectSpread2(_objectSpread2({}, propState), {}, {
        activeTabIndex: propState.activeStepIndex || INIT_STATE.activeTabIndex
      })
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [dispatch, ...Object.values(propState)]);

  // On active tab index state change => send update to the onChange.
  useEffect(() => {
    if (state === INIT_STATE || !onChange || propState.activeStepIndex === state.activeTabIndex) {
      return;
    }
    onChange(state.activeTabIndex);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [onChange, state.activeTabIndex]);
  return /*#__PURE__*/React.createElement(TabProviderContext.Provider, {
    value: [state, dispatch]
  }, children);
};
ProgressTrackerProvider.defaultProps = DEFAULT_PROPS$C;

const useRovingTabIndex = _ref => {
  let {
    parentRef,
    elementSelector,
    keepTabIndex,
    onElementFocus,
    extraDependencies = []
  } = _ref;
  useEffect(() => {
    const parent = parentRef === null || parentRef === void 0 ? void 0 : parentRef.current;
    if (!parent) {
      return undefined;
    }
    const elements = parent.querySelectorAll(elementSelector);
    const initialFocusableElement = parent === null || parent === void 0 ? void 0 : parent.querySelector(`${elementSelector}[tabindex="0"]`);
    const handleKeyDown = index => evt => {
      let newTabFocus = index;
      if (!(evt.key === 'ArrowRight' || evt.key === 'ArrowLeft')) {
        return;
      }
      if (evt.key === 'ArrowRight') {
        // Move right
        newTabFocus += 1;
        // If we're at the end, go to the start
        if (newTabFocus >= elements.length) {
          newTabFocus = 0;
        }
      } else if (evt.key === 'ArrowLeft') {
        // Move left
        newTabFocus -= 1;
        if (newTabFocus < 0) {
          // If we're at the start, move to the end
          newTabFocus = elements.length - 1;
        }
      }
      const newElement = elements[newTabFocus];
      newElement === null || newElement === void 0 ? void 0 : newElement.focus();

      // When an element is focused using roving tab index, trigger the onElementFocus callback
      if (newElement && onElementFocus) {
        onElementFocus(newElement);
      }
      if (keepTabIndex) {
        evt.currentTarget.setAttribute('tabindex', '-1');
        newElement === null || newElement === void 0 ? void 0 : newElement.setAttribute('tabindex', '0');
      }
    };
    if ((elements === null || elements === void 0 ? void 0 : elements.length) > 0) {
      elements.forEach((el, key) => {
        // if no element has tabindex set to 0, set the first element as focusable
        if (!initialFocusableElement && key === 0) {
          el.setAttribute('tabindex', '0');
          // set all other to -1
        } else if (initialFocusableElement !== el) {
          el.setAttribute('tabindex', '-1');
        }
        // add event listener
        el.addEventListener('keydown', handleKeyDown(key));
      });
    }

    // Cleanup listeners
    return () => {
      if ((elements === null || elements === void 0 ? void 0 : elements.length) > 0) {
        elements.forEach((el, key) => {
          el.removeEventListener('keydown', handleKeyDown(key));
        });
      }
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [parentRef, ...extraDependencies]);
};

const _excluded$W = ["aria-label", "children", "className"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$S = 'ProgressTracker';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$P = getRootClassName(COMPONENT_NAME$S);

/**
 * Component default props.
 */
const DEFAULT_PROPS$D = {};

/**
 * ProgressTracker component.
 *
 * Implements WAI-ARIA `tablist` role {@see https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html#rps_label}
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ProgressTracker = /*#__PURE__*/forwardRef((props, ref) => {
  var _state$ids, _state$ids$tab;
  const {
      'aria-label': ariaLabel,
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$W);
  const stepListRef = React.useRef(null);
  useRovingTabIndex({
    parentRef: stepListRef,
    elementSelector: '[role="tab"]',
    keepTabIndex: false,
    extraDependencies: [children]
  });
  const state = useTabProviderContextState();
  const numberOfSteps = (state === null || state === void 0 ? void 0 : (_state$ids = state.ids) === null || _state$ids === void 0 ? void 0 : (_state$ids$tab = _state$ids.tab) === null || _state$ids$tab === void 0 ? void 0 : _state$ids$tab.length) || 0;
  const backgroundPosition = numberOfSteps > 0 ? 100 / (numberOfSteps * 2) : 0;
  const trackPosition = numberOfSteps > 0 ? 100 / (numberOfSteps - 1) * ((state === null || state === void 0 ? void 0 : state.activeTabIndex) || 0) / 100 : 0;
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: mergeRefs(ref, stepListRef)
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$P
    }))
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$P}__steps`,
    role: "tablist",
    "aria-label": ariaLabel
  }, children), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$P}__background-bar`,
    style: {
      left: `${backgroundPosition}%`,
      right: `${backgroundPosition}%`
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$P}__foreground-bar`,
    style: {
      left: `${backgroundPosition}%`,
      right: `${backgroundPosition}%`,
      transform: `scaleX(${trackPosition})`
    }
  }));
});
ProgressTracker.displayName = COMPONENT_NAME$S;
ProgressTracker.className = CLASSNAME$P;
ProgressTracker.defaultProps = DEFAULT_PROPS$D;

const _excluded$X = ["className", "disabled", "hasError", "helper", "id", "isActive", "isComplete", "isDisabled", "label", "onFocus", "onKeyPress", "tabIndex"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$T = 'ProgressTrackerStep';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$Q = getRootClassName(COMPONENT_NAME$T);

/**
 * Component default props.
 */
const DEFAULT_PROPS$E = {};

/**
 * ProgressTrackerStep component.
 *
 * Implements WAI-ARIA `tab` role {@see https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html#rps_label}
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ProgressTrackerStep = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      disabled,
      hasError,
      helper,
      id,
      isActive: propIsActive,
      isComplete,
      isDisabled = disabled,
      label,
      onFocus,
      onKeyPress,
      tabIndex = -1
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$X);
  const state = useTabProviderContext('tab', id);
  const isActive = propIsActive || (state === null || state === void 0 ? void 0 : state.isActive);
  const changeToCurrentTab = useCallback(() => {
    if (isDisabled) {
      return;
    }
    state === null || state === void 0 ? void 0 : state.changeToTab();
  }, [isDisabled, state]);
  const handleFocus = useCallback(event => {
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
    if (state !== null && state !== void 0 && state.shouldActivateOnFocus) {
      changeToCurrentTab();
    }
  }, [changeToCurrentTab, onFocus, state === null || state === void 0 ? void 0 : state.shouldActivateOnFocus]);
  const handleKeyPress = useCallback(event => {
    onKeyPress === null || onKeyPress === void 0 ? void 0 : onKeyPress(event);
    if (event.key !== 'Enter') {
      return;
    }
    changeToCurrentTab();
  }, [changeToCurrentTab, onKeyPress]);
  const getIcon = () => {
    if (isComplete) {
      return mdiCheckCircle;
    }
    if (isActive) {
      return hasError ? mdiAlertCircle : mdiRadioboxMarked;
    }
    return mdiRadioboxBlank;
  };
  return /*#__PURE__*/React.createElement("button", _extends({
    ref: ref
  }, forwardedProps, {
    type: "button",
    id: state === null || state === void 0 ? void 0 : state.tabId,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$Q,
      hasError,
      isActive,
      isClickable: state && !isDisabled,
      isComplete
    })),
    onClick: changeToCurrentTab,
    onKeyPress: handleKeyPress,
    onFocus: handleFocus,
    role: "tab",
    tabIndex: isActive ? 0 : tabIndex,
    "aria-disabled": isDisabled,
    "aria-selected": isActive,
    "aria-controls": state === null || state === void 0 ? void 0 : state.tabPanelId
  }), /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$Q}__state`,
    icon: getIcon(),
    size: Size.s
  }), /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: (state === null || state === void 0 ? void 0 : state.tabId) || '',
    className: `${CLASSNAME$Q}__label`
  }, label), helper && /*#__PURE__*/React.createElement(InputHelper, {
    kind: hasError ? Kind.error : Kind.info,
    className: `${CLASSNAME$Q}__helper`
  }, helper));
});
ProgressTrackerStep.displayName = COMPONENT_NAME$T;
ProgressTrackerStep.className = CLASSNAME$Q;
ProgressTrackerStep.defaultProps = DEFAULT_PROPS$E;

const _excluded$Y = ["children", "id", "className", "isActive"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$U = 'ProgressTrackerStepPanel';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$R = `${CSS_PREFIX}-step-panel`;

/**
 * Component default props.
 */
const DEFAULT_PROPS$F = {};

/**
 * ProgressTrackerStepPanel component.
 *
 * Implements WAI-ARIA `tabpanel` role {@see https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html#rps_label}
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const ProgressTrackerStepPanel = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      id,
      className,
      isActive: propIsActive
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$Y);
  const state = useTabProviderContext('tabPanel', id);
  const isActive = propIsActive || (state === null || state === void 0 ? void 0 : state.isActive);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    id: state === null || state === void 0 ? void 0 : state.tabPanelId,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$R,
      isActive
    })),
    role: "tabpanel",
    tabIndex: isActive ? 0 : -1,
    "aria-labelledby": state === null || state === void 0 ? void 0 : state.tabId
  }), (!(state !== null && state !== void 0 && state.isLazy) || isActive) && children);
});
ProgressTrackerStepPanel.displayName = COMPONENT_NAME$U;
ProgressTrackerStepPanel.className = CLASSNAME$R;
ProgressTrackerStepPanel.defaultProps = DEFAULT_PROPS$F;

const _excluded$Z = ["checked", "className", "disabled", "helper", "id", "inputRef", "isChecked", "isDisabled", "label", "name", "onChange", "theme", "value", "inputProps"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$V = 'RadioButton';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$S = getRootClassName(COMPONENT_NAME$V);

/**
 * Component default props.
 */
const DEFAULT_PROPS$G = {
  theme: Theme.light
};

/**
 * RadioButton component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const RadioButton = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      checked,
      className,
      disabled,
      helper,
      id,
      inputRef,
      isChecked = checked,
      isDisabled = disabled,
      label,
      name,
      onChange,
      theme,
      value,
      inputProps
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$Z);
  const generatedInputId = useId();
  const inputId = id || generatedInputId;
  const handleChange = event => {
    if (onChange) {
      onChange(value, name, event);
    }
  };
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      isChecked,
      isDisabled,
      isUnchecked: !isChecked,
      prefix: CLASSNAME$S,
      theme
    }))
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$S}__input-wrapper`
  }, /*#__PURE__*/React.createElement("input", _extends({
    ref: inputRef,
    className: `${CLASSNAME$S}__input-native`,
    disabled: isDisabled,
    id: inputId,
    tabIndex: isDisabled ? -1 : 0,
    type: "radio",
    name: name,
    value: value,
    checked: isChecked,
    onChange: handleChange,
    "aria-describedby": helper ? `${inputId}-helper` : undefined
  }, inputProps)), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$S}__input-placeholder`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$S}__input-background`
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$S}__input-indicator`
  }))), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$S}__content`
  }, label && /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: inputId,
    theme: theme,
    className: `${CLASSNAME$S}__label`
  }, label), helper && /*#__PURE__*/React.createElement(InputHelper, {
    id: `${inputId}-helper`,
    theme: theme,
    className: `${CLASSNAME$S}__helper`
  }, helper)));
});
RadioButton.displayName = COMPONENT_NAME$V;
RadioButton.className = CLASSNAME$S;
RadioButton.defaultProps = DEFAULT_PROPS$G;

const _excluded$_ = ["children", "className"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$W = 'RadioGroup';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$T = getRootClassName(COMPONENT_NAME$W);

/**
 * RadioGroup component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const RadioGroup = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$_);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$T
    }))
  }), children);
});
RadioGroup.displayName = COMPONENT_NAME$W;
RadioGroup.className = CLASSNAME$T;

/**
 * Listen on element focus to store the focus status.
 */
function useListenFocus(ref) {
  const [isFocus, setFocus] = useState(false);
  useEffect(() => {
    const {
      current: element
    } = ref;
    if (!element) {
      return undefined;
    }
    const onFocus = () => setFocus(true);
    const onBlur = () => setFocus(false);
    element.addEventListener('focus', onFocus);
    element.addEventListener('blur', onBlur);
    return () => {
      element.removeEventListener('focus', onFocus);
      element.removeEventListener('blur', onBlur);
    };
  }, [ref, setFocus]);
  return isFocus;
}

/**
 * Select variants.
 */
const SelectVariant = {
  input: 'input',
  chip: 'chip'
};

const _excluded$$ = ["children", "className", "focusElement", "isMultiple", "closeOnClick", "disabled", "error", "hasError", "helper", "id", "isDisabled", "isEmpty", "isOpen", "isRequired", "isValid", "label", "onClear", "onDropdownClose", "onInfiniteScroll", "onInputClick", "placeholder", "theme", "value", "variant"];

/** The display name of the component. */
const COMPONENT_NAME$X = 'Select';

/** The default class name and classes prefix for this component. */
const CLASSNAME$U = getRootClassName(COMPONENT_NAME$X);

/** The default value of props. */
const DEFAULT_PROPS$H = {
  theme: Theme.light,
  variant: SelectVariant.input
};
const WithSelectContext = (SelectElement, _ref, ref) => {
  let {
      children,
      className,
      focusElement,
      isMultiple,
      closeOnClick = !isMultiple,
      disabled,
      error,
      hasError,
      helper,
      id,
      isDisabled = disabled,
      isEmpty,
      isOpen,
      isRequired,
      isValid,
      label,
      onClear,
      onDropdownClose,
      onInfiniteScroll,
      onInputClick,
      placeholder,
      theme = DEFAULT_PROPS$H.theme,
      value,
      variant = DEFAULT_PROPS$H.variant
    } = _ref,
    forwardedProps = _objectWithoutProperties(_ref, _excluded$$);
  const generatedSelectId = useId();
  const selectId = id || generatedSelectId;
  const anchorRef = useRef(null);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const isFocus = useListenFocus(anchorRef);
  const handleKeyboardNav = useCallback(evt => {
    if ((evt.key === 'Enter' || evt.key === ' ' || evt.key === 'ArrowDown') && onInputClick) {
      evt.preventDefault();
      onInputClick();
    }
  }, [onInputClick]);
  const onClose = () => {
    var _anchorRef$current;
    if (onDropdownClose) {
      onDropdownClose();
    }
    anchorRef === null || anchorRef === void 0 ? void 0 : (_anchorRef$current = anchorRef.current) === null || _anchorRef$current === void 0 ? void 0 : _anchorRef$current.blur();
  };

  // Handle focus trap.
  useFocusTrap(isOpen && dropdownRef.current, focusElement === null || focusElement === void 0 ? void 0 : focusElement.current);
  return /*#__PURE__*/React.createElement("div", {
    ref: mergeRefs(ref, selectRef),
    className: classnames(className, handleBasicClasses({
      hasError,
      hasLabel: Boolean(label),
      hasPlaceholder: Boolean(placeholder),
      hasValue: !isEmpty,
      isDisabled,
      isEmpty,
      isFocus,
      isOpen,
      isValid,
      prefix: CLASSNAME$U,
      theme: theme === Theme.light ? Theme.light : Theme.dark
    }))
  }, /*#__PURE__*/React.createElement(SelectElement, _extends({}, forwardedProps, {
    anchorRef: anchorRef,
    "aria-disabled": isDisabled,
    handleKeyboardNav: handleKeyboardNav,
    hasError: hasError,
    isDisabled: isDisabled,
    isEmpty: isEmpty,
    isRequired: isRequired,
    isValid: isValid,
    label: label,
    placeholder: placeholder,
    id: selectId,
    theme: theme,
    value: value,
    variant: variant,
    onClear: onClear,
    onInputClick: onInputClick
  })), /*#__PURE__*/React.createElement(Dropdown, {
    anchorRef: anchorRef,
    closeOnClick: closeOnClick,
    closeOnClickAway: true,
    closeOnEscape: true,
    isOpen: !!isOpen,
    placement: Placement.BOTTOM_START,
    onClose: onClose,
    onInfiniteScroll: onInfiniteScroll,
    ref: dropdownRef
  }, children), hasError && error && /*#__PURE__*/React.createElement(InputHelper, {
    className: `${CLASSNAME$U}__helper`,
    kind: Kind.error,
    theme: theme
  }, error), helper && /*#__PURE__*/React.createElement(InputHelper, {
    className: `${CLASSNAME$U}__helper`,
    theme: theme
  }, helper));
};

const _excluded$10 = ["anchorRef", "clearButtonProps", "handleKeyboardNav", "hasError", "hasInputClear", "icon", "id", "isDisabled", "isEmpty", "isRequired", "isValid", "label", "onClear", "onInputClick", "placeholder", "selectedValueRender", "theme", "value", "variant", "selectElementRef"];

/** The display name of the component. */
const COMPONENT_NAME$Y = 'Select';

/** The default class name and classes prefix for this component. */
const CLASSNAME$V = getRootClassName(COMPONENT_NAME$Y);

/** The default value of props. */
const DEFAULT_PROPS$I = {
  selectedValueRender: choice => choice
};
const stopPropagation = evt => evt.stopPropagation();

/**
 * Select component.
 */
const SelectField = _ref => {
  let {
      anchorRef,
      clearButtonProps,
      handleKeyboardNav,
      hasError,
      hasInputClear,
      icon,
      id,
      isDisabled,
      isEmpty,
      isRequired,
      isValid,
      label,
      onClear,
      onInputClick,
      placeholder,
      selectedValueRender,
      theme,
      value,
      variant,
      selectElementRef
    } = _ref,
    forwardedProps = _objectWithoutProperties(_ref, _excluded$10);
  return /*#__PURE__*/React.createElement(React.Fragment, null, variant === SelectVariant.input && /*#__PURE__*/React.createElement(React.Fragment, null, label && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$V}__header`
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: id,
    className: `${CLASSNAME$V}__label`,
    isRequired: isRequired,
    theme: theme
  }, label)), /*#__PURE__*/React.createElement("div", _extends({
    ref: mergeRefs(anchorRef, selectElementRef),
    id: id,
    className: `${CLASSNAME$V}__wrapper`,
    onClick: onInputClick,
    onKeyDown: handleKeyboardNav,
    tabIndex: isDisabled ? undefined : 0,
    "aria-disabled": isDisabled || undefined
  }, forwardedProps), icon && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$V}__input-icon`,
    color: theme === Theme.dark ? 'light' : undefined,
    icon: icon,
    size: Size.xs
  }), /*#__PURE__*/React.createElement("div", {
    className: classnames([`${CLASSNAME$V}__input-native`, isEmpty && placeholder && `${CLASSNAME$V}__input-native--placeholder`])
  }, !isEmpty && /*#__PURE__*/React.createElement("span", null, selectedValueRender === null || selectedValueRender === void 0 ? void 0 : selectedValueRender(value)), isEmpty && placeholder && /*#__PURE__*/React.createElement("span", null, placeholder)), (isValid || hasError) && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$V}__input-validity`
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: isValid ? mdiCheckCircle : mdiAlertCircle,
    size: Size.xxs
  })), hasInputClear && clearButtonProps && /*#__PURE__*/React.createElement(IconButton, _extends({}, clearButtonProps, {
    className: `${CLASSNAME$V}__input-clear`,
    icon: mdiCloseCircle,
    emphasis: Emphasis.low,
    size: Size.s,
    theme: theme,
    onClick: onClear,
    onKeyDown: stopPropagation
  })), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$V}__input-indicator`
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: mdiMenuDown,
    size: Size.s
  })))), variant === SelectVariant.chip && /*#__PURE__*/React.createElement(Chip, _extends({
    id: id,
    isSelected: !isEmpty,
    isDisabled: isDisabled,
    after: /*#__PURE__*/React.createElement(Icon, {
      icon: isEmpty ? mdiMenuDown : mdiCloseCircle
    }),
    onAfterClick: isEmpty ? onInputClick : onClear,
    onClick: onInputClick,
    ref: mergeRefs(anchorRef, selectElementRef),
    theme: theme
  }, forwardedProps), isEmpty && /*#__PURE__*/React.createElement("span", null, label), !isEmpty && /*#__PURE__*/React.createElement("span", null, selectedValueRender === null || selectedValueRender === void 0 ? void 0 : selectedValueRender(value))));
};

/**
 * Select component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Select = /*#__PURE__*/forwardRef((props, ref) => {
  const isEmpty$1 = isEmpty(props.value);
  const hasInputClear = props.onClear && props.clearButtonProps && !isEmpty$1;
  return WithSelectContext(SelectField, _objectSpread2(_objectSpread2({}, props), {}, {
    className: classnames(props.className, handleBasicClasses({
      hasInputClear,
      hasUnique: !props.isEmpty,
      prefix: CLASSNAME$V
    })),
    hasInputClear,
    isEmpty: isEmpty$1
  }), ref);
});
Select.displayName = COMPONENT_NAME$Y;
Select.className = CLASSNAME$V;
Select.defaultProps = DEFAULT_PROPS$I;
Select.className = CLASSNAME$V;

const _excluded$11 = ["anchorRef", "handleKeyboardNav", "hasError", "icon", "id", "isDisabled", "isEmpty", "isRequired", "isValid", "label", "onClear", "onInputClick", "placeholder", "selectedChipRender", "selectedValueRender", "theme", "value", "variant", "selectElementRef"];

/** Defines the props of the component. */

/** The display name of the component. */
const COMPONENT_NAME$Z = 'Select';

/** The default class name and classes prefix for this component. */
const CLASSNAME$W = getRootClassName(COMPONENT_NAME$Z);

/** The default value of props. */
const DEFAULT_PROPS$J = {
  selectedChipRender(choice, index, onClear, isDisabled, theme) {
    const onClick = event => onClear && onClear(event, choice);
    return /*#__PURE__*/React.createElement(Chip, {
      key: index,
      after: onClear && /*#__PURE__*/React.createElement(Icon, {
        icon: mdiClose,
        size: Size.xxs
      }),
      isDisabled: isDisabled,
      size: Size.s,
      onAfterClick: onClick,
      onClick: onClick,
      theme: theme
    }, choice);
  },
  selectedValueRender: choice => choice
};
const SelectMultipleField = _ref => {
  let {
      anchorRef,
      handleKeyboardNav,
      hasError,
      icon,
      id,
      isDisabled,
      isEmpty,
      isRequired,
      isValid,
      label,
      onClear,
      onInputClick,
      placeholder,
      selectedChipRender,
      selectedValueRender,
      theme,
      value,
      variant,
      selectElementRef
    } = _ref,
    forwardedProps = _objectWithoutProperties(_ref, _excluded$11);
  return /*#__PURE__*/React.createElement(React.Fragment, null, variant === SelectVariant.input && /*#__PURE__*/React.createElement(React.Fragment, null, label && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$W}__header`
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: id,
    className: `${CLASSNAME$W}__label`,
    isRequired: isRequired,
    theme: theme
  }, label)), /*#__PURE__*/React.createElement("div", _extends({
    ref: mergeRefs(anchorRef, selectElementRef),
    id: id,
    className: `${CLASSNAME$W}__wrapper`,
    onClick: onInputClick,
    onKeyDown: handleKeyboardNav,
    tabIndex: isDisabled ? undefined : 0,
    "aria-disabled": isDisabled || undefined
  }, forwardedProps), icon && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$W}__input-icon`,
    color: theme === Theme.dark ? 'light' : undefined,
    icon: icon,
    size: Size.xs
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$W}__chips`
  }, !isEmpty && value.map((val, index) => selectedChipRender === null || selectedChipRender === void 0 ? void 0 : selectedChipRender(val, index, onClear, isDisabled, theme))), isEmpty && placeholder && /*#__PURE__*/React.createElement("div", {
    className: classnames([`${CLASSNAME$W}__input-native`, `${CLASSNAME$W}__input-native--placeholder`])
  }, /*#__PURE__*/React.createElement("span", null, placeholder)), (isValid || hasError) && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$W}__input-validity`
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: isValid ? mdiCheckCircle : mdiAlertCircle,
    size: Size.xxs
  })), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$W}__input-indicator`
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: mdiMenuDown,
    size: Size.s
  })))), variant === SelectVariant.chip && /*#__PURE__*/React.createElement(Chip, _extends({
    id: id,
    isSelected: !isEmpty,
    isDisabled: isDisabled,
    after: /*#__PURE__*/React.createElement(Icon, {
      icon: isEmpty ? mdiMenuDown : mdiCloseCircle
    }),
    onAfterClick: isEmpty ? onInputClick : onClear,
    onClick: onInputClick,
    ref: mergeRefs(anchorRef, selectElementRef),
    theme: theme
  }, forwardedProps), isEmpty && /*#__PURE__*/React.createElement("span", null, label), !isEmpty && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", null, selectedValueRender === null || selectedValueRender === void 0 ? void 0 : selectedValueRender(value[0])), value.length > 1 && /*#__PURE__*/React.createElement("span", null, "\xA0+", value.length - 1))));
};

/**
 * SelectMultiple component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const SelectMultiple = /*#__PURE__*/forwardRef((props, ref) => {
  return WithSelectContext(SelectMultipleField, _objectSpread2(_objectSpread2({}, props), {}, {
    className: classnames(props.className, handleBasicClasses({
      hasMultiple: !props.isEmpty,
      prefix: CLASSNAME$W
    })),
    isEmpty: props.value.length === 0,
    isMultiple: true
  }), ref);
});
SelectMultiple.displayName = COMPONENT_NAME$Z;
SelectMultiple.className = CLASSNAME$W;
SelectMultiple.defaultProps = DEFAULT_PROPS$J;

const _excluded$12 = ["children", "className", "theme"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$_ = 'SideNavigation';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$X = getRootClassName(COMPONENT_NAME$_);

/**
 * SideNavigation component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const SideNavigation = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$12);
  const content = Children.toArray(children).filter(isComponent(SideNavigationItem));
  return /*#__PURE__*/React.createElement("ul", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, theme === Theme.dark && 'lumx-color-font-light-N', handleBasicClasses({
      prefix: CLASSNAME$X
    }))
  }), content);
});
SideNavigation.displayName = COMPONENT_NAME$_;
SideNavigation.className = CLASSNAME$X;

const _excluded$13 = ["linkAs", "href"];
/**
 * Render <button> HTML component, fallbacks to `<a>` when a `href` is provided or a custom component with `linkAs`.
 */
const renderButtonOrLink = function (props) {
  const {
      linkAs,
      href
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$13);
  for (var _len = arguments.length, children = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    children[_key - 1] = arguments[_key];
  }
  if (linkAs || href) return renderLink(props, ...children);
  return /*#__PURE__*/React.createElement('button', _objectSpread2({
    type: 'button'
  }, forwardedProps), ...children);
};

const _excluded$14 = ["children", "className", "emphasis", "icon", "isOpen", "isSelected", "label", "linkAs", "linkProps", "onActionClick", "onClick", "toggleButtonProps", "closeMode"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$$ = 'SideNavigationItem';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$Y = getRootClassName(COMPONENT_NAME$$);

/**
 * Component default props.
 */
const DEFAULT_PROPS$K = {
  emphasis: Emphasis.high,
  closeMode: 'unmount'
};

/**
 * SideNavigationItem component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const SideNavigationItem = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      emphasis,
      icon,
      isOpen,
      isSelected,
      label,
      linkAs,
      linkProps,
      onActionClick,
      onClick,
      toggleButtonProps,
      closeMode = 'unmount'
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$14);
  const content = children && Children.toArray(children).filter(isComponent(SideNavigationItem));
  const hasContent = !isEmpty(content);
  const shouldSplitActions = Boolean(onActionClick);
  const showChildren = hasContent && isOpen;
  const contentId = useId();
  const ariaProps = {};
  if (hasContent) {
    ariaProps['aria-expanded'] = !!showChildren;
    // Associate with content ID only if in DOM (shown or hidden and not unmounted)
    ariaProps['aria-controls'] = showChildren || closeMode === 'hide' ? contentId : undefined;
  }
  return /*#__PURE__*/React.createElement("li", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      emphasis,
      isOpen: showChildren,
      isSelected,
      prefix: CLASSNAME$Y
    }))
  }), shouldSplitActions ? /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$Y}__wrapper`
  }, renderLink(_objectSpread2(_objectSpread2({
    linkAs
  }, linkProps), {}, {
    className: `${CLASSNAME$Y}__link`,
    onClick,
    tabIndex: 0
  }), icon && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$Y}__icon`,
    icon: icon,
    size: Size.xs
  }), /*#__PURE__*/React.createElement("span", null, label)), /*#__PURE__*/React.createElement(IconButton, _extends({}, toggleButtonProps, {
    className: `${CLASSNAME$Y}__toggle`,
    icon: isOpen ? mdiChevronUp : mdiChevronDown,
    size: Size.m,
    emphasis: Emphasis.low,
    onClick: onActionClick
  }, ariaProps))) : renderButtonOrLink(_objectSpread2(_objectSpread2({
    linkAs
  }, linkProps), {}, {
    className: `${CLASSNAME$Y}__link`,
    tabIndex: 0,
    onClick
  }, ariaProps), icon && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$Y}__icon`,
    icon: icon,
    size: Size.xs
  }), /*#__PURE__*/React.createElement("span", null, label), hasContent && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$Y}__chevron`,
    icon: isOpen ? mdiChevronUp : mdiChevronDown,
    size: Size.xs
  })), (closeMode === 'hide' || showChildren) && /*#__PURE__*/React.createElement("ul", {
    className: `${CLASSNAME$Y}__children`,
    id: contentId
  }, content));
});
SideNavigationItem.displayName = COMPONENT_NAME$$;
SideNavigationItem.className = CLASSNAME$Y;
SideNavigationItem.defaultProps = DEFAULT_PROPS$K;

const _excluded$15 = ["className", "size", "color", "theme"];

/**
 * Defines the props of the component.
 */

const DEFAULT_PROPS$L = {
  theme: Theme.light
};

/**
 * Component display name.
 */
const COMPONENT_NAME$10 = 'SkeletonCircle';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$Z = getRootClassName(COMPONENT_NAME$10);

/**
 * SkeletonCircle component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const SkeletonCircle = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      size,
      color,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$15);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$Z,
      size,
      color,
      theme
    }))
  }));
});
SkeletonCircle.displayName = COMPONENT_NAME$10;
SkeletonCircle.defaultProps = DEFAULT_PROPS$L;
SkeletonCircle.className = CLASSNAME$Z;

const _excluded$16 = ["aspectRatio", "className", "height", "theme", "variant", "width", "color"];

/**
 * Skeleton variants.
 */
const SkeletonRectangleVariant = {
  squared: 'squared',
  rounded: 'rounded',
  pill: 'pill'
};

/**
 * Defines the props of the component.
 */

const DEFAULT_PROPS$M = {
  theme: Theme.light,
  variant: SkeletonRectangleVariant.squared
};

/**
 * Component display name.
 */
const COMPONENT_NAME$11 = 'SkeletonRectangle';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$_ = getRootClassName(COMPONENT_NAME$11);

/**
 * SkeletonRectangle component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const SkeletonRectangle = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      aspectRatio,
      className,
      height,
      theme,
      variant,
      width,
      color
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$16);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$_,
      aspectRatio,
      height: aspectRatio ? undefined : height,
      theme,
      variant,
      width,
      color
    }))
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$_}__inner`
  }));
});
SkeletonRectangle.displayName = COMPONENT_NAME$11;
SkeletonRectangle.className = CLASSNAME$_;
SkeletonRectangle.defaultProps = DEFAULT_PROPS$M;

const _excluded$17 = ["className", "theme", "typography", "width", "color"];

/**
 * Defines the props of the component.
 */

const DEFAULT_PROPS$N = {
  theme: Theme.light
};

/**
 * Component display name.
 */
const COMPONENT_NAME$12 = 'SkeletonTypography';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$$ = getRootClassName(COMPONENT_NAME$12);

/**
 * SkeletonTypography component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const SkeletonTypography = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      theme,
      typography,
      width,
      color
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$17);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$$,
      theme,
      typography,
      color
    })),
    style: _objectSpread2(_objectSpread2({}, forwardedProps.style), {}, {
      width
    })
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$$}__inner`
  }));
});
SkeletonTypography.displayName = COMPONENT_NAME$12;
SkeletonTypography.defaultProps = DEFAULT_PROPS$N;
SkeletonTypography.className = CLASSNAME$$;

const useEnhancedEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

/**
 * https://github.com/facebook/react/issues/14099#issuecomment-440013892
 *
 * @param fn A function to run
 * @return A React callback
 */
function useEventCallback(fn) {
  const ref = React.useRef(fn);
  useEnhancedEffect(() => {
    ref.current = fn;
  });
  return React.useCallback(event => ref.current(event), []);
}

/**
 * Clamp value in range.
 *
 * @param value Value to clamp.
 * @param min   Minimum value.
 * @param max   Maximum value.
 * @return Clamped value.
 */
const clamp = (value, min, max) => {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
};

const _excluded$18 = ["className", "disabled", "helper", "hideMinMaxLabel", "id", "isDisabled", "label", "max", "min", "name", "onChange", "onMouseDown", "precision", "steps", "theme", "value"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$13 = 'Slider';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$10 = getRootClassName(COMPONENT_NAME$13);

/**
 * Component default props.
 */
const DEFAULT_PROPS$O = {
  precision: 0,
  steps: 0,
  theme: Theme.light
};

/**
 * Convert a percent value to a value in range min - max.
 *
 * @param percent   Value to convert.
 * @param min       Minimum value.
 * @param max       Maximum value.
 * @param precision Precision.
 * @return Value in range min - max
 */
const computeValueFromPercent = function (percent, min, max) {
  let precision = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  return Number((min + percent * (max - min)).toFixed(precision));
};

/**
 * Convert a value in range min - max to a percent value.
 *
 * @param value Value to convert.
 * @param min   Minimum value.
 * @param max   Maximum value.
 * @return Value in percent
 */
const computePercentFromValue = (value, min, max) => Number((value - min) / (max - min));

/**
 * Slider component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Slider = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      disabled,
      helper,
      hideMinMaxLabel,
      id,
      isDisabled = disabled,
      label,
      max,
      min,
      name,
      onChange,
      onMouseDown,
      precision,
      steps,
      theme,
      value
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$18);
  const generatedId = useId();
  const sliderId = id || generatedId;
  const sliderLabelId = useMemo(() => `label-${sliderId}`, [sliderId]);
  const sliderRef = useRef(null);

  // build a lookup array for the steps.
  const availableSteps = useMemo(() => {
    if (!steps) return [];
    const available = [0];
    const percentStep = 1 / ((max - min) / steps);
    let ptr = 0;
    while (ptr + percentStep < 1) {
      ptr += percentStep;
      available.push(ptr);
    }
    return available;
  }, [steps, min, max]);

  /**
   * Try to find the closest step to the current slider position.
   *
   * @param percentValue Reference value
   * @return The closest step value
   */
  const findClosestStep = percentValue => {
    const closest = availableSteps.reduce((acc, step) => {
      const aDst = Math.abs(percentValue - step);
      if (aDst < acc.dst) {
        return {
          dst: aDst,
          val: step
        };
      }
      return acc;
    }, {
      dst: Infinity,
      val: -1
    });
    return closest.val;
  };

  /**
   * Convert slider's handle position to percent.
   *
   * @param event The interaction event
   * @param slider the slider element
   * @return The computed percent value
   */
  const getPercentValue = (event, slider) => {
    const {
      width,
      left
    } = slider.getBoundingClientRect();
    let percent = (event.pageX - left - window.pageXOffset) / width;
    percent = clamp(percent, 0, 1);
    if (steps) {
      percent = findClosestStep(percent);
    }
    return percent;
  };

  /**
   * Register a handler for the mouse move event.
   */
  const handleMove = useEventCallback(event => {
    const {
      current: slider
    } = sliderRef;
    if (!slider || !onChange) return;
    const newValue = getPercentValue(event, slider);
    onChange(computeValueFromPercent(newValue, min, max, precision), name, event);
  });

  /**
   * Register a handler for the mouse up event.
   * Clean a all listeners.
   */
  const handleEnd = useEventCallback(() => {
    document.body.removeEventListener('mousemove', handleMove);
    document.body.removeEventListener('mouseup', handleEnd);
    document.body.removeEventListener('touchmove', handleMove);
    document.body.removeEventListener('touchend', handleEnd);
  });

  /**
   * Move to the next or previous value (i.e. + or - 10%) or next step
   * @param previous Should seek the previous value.
   */
  const hopToValue = function () {
    let previous = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    const oldPercent = computePercentFromValue(value, min, max);
    let percent = clamp(oldPercent + (previous ? -0.1 : 0.1), 0, 1);
    if (steps) {
      percent = oldPercent + availableSteps[1] * (previous ? -1 : 1);
      percent = findClosestStep(percent);
    }
    if (onChange) {
      onChange(computeValueFromPercent(percent, min, max, precision), name);
    }
  };

  /**
   * Register a handler for keyboard interactions
   */
  const handleKeyDown = useEventCallback(event => {
    if (event.key === 'ArrowRight') {
      hopToValue();
    } else if (event.key === 'ArrowLeft') {
      hopToValue(true);
    }
  });

  /**
   * Register a handler for the mouseDown event.
   */
  const handleMouseDown = useEventCallback(event => {
    onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown(event);
    const {
      current: slider
    } = sliderRef;
    if (isDisabled || !slider) return;
    const newValue = getPercentValue(event, slider);
    if (onChange) {
      onChange(computeValueFromPercent(newValue, min, max, precision), name, event);
    }
    document.body.addEventListener('mousemove', handleMove);
    document.body.addEventListener('mouseup', handleEnd);
  });
  const percentString = `${computePercentFromValue(value, min, max) * 100}%`;
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$10,
      theme,
      hasLabel: Boolean(label)
    })),
    onMouseDown: handleMouseDown,
    "aria-disabled": isDisabled
  }), label && /*#__PURE__*/React.createElement(InputLabel, {
    id: sliderLabelId,
    htmlFor: sliderId,
    className: `${CLASSNAME$10}__label`,
    theme: theme
  }, label), helper && /*#__PURE__*/React.createElement(InputHelper, {
    className: `${CLASSNAME$10}__helper`,
    theme: theme
  }, helper), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$10}__ui-wrapper`
  }, !hideMinMaxLabel && /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$10}__value-label ${CLASSNAME$10}__value-label--min`
  }, min), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$10}__wrapper`,
    ref: sliderRef
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$10}__track ${CLASSNAME$10}__track--background`
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$10}__track ${CLASSNAME$10}__track--active`,
    style: {
      width: percentString
    }
  }), steps ? /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$10}__ticks`
  }, availableSteps.map((step, idx) => /*#__PURE__*/React.createElement("div", {
    key: `tick_${idx}`,
    className: `${CLASSNAME$10}__tick`,
    style: {
      left: `${step * 100}%`
    }
  }))) : null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-labelledby": sliderLabelId,
    name: name,
    id: sliderId,
    className: `${CLASSNAME$10}__handle`,
    style: {
      left: percentString
    },
    onKeyDown: handleKeyDown,
    disabled: isDisabled
  })), !hideMinMaxLabel && /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$10}__value-label ${CLASSNAME$10}__value-label--max`
  }, max)));
});
Slider.displayName = COMPONENT_NAME$13;
Slider.className = CLASSNAME$10;
Slider.defaultProps = DEFAULT_PROPS$O;

/**
 * Making setInterval Declarative with React Hooks.
 * Credits: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 *
 * @param callback Function called by setInterval.
 * @param     delay    Delay for setInterval.
 */
function useInterval(callback, delay) {
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  });
  useEffect(() => {
    if (delay === null) return undefined;
    function tick() {
      if (isFunction(savedCallback.current)) {
        savedCallback.current();
      }
    }
    const id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
}

/**
 * Autoplay default interval in ms.
 */
const AUTOPLAY_DEFAULT_INTERVAL = 5000;

/**
 * Full width size in percent.
 */
const FULL_WIDTH_PERCENT = 100;

/**
 * Edge from the active index.
 */
const EDGE_FROM_ACTIVE_INDEX = 2;

/**
 * Max number of pagination items.
 */
const PAGINATION_ITEMS_MAX = 5;

/**
 * Size of a pagination item. Used to translate wrapper.
 */
const PAGINATION_ITEM_SIZE = 12;

const DEFAULT_OPTIONS$1 = {
  activeIndex: 0,
  groupBy: 1,
  interval: AUTOPLAY_DEFAULT_INTERVAL
};
const useSlideshowControls = _ref => {
  let {
    activeIndex = DEFAULT_OPTIONS$1.activeIndex,
    groupBy = DEFAULT_OPTIONS$1.groupBy,
    interval = DEFAULT_OPTIONS$1.interval,
    autoPlay,
    defaultActiveIndex,
    onChange,
    itemsCount,
    id,
    slidesId
  } = _ref;
  const [currentIndex, setCurrentIndex] = useState(activeIndex);
  // Use state instead of a ref to make the slideshow controls update directly when the element is set.
  const [element, setElement] = useState(null);

  // Number of slides when using groupBy prop.
  const slidesCount = Math.ceil(itemsCount / Math.min(groupBy, itemsCount));

  // Change current index to display next slide.
  const goToNextSlide = useCallback(function () {
    let loopback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    setCurrentIndex(index => {
      if (loopback && index === slidesCount - 1) {
        // Loopback to the start.
        return 0;
      }
      if (index < slidesCount - 1) {
        // Next slide.
        return index + 1;
      }
      return index;
    });
  }, [slidesCount, setCurrentIndex]);

  // Change current index to display previous slide.
  const goToPreviousSlide = useCallback(function () {
    let loopback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    setCurrentIndex(index => {
      if (loopback && index === 0) {
        // Loopback to the end.
        return slidesCount - 1;
      }
      if (index > 0) {
        // Previous slide.
        return index - 1;
      }
      return index;
    });
  }, [slidesCount, setCurrentIndex]);

  // Auto play
  const [isAutoPlaying, setIsAutoPlaying] = useState(Boolean(autoPlay));
  const [isForcePaused, setIsForcePaused] = useState(false);
  const isSlideshowAutoPlaying = isForcePaused ? false : isAutoPlaying;
  // Start
  useInterval(goToNextSlide, isSlideshowAutoPlaying && slidesCount > 1 ? interval : null);

  // Reset current index if it become invalid.
  useEffect(() => {
    if (currentIndex > slidesCount - 1) {
      setCurrentIndex(defaultActiveIndex);
    }
  }, [currentIndex, slidesCount, defaultActiveIndex]);
  const startAutoPlay = () => {
    setIsAutoPlaying(Boolean(autoPlay));
  };
  const stopAutoPlay = () => {
    setIsAutoPlaying(false);
  };

  // Handle click on a bullet to go to a specific slide.
  const onPaginationClick = useCallback(index => {
    stopAutoPlay();
    setIsForcePaused(true);
    if (index >= 0 && index < slidesCount) {
      setCurrentIndex(index);
    }
  }, [slidesCount, setCurrentIndex]);

  // Handle click or keyboard event to go to next slide.
  const onNextClick = useCallback(function () {
    let loopback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    stopAutoPlay();
    setIsForcePaused(true);
    goToNextSlide(loopback);
  }, [goToNextSlide]);

  // Handle click or keyboard event to go to previous slide.
  const onPreviousClick = useCallback(function () {
    let loopback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    stopAutoPlay();
    setIsForcePaused(true);
    goToPreviousSlide(loopback);
  }, [goToPreviousSlide]);

  // If the activeIndex props changes, update the current slide
  useEffect(() => {
    setCurrentIndex(activeIndex);
  }, [activeIndex]);

  // If the slide changes, with autoplay for example, trigger "onChange"
  useEffect(() => {
    if (!onChange) return;
    onChange(currentIndex);
  }, [currentIndex, onChange]);
  const generatedSlideshowId = useId();
  const slideshowId = id || generatedSlideshowId;
  const generatedSlidesId = useId();
  const slideshowSlidesId = slidesId || generatedSlidesId;
  const toggleAutoPlay = () => {
    if (isSlideshowAutoPlaying) {
      stopAutoPlay();
    } else {
      startAutoPlay();
    }
  };
  const toggleForcePause = () => {
    const shouldBePaused = !isForcePaused;
    setIsForcePaused(shouldBePaused);
    if (!shouldBePaused) {
      startAutoPlay();
    } else {
      stopAutoPlay();
    }
  };

  // Start index and end index of visible slides.
  const startIndexVisible = currentIndex * groupBy;
  const endIndexVisible = startIndexVisible + groupBy;
  return {
    startIndexVisible,
    endIndexVisible,
    setSlideshow: setElement,
    slideshow: element,
    slideshowId,
    slideshowSlidesId,
    onPreviousClick,
    onNextClick,
    onPaginationClick,
    isAutoPlaying: isSlideshowAutoPlaying,
    toggleAutoPlay,
    activeIndex: currentIndex,
    slidesCount,
    setActiveIndex: setCurrentIndex,
    startAutoPlay,
    stopAutoPlay,
    isForcePaused,
    toggleForcePause
  };
};

/**
 * Hook that allows to control when there is a focus event within a given element, meaning
 * that any element within the given target will trigger the focus in and focus out events.
 * @param options - UseFocusWithinOptions
 */
const useFocusWithin = _ref => {
  let {
    element,
    onFocusIn,
    onFocusOut
  } = _ref;
  useEffect(() => {
    if (element) {
      element.addEventListener('focusin', onFocusIn);
      element.addEventListener('focusout', onFocusOut);
    }
    return () => {
      if (element) {
        element.removeEventListener('focusin', onFocusIn);
        element.removeEventListener('focusout', onFocusOut);
      }
    };
  }, [onFocusIn, element, onFocusOut]);
};

/**
 * Classname set on elements whose focus was blocked.
 * This is to easily find elements that have been tempered with,
 * and not elements whose focus was already initially blocked.
 * */
const BLOCKED_FOCUS_CLASSNAME = 'focus-blocked';

/**
 * Manage how slides must behave when visible or not.
 * When not visible, they should be hidden from screen readers and not focusable.
 */
const useSlideFocusManagement = _ref => {
  let {
    isSlideDisplayed,
    slideRef
  } = _ref;
  useEffect(() => {
    const element = slideRef === null || slideRef === void 0 ? void 0 : slideRef.current;
    if (!element) {
      return undefined;
    }

    /**
     * Display given slide to screen readers and, if focus was blocked, restore focus on elements.
     */
    const enableSlide = () => {
      // Hide from screen readers
      element.setAttribute('aria-hidden', 'false');
      // Find elements we have blocked focus on
      element.querySelectorAll(`.${BLOCKED_FOCUS_CLASSNAME}`).forEach(focusableElement => {
        focusableElement.removeAttribute('tabindex');
        focusableElement.classList.remove(BLOCKED_FOCUS_CLASSNAME);
      });
    };

    /**
     * Hide given slide from screen readers and block focus on all focusable elements within.
     */
    const blockSlide = () => {
      element.setAttribute('aria-hidden', 'true');
      getFocusableElements(element).forEach(focusableElement => {
        focusableElement.setAttribute('tabindex', '-1');
        focusableElement.classList.add(BLOCKED_FOCUS_CLASSNAME);
      });
    };
    const handleDisplay = () => {
      if (!element) {
        return;
      }
      if (isSlideDisplayed) {
        enableSlide();
      } else {
        blockSlide();
      }
    };

    // Callback function to execute when mutations are observed
    const callback = mutationsList => {
      if (element) {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            handleDisplay();
          }
        }
      }
    };

    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(callback);
    if (element) {
      handleDisplay();

      /** If slide is hidden, start observing for elements to block focus  */
      if (!isSlideDisplayed) {
        observer.observe(element, {
          attributes: true,
          childList: true,
          subtree: true
        });
      }
    }
    return () => {
      if (!isSlideDisplayed) {
        observer.disconnect();
      }
    };
  }, [isSlideDisplayed, slideRef]);
};

const _excluded$19 = ["className", "children", "role", "label", "isDisplayed"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$14 = 'SlideshowItemGroup';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$11 = getRootClassName(COMPONENT_NAME$14);
const buildSlideShowGroupId = (slidesId, index) => `${slidesId}-slide-${index}`;

/**
 * SlideshowItemGroup component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const SlideshowItemGroup = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      children,
      role = 'group',
      label,
      isDisplayed
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$19);
  const groupRef = React.useRef(null);
  useSlideFocusManagement({
    isSlideDisplayed: isDisplayed,
    slideRef: groupRef
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: mergeRefs(groupRef, ref),
    role: role,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$11
    })),
    "aria-roledescription": "slide",
    "aria-label": label
  }, forwardedProps), children);
});
SlideshowItemGroup.displayName = COMPONENT_NAME$14;
SlideshowItemGroup.className = CLASSNAME$11;

const _excluded$1a = ["activeIndex", "autoPlay", "children", "className", "fillHeight", "groupBy", "interval", "onChange", "slideshowControlsProps", "theme", "id", "slidesId", "slideGroupLabel"];

/**
 * Defines the props of the component.
 */

/**
 * Component default props.
 */
const DEFAULT_PROPS$P = _objectSpread2(_objectSpread2({}, DEFAULT_OPTIONS$1), {}, {
  theme: Theme.light
});

/**
 * Slideshow component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Slideshow = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      activeIndex,
      autoPlay,
      children,
      className,
      fillHeight,
      groupBy,
      interval,
      onChange,
      slideshowControlsProps,
      theme,
      id,
      slidesId,
      slideGroupLabel
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1a);
  // Number of slideshow items.
  const itemsCount = React.Children.count(children);
  const {
    activeIndex: currentIndex,
    slideshowId,
    setSlideshow,
    isAutoPlaying,
    slideshowSlidesId,
    slidesCount,
    onNextClick,
    onPaginationClick,
    onPreviousClick,
    slideshow,
    stopAutoPlay,
    startAutoPlay,
    toggleAutoPlay,
    toggleForcePause
  } = SlideshowControls.useSlideshowControls({
    activeIndex,
    defaultActiveIndex: DEFAULT_PROPS$P.activeIndex,
    autoPlay: Boolean(autoPlay),
    itemsCount,
    groupBy,
    id,
    interval,
    onChange,
    slidesId
  });
  useFocusWithin({
    element: slideshow,
    onFocusIn: stopAutoPlay,
    onFocusOut: startAutoPlay
  });
  const showControls = slideshowControlsProps && slidesCount > 1;
  return /*#__PURE__*/React.createElement(Slides, _extends({
    activeIndex: currentIndex,
    id: slideshowId,
    className: className,
    theme: theme,
    fillHeight: fillHeight,
    groupBy: groupBy,
    isAutoPlaying: isAutoPlaying,
    autoPlay: autoPlay,
    slidesId: slideshowSlidesId,
    toggleAutoPlay: toggleAutoPlay,
    ref: mergeRefs(ref, setSlideshow),
    hasControls: showControls,
    slideGroupLabel: slideGroupLabel,
    afterSlides: slideshowControlsProps && slidesCount > 1 ? /*#__PURE__*/React.createElement("div", {
      className: `${Slides.className}__controls`
    }, /*#__PURE__*/React.createElement(SlideshowControls, _extends({}, slideshowControlsProps, {
      activeIndex: currentIndex,
      onPaginationClick: onPaginationClick,
      onNextClick: onNextClick,
      onPreviousClick: onPreviousClick,
      slidesCount: slidesCount,
      parentRef: slideshow,
      theme: theme,
      isAutoPlaying: isAutoPlaying,
      nextButtonProps: _objectSpread2({
        'aria-controls': slideshowSlidesId
      }, slideshowControlsProps.nextButtonProps),
      previousButtonProps: _objectSpread2({
        'aria-controls': slideshowSlidesId
      }, slideshowControlsProps.previousButtonProps),
      playButtonProps: autoPlay ? _objectSpread2({
        'aria-controls': slideshowSlidesId,
        onClick: toggleForcePause
      }, slideshowControlsProps.playButtonProps) : undefined,
      paginationItemProps: index => {
        var _slideshowControlsPro;
        return _objectSpread2({
          'aria-controls': buildSlideShowGroupId(slideshowSlidesId, index)
        }, (_slideshowControlsPro = slideshowControlsProps.paginationItemProps) === null || _slideshowControlsPro === void 0 ? void 0 : _slideshowControlsPro.call(slideshowControlsProps, index));
      }
    }))) : undefined
  }, forwardedProps), children);
});
Slideshow.displayName = 'Slideshow';
Slideshow.defaultProps = DEFAULT_PROPS$P;

const _excluded$1b = ["className", "children"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$15 = 'SlideshowItem';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$12 = getRootClassName(COMPONENT_NAME$15);

/**
 * SlideshowItem component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const SlideshowItem = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      children
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1b);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$12
    }))
  }, forwardedProps), children);
});
SlideshowItem.displayName = COMPONENT_NAME$15;
SlideshowItem.className = CLASSNAME$12;

const isTouchDevice = () => 'ontouchstart' in window;

/**
 * Listen swipe to navigate left and right.
 */
function useSwipeNavigate(element, onNext, onPrevious) {
  useEffect(() => {
    if (!element || !isTouchDevice()) return undefined;
    return detectHorizontalSwipe(element, swipe => {
      const callback = swipe === 'right' ? onPrevious : onNext;
      callback === null || callback === void 0 ? void 0 : callback();
    });
  }, [onPrevious, onNext, element]);
}

/**
 * Calculate the currently visible pagination "bullet" range.
 */
function usePaginationVisibleRange(activeIndex, slideCount) {
  const previousVisibleRangeRef = useRef();
  return useMemo(() => {
    const lastSlide = slideCount - 1;
    const {
      current: previousVisibleRange
    } = previousVisibleRangeRef;
    let newVisibleRange;
    if (activeIndex === (previousVisibleRange === null || previousVisibleRange === void 0 ? void 0 : previousVisibleRange.max) && activeIndex < lastSlide) {
      newVisibleRange = {
        min: previousVisibleRange.min + 1,
        max: previousVisibleRange.max + 1
      };
    } else if (activeIndex === (previousVisibleRange === null || previousVisibleRange === void 0 ? void 0 : previousVisibleRange.min) && activeIndex > 0) {
      newVisibleRange = {
        min: previousVisibleRange.min - 1,
        max: previousVisibleRange.max - 1
      };
    } else {
      const deltaItems = PAGINATION_ITEMS_MAX - 1;
      let min = activeIndex - EDGE_FROM_ACTIVE_INDEX;
      let max = activeIndex + EDGE_FROM_ACTIVE_INDEX;
      if (activeIndex > lastSlide - EDGE_FROM_ACTIVE_INDEX) {
        min = lastSlide - deltaItems;
        max = lastSlide;
      } else if (activeIndex < deltaItems) {
        min = 0;
        max = deltaItems;
      }
      newVisibleRange = {
        min,
        max
      };
    }
    previousVisibleRangeRef.current = newVisibleRange;
    return newVisibleRange;
  }, [activeIndex, slideCount]);
}

const _excluded$1c = ["activeIndex", "className", "nextButtonProps", "onNextClick", "onPaginationClick", "onPreviousClick", "parentRef", "previousButtonProps", "paginationProps", "slidesCount", "theme", "isAutoPlaying", "playButtonProps", "paginationItemLabel", "paginationItemProps"],
  _excluded2$2 = ["className", "label"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$16 = 'SlideshowControls';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$13 = getRootClassName(COMPONENT_NAME$16);

/**
 * Component default props.
 */
const DEFAULT_PROPS$Q = {
  activeIndex: 0,
  theme: Theme.light
};

/**
 * SlideshowControls component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const InternalSlideshowControls = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      activeIndex,
      className,
      nextButtonProps,
      onNextClick,
      onPaginationClick,
      onPreviousClick,
      parentRef,
      previousButtonProps,
      paginationProps,
      slidesCount,
      theme,
      isAutoPlaying = false,
      playButtonProps,
      paginationItemLabel,
      paginationItemProps
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1c);
  let parent;
  if (WINDOW) {
    // Checking window object to avoid errors in SSR.
    parent = parentRef instanceof HTMLElement ? parentRef : parentRef === null || parentRef === void 0 ? void 0 : parentRef.current;
  }
  const paginationRef = React.useRef(null);
  // Listen to touch swipe navigate left & right.
  useSwipeNavigate(parent,
  // Go next without loopback.
  useCallback(() => onNextClick === null || onNextClick === void 0 ? void 0 : onNextClick(false), [onNextClick]),
  // Go previous without loopback.
  useCallback(() => onPreviousClick === null || onPreviousClick === void 0 ? void 0 : onPreviousClick(false), [onPreviousClick]));

  /**
   * Add roving tab index pattern to pagination items and activate slide on focus.
   */
  useRovingTabIndex({
    parentRef: paginationRef,
    elementSelector: 'button',
    keepTabIndex: true,
    onElementFocus: element => {
      element.click();
    }
  });

  // Pagination "bullet" range.
  const visibleRange = usePaginationVisibleRange(activeIndex, slidesCount);

  // Inline style of wrapper element.
  const wrapperStyle = {
    transform: `translateX(-${PAGINATION_ITEM_SIZE * visibleRange.min}px)`
  };
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$13,
      theme
    }), {
      [`${CLASSNAME$13}--has-infinite-pagination`]: slidesCount > PAGINATION_ITEMS_MAX
    })
  }), /*#__PURE__*/React.createElement(IconButton, _extends({}, previousButtonProps, {
    icon: mdiChevronLeft,
    className: `${CLASSNAME$13}__navigation`,
    color: theme === Theme.dark ? 'light' : 'dark',
    emphasis: Emphasis.low,
    onClick: onPreviousClick
  })), /*#__PURE__*/React.createElement("div", {
    ref: paginationRef,
    className: `${CLASSNAME$13}__pagination`
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: `${CLASSNAME$13}__pagination-items`,
    style: wrapperStyle,
    role: "tablist"
  }, paginationProps), useMemo(() => range(slidesCount).map(index => {
    const isOnEdge = index !== 0 && index !== slidesCount - 1 && (index === visibleRange.min || index === visibleRange.max);
    const isActive = activeIndex === index;
    const isOutRange = index < visibleRange.min || index > visibleRange.max;
    const _ref = paginationItemProps ? paginationItemProps(index) : {},
      {
        className: itemClassName = undefined,
        label = undefined
      } = _ref,
      itemProps = _objectWithoutProperties(_ref, _excluded2$2);
    const ariaLabel = label || (paginationItemLabel === null || paginationItemLabel === void 0 ? void 0 : paginationItemLabel(index)) || `${index + 1} / ${slidesCount}`;
    return /*#__PURE__*/React.createElement("button", _extends({
      className: classnames(handleBasicClasses({
        prefix: `${CLASSNAME$13}__pagination-item`,
        isActive,
        isOnEdge,
        isOutRange
      }), itemClassName),
      key: index,
      type: "button",
      tabIndex: isActive ? undefined : -1,
      role: "tab",
      "aria-selected": isActive,
      onClick: () => onPaginationClick === null || onPaginationClick === void 0 ? void 0 : onPaginationClick(index),
      "aria-label": ariaLabel
    }, itemProps));
  }), [slidesCount, visibleRange.min, visibleRange.max, activeIndex, paginationItemProps, paginationItemLabel, onPaginationClick]))), playButtonProps ? /*#__PURE__*/React.createElement(IconButton, _extends({}, playButtonProps, {
    icon: isAutoPlaying ? mdiPauseCircleOutline : mdiPlayCircleOutline,
    className: `${CLASSNAME$13}__play`,
    color: theme === Theme.dark ? 'light' : 'dark',
    emphasis: Emphasis.low
  })) : null, /*#__PURE__*/React.createElement(IconButton, _extends({}, nextButtonProps, {
    icon: mdiChevronRight,
    className: `${CLASSNAME$13}__navigation`,
    color: theme === Theme.dark ? 'light' : 'dark',
    emphasis: Emphasis.low,
    onClick: onNextClick
  })));
});
InternalSlideshowControls.displayName = COMPONENT_NAME$16;
InternalSlideshowControls.className = CLASSNAME$13;
InternalSlideshowControls.defaultProps = DEFAULT_PROPS$Q;
const SlideshowControls = Object.assign(InternalSlideshowControls, {
  useSlideshowControls,
  useSlideshowControlsDefaultOptions: DEFAULT_OPTIONS$1
});

const _excluded$1d = ["activeIndex", "id", "className", "theme", "fillHeight", "groupBy", "isAutoPlaying", "toggleAutoPlay", "slidesId", "children", "afterSlides", "hasControls", "slideGroupLabel"];
/**
 * Component display name.
 */
const COMPONENT_NAME$17 = 'Slideshow';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$14 = getRootClassName(COMPONENT_NAME$17);

/**
 * Slides component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Slides = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      activeIndex,
      id,
      className,
      theme,
      fillHeight,
      groupBy,
      isAutoPlaying,
      toggleAutoPlay,
      slidesId,
      children,
      afterSlides,
      hasControls,
      slideGroupLabel
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1d);
  const wrapperRef = React.useRef(null);
  const startIndexVisible = activeIndex;
  const endIndexVisible = startIndexVisible + 1;

  // Inline style of wrapper element.
  const wrapperStyle = {
    transform: `translateX(-${FULL_WIDTH_PERCENT * activeIndex}%)`
  };
  const groups = React.useMemo(() => {
    const childrenArray = Children.toArray(children);
    return groupBy && groupBy > 1 ? chunk(childrenArray, groupBy) : childrenArray;
  }, [children, groupBy]);
  return /*#__PURE__*/React.createElement("section", _extends({
    id: id,
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$14,
      theme
    }), {
      [`${CLASSNAME$14}--fill-height`]: fillHeight,
      [`${CLASSNAME$14}--group-by-${groupBy}`]: Boolean(groupBy)
    }),
    "aria-roledescription": "carousel"
  }), /*#__PURE__*/React.createElement("div", {
    id: slidesId,
    className: `${CLASSNAME$14}__slides`,
    onMouseEnter: toggleAutoPlay,
    onMouseLeave: toggleAutoPlay,
    "aria-live": isAutoPlaying ? 'off' : 'polite'
  }, /*#__PURE__*/React.createElement("div", {
    ref: wrapperRef,
    className: `${CLASSNAME$14}__wrapper`,
    style: wrapperStyle
  }, groups.map((group, index) => /*#__PURE__*/React.createElement(SlideshowItemGroup, {
    key: index,
    id: slidesId && buildSlideShowGroupId(slidesId, index),
    role: hasControls ? 'tabpanel' : 'group',
    label: slideGroupLabel ? slideGroupLabel(index + 1, groups.length) : undefined,
    isDisplayed: index >= startIndexVisible && index < endIndexVisible
  }, group)))), afterSlides);
});
Slides.displayName = COMPONENT_NAME$17;
Slides.className = CLASSNAME$14;

const _excluded$1e = ["checked", "children", "className", "disabled", "helper", "id", "isChecked", "isDisabled", "name", "onChange", "position", "theme", "value", "inputProps"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$18 = 'Switch';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$15 = getRootClassName(COMPONENT_NAME$18);

/**
 * Component default props.
 */
const DEFAULT_PROPS$R = {
  position: Alignment.left,
  theme: Theme.light
};

/**
 * Switch component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Switch = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      checked,
      children,
      className,
      disabled,
      helper,
      id,
      isChecked = checked,
      isDisabled = disabled,
      name,
      onChange,
      position,
      theme,
      value,
      inputProps = {}
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1e);
  const generatedInputId = useId();
  const inputId = id || generatedInputId;
  const handleChange = event => {
    if (onChange) {
      onChange(!isChecked, value, name, event);
    }
  };
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$15,
      isChecked,
      isDisabled,
      position,
      theme,
      isUnchecked: !isChecked
    })),
    "aria-disabled": isDisabled
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$15}__input-wrapper`
  }, /*#__PURE__*/React.createElement("input", _extends({
    type: "checkbox",
    role: "switch",
    id: inputId,
    className: `${CLASSNAME$15}__input-native`,
    name: name,
    value: value,
    disabled: isDisabled,
    checked: isChecked,
    "aria-checked": Boolean(isChecked),
    onChange: handleChange,
    "aria-describedby": helper ? `${inputId}-helper` : undefined
  }, inputProps)), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$15}__input-placeholder`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$15}__input-background`
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$15}__input-indicator`
  }))), Children.count(children) > 0 && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$15}__content`
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: inputId,
    theme: theme,
    className: `${CLASSNAME$15}__label`
  }, children), !isEmpty(helper) && /*#__PURE__*/React.createElement(InputHelper, {
    id: `${inputId}-helper`,
    theme: theme,
    className: `${CLASSNAME$15}__helper`
  }, helper)));
});
Switch.displayName = COMPONENT_NAME$18;
Switch.className = CLASSNAME$15;
Switch.defaultProps = DEFAULT_PROPS$R;

const _excluded$1f = ["children", "className", "hasBefore", "hasDividers", "theme"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$19 = 'Table';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$16 = getRootClassName(COMPONENT_NAME$19);

/**
 * Component default props.
 */
const DEFAULT_PROPS$S = {
  theme: Theme.light
};

/**
 * Table component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Table = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      hasBefore,
      hasDividers,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1f);
  return /*#__PURE__*/React.createElement("table", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$16,
      hasBefore,
      hasDividers,
      theme
    }))
  }), children);
});
Table.displayName = COMPONENT_NAME$19;
Table.className = CLASSNAME$16;
Table.defaultProps = DEFAULT_PROPS$S;

const _excluded$1g = ["children", "className"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1a = 'TableBody';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$17 = getRootClassName(COMPONENT_NAME$1a, true);

/**
 * TableBody component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const TableBody = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1g);
  return /*#__PURE__*/React.createElement("tbody", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$17
    }))
  }), children);
});
TableBody.displayName = COMPONENT_NAME$1a;
TableBody.className = CLASSNAME$17;

const _excluded$1h = ["children", "className", "icon", "isSortable", "onHeaderClick", "sortOrder", "variant"];

/**
 * Table head cell sort order.
 */
const ThOrder = {
  asc: 'asc',
  desc: 'desc'
};
/**
 * Table cell variants.
 */
const TableCellVariant = {
  body: 'body',
  head: 'head'
};

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1b = 'TableCell';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$18 = getRootClassName(COMPONENT_NAME$1b, true);

/**
 * Component default props.
 */
const DEFAULT_PROPS$T = {
  variant: TableCellVariant.body
};

/**
 * TableCell component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const TableCell = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      icon,
      isSortable,
      onHeaderClick,
      sortOrder,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1h);

  // Use button if clickable
  const Wrapper = onHeaderClick ? 'button' : 'div';
  const wrapperProps = Wrapper === 'button' ? {
    type: 'button',
    onClick: onHeaderClick
  } : undefined;

  // ARIA sort
  let ariaSort;
  if (isSortable) {
    ariaSort = 'none';
    if (sortOrder === ThOrder.asc) ariaSort = 'ascending';
    if (sortOrder === ThOrder.desc) ariaSort = 'descending';
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, variant === TableCellVariant.head && /*#__PURE__*/React.createElement("th", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(handleBasicClasses({
      prefix: CLASSNAME$18,
      isSortable,
      isSorted: isSortable && !!sortOrder
    }), className, `${CLASSNAME$18}--head`),
    "aria-sort": ariaSort
  }), /*#__PURE__*/React.createElement(Wrapper, _extends({
    className: `${CLASSNAME$18}-wrapper`
  }, wrapperProps), icon && !isSortable && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$18}-icon`,
    icon: icon,
    size: Size.xxs
  }), isSortable && sortOrder === ThOrder.asc && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$18}-icon`,
    icon: mdiArrowUp,
    size: Size.xxs
  }), isSortable && sortOrder === ThOrder.desc && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$18}-icon`,
    icon: mdiArrowDown,
    size: Size.xxs
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$18}-content`
  }, children))), variant === TableCellVariant.body && /*#__PURE__*/React.createElement("td", _extends({}, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$18
    }), `${CLASSNAME$18}--body`)
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$18}-content`
  }, children)));
});
TableCell.displayName = COMPONENT_NAME$1b;
TableCell.className = CLASSNAME$18;
TableCell.defaultProps = DEFAULT_PROPS$T;

const _excluded$1i = ["children", "className"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1c = 'TableHeader';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$19 = getRootClassName(COMPONENT_NAME$1c, true);

/**
 * Component default props.
 */
const DEFAULT_PROPS$U = {};

/**
 * TableHeader component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const TableHeader = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1i);
  return /*#__PURE__*/React.createElement("thead", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$19
    }))
  }), children);
});
TableHeader.displayName = COMPONENT_NAME$1c;
TableHeader.className = CLASSNAME$19;
TableHeader.defaultProps = DEFAULT_PROPS$U;

const _excluded$1j = ["children", "className", "disabled", "isClickable", "isDisabled", "isSelected"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1d = 'TableRow';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1a = getRootClassName(COMPONENT_NAME$1d, true);

/**
 * Component default props.
 */
const DEFAULT_PROPS$V = {};

/**
 * TableRow component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const TableRow = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      disabled,
      isClickable,
      isDisabled = disabled,
      isSelected
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1j);
  return /*#__PURE__*/React.createElement("tr", _extends({
    ref: ref,
    tabIndex: isClickable && !isDisabled ? 0 : -1
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      isClickable: isClickable && !isDisabled,
      isDisabled,
      isSelected: isSelected && !isDisabled,
      prefix: CLASSNAME$1a
    })),
    "aria-disabled": isDisabled
  }), children);
});
TableRow.displayName = COMPONENT_NAME$1d;
TableRow.className = CLASSNAME$1a;
TableRow.defaultProps = DEFAULT_PROPS$V;

const _excluded$1k = ["children", "onChange"];
const DEFAULT_PROPS$W = {
  isLazy: INIT_STATE.isLazy,
  shouldActivateOnFocus: INIT_STATE.shouldActivateOnFocus
};

/**
 * This component provides a context in which tabs can be defined and linked to their tab panel.
 *
 * It does not produce any markup so you can wrap it around any React elements and then split the TabList and TabPanel
 * components in the react tree.
 *
 * @param  props React component props.
 * @return React element.
 */
const TabProvider = props => {
  const {
      children,
      onChange
    } = props,
    propState = _objectWithoutProperties(props, _excluded$1k);
  const [state, dispatch] = useReducer(reducer, INIT_STATE);

  // On prop state change => dispatch update.
  useEffect(() => {
    dispatch({
      type: 'update',
      payload: propState
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [dispatch, ...Object.values(propState)]);

  // On active tab index state change => send update to the onChange.
  useEffect(() => {
    if (state === INIT_STATE || !onChange || propState.activeTabIndex === state.activeTabIndex) {
      return;
    }
    onChange(state.activeTabIndex);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [onChange, state.activeTabIndex]);
  return /*#__PURE__*/React.createElement(TabProviderContext.Provider, {
    value: [state, dispatch]
  }, children);
};
TabProvider.defaultProps = DEFAULT_PROPS$W;

const _excluded$1l = ["aria-label", "children", "className", "layout", "position", "theme"];
let TabListLayout = /*#__PURE__*/function (TabListLayout) {
  TabListLayout["clustered"] = "clustered";
  TabListLayout["fixed"] = "fixed";
  return TabListLayout;
}({});

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1e = 'TabList';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1b = `${CSS_PREFIX}-tabs`;

/**
 * Component default props.
 */
const DEFAULT_PROPS$X = {
  layout: TabListLayout.fixed,
  position: Alignment.left,
  theme: Theme.light
};

/**
 * TabList component.
 *
 * Implements WAI-ARIA `tablist` role {@see https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html#rps_label}
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const TabList = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      'aria-label': ariaLabel,
      children,
      className,
      layout,
      position,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1l);
  const tabListRef = React.useRef(null);
  useRovingTabIndex({
    parentRef: tabListRef,
    elementSelector: '[role="tab"]',
    keepTabIndex: false,
    extraDependencies: [children]
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: mergeRefs(ref, tabListRef)
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$1b,
      layout,
      position,
      theme
    }))
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1b}__links`,
    role: "tablist",
    "aria-label": ariaLabel
  }, children));
});
TabList.displayName = COMPONENT_NAME$1e;
TabList.className = CLASSNAME$1b;
TabList.defaultProps = DEFAULT_PROPS$X;

const _excluded$1m = ["className", "disabled", "icon", "iconProps", "id", "isActive", "isDisabled", "label", "onFocus", "onKeyPress", "tabIndex"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1f = 'Tab';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1c = `${CSS_PREFIX}-tabs__link`;

/**
 * Component default props.
 */
const DEFAULT_PROPS$Y = {};

/**
 * Tab component.
 *
 * Implements WAI-ARIA `tab` role {@see https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html#rps_label}
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Tab = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      disabled,
      icon,
      iconProps = {},
      id,
      isActive: propIsActive,
      isDisabled = disabled,
      label,
      onFocus,
      onKeyPress,
      tabIndex = -1
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1m);
  const state = useTabProviderContext('tab', id);
  const isActive = propIsActive || (state === null || state === void 0 ? void 0 : state.isActive);
  const changeToCurrentTab = useCallback(() => {
    if (isDisabled) {
      return;
    }
    state === null || state === void 0 ? void 0 : state.changeToTab();
  }, [isDisabled, state]);
  const handleFocus = useCallback(event => {
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
    if (state !== null && state !== void 0 && state.shouldActivateOnFocus) {
      changeToCurrentTab();
    }
  }, [changeToCurrentTab, onFocus, state === null || state === void 0 ? void 0 : state.shouldActivateOnFocus]);
  const handleKeyPress = useCallback(event => {
    onKeyPress === null || onKeyPress === void 0 ? void 0 : onKeyPress(event);
    if (event.key !== 'Enter') {
      return;
    }
    changeToCurrentTab();
  }, [changeToCurrentTab, onKeyPress]);
  return /*#__PURE__*/React.createElement("button", _extends({
    ref: ref
  }, forwardedProps, {
    type: "button",
    id: state === null || state === void 0 ? void 0 : state.tabId,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$1c,
      isActive,
      isDisabled
    })),
    onClick: changeToCurrentTab,
    onKeyPress: handleKeyPress,
    onFocus: handleFocus,
    role: "tab",
    tabIndex: isActive ? 0 : tabIndex,
    "aria-disabled": isDisabled,
    "aria-selected": isActive,
    "aria-controls": state === null || state === void 0 ? void 0 : state.tabPanelId
  }), icon && /*#__PURE__*/React.createElement(Icon, _extends({
    icon: icon,
    size: Size.xs
  }, iconProps)), label && /*#__PURE__*/React.createElement("span", null, label));
});
Tab.displayName = COMPONENT_NAME$1f;
Tab.className = CLASSNAME$1c;
Tab.defaultProps = DEFAULT_PROPS$Y;

const _excluded$1n = ["children", "id", "className", "isActive"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1g = 'TabPanel';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1d = `${CSS_PREFIX}-tab-panel`;

/**
 * Component default props.
 */
const DEFAULT_PROPS$Z = {};

/**
 * TabPanel component.
 *
 * Implements WAI-ARIA `tabpanel` role {@see https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html#rps_label}
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const TabPanel = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      id,
      className,
      isActive: propIsActive
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1n);
  const state = useTabProviderContext('tabPanel', id);
  const isActive = propIsActive || (state === null || state === void 0 ? void 0 : state.isActive);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    id: state === null || state === void 0 ? void 0 : state.tabPanelId,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$1d,
      isActive
    })),
    role: "tabpanel",
    tabIndex: isActive ? 0 : -1,
    "aria-labelledby": state === null || state === void 0 ? void 0 : state.tabId
  }), (!(state !== null && state !== void 0 && state.isLazy) || isActive) && children);
});
TabPanel.displayName = COMPONENT_NAME$1g;
TabPanel.className = CLASSNAME$1d;
TabPanel.defaultProps = DEFAULT_PROPS$Z;

const _excluded$1o = ["id", "isDisabled", "isRequired", "placeholder", "multiline", "value", "setFocus", "onChange", "onFocus", "onBlur", "inputRef", "rows", "recomputeNumberOfRows", "type", "name", "hasError", "describedById"],
  _excluded2$3 = ["chips", "className", "clearButtonProps", "disabled", "error", "forceFocusStyle", "hasError", "helper", "icon", "id", "inputRef", "isDisabled", "isRequired", "isValid", "label", "labelProps", "maxLength", "minimumRows", "multiline", "name", "onBlur", "onChange", "onClear", "onFocus", "placeholder", "textFieldRef", "theme", "type", "value", "afterElement"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1h = 'TextField';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1e = getRootClassName(COMPONENT_NAME$1h);

/**
 * Default minimum number of rows in the multiline mode.
 */
const DEFAULT_MIN_ROWS = 2;

/**
 * Component default props.
 */
const DEFAULT_PROPS$_ = {
  theme: Theme.light,
  type: 'text'
};

/**
 * Hook that allows to calculate the number of rows needed for a text area.
 * @param minimumRows Minimum number of rows that we want to display.
 * @return rows to be used and a callback to recalculate
 */
const useComputeNumberOfRows = minimumRows => {
  const [rows, setRows] = useState(minimumRows);
  const recompute = target => {
    /**
     * HEAD's UP! This part is a little bit tricky. The idea here is to only
     * display the necessary rows on the textarea. In order to dynamically adjust
     * the height on that field, we need to:
     * 1. Set the current amount of rows to the minimum. That will make the scroll appear.
     * 2. With that, we will have the `scrollHeight`, meaning the height of the container adjusted to the current content
     * 3. With the scroll height, we can figure out how many rows we need to use by dividing the scroll height
     * by the line height.
     * 4. With that number, we can readjust the number of rows on the text area. We need to do that here, if we leave that to
     * the state change through React, there are some scenarios (resize, hitting ENTER or BACKSPACE which add or remove lines)
     * when we will not see the update and the rows will be resized to the minimum.
     * 5. In case there is any other update on the component that changes the UI, we need to keep the number of rows
     * on the state in order to allow React to re-render. Therefore, we save them using `useState`
     */
    // eslint-disable-next-line no-param-reassign
    target.rows = minimumRows;
    let currentRows = target.scrollHeight / (target.clientHeight / minimumRows);
    currentRows = currentRows >= minimumRows ? currentRows : minimumRows;
    // eslint-disable-next-line no-param-reassign
    target.rows = currentRows;
    setRows(currentRows);
  };
  return {
    recomputeNumberOfRows: recompute,
    rows
  };
};
const renderInputNative = props => {
  const {
      id,
      isDisabled,
      isRequired,
      placeholder,
      multiline,
      value,
      setFocus,
      onChange,
      onFocus,
      onBlur,
      inputRef,
      rows,
      recomputeNumberOfRows,
      type,
      name,
      hasError,
      describedById
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1o);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ref = useRef(null);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    // Recompute the number of rows for the first rendering
    if (multiline && ref && ref.current) {
      recomputeNumberOfRows(ref.current);
    }
  }, [ref, multiline, recomputeNumberOfRows, value]);
  const onTextFieldFocus = event => {
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
    setFocus(true);
  };
  const onTextFieldBlur = event => {
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    setFocus(false);
  };
  const handleChange = event => {
    onChange(get(event, 'target.value'), name, event);
  };
  const Component = multiline ? 'textarea' : 'input';
  const inputProps = _objectSpread2(_objectSpread2({}, forwardedProps), {}, {
    id,
    className: multiline ? `${CLASSNAME$1e}__input-native ${CLASSNAME$1e}__input-native--textarea` : `${CLASSNAME$1e}__input-native ${CLASSNAME$1e}__input-native--text`,
    placeholder,
    value,
    name,
    disabled: isDisabled,
    required: isRequired,
    onFocus: onTextFieldFocus,
    onBlur: onTextFieldBlur,
    onChange: handleChange,
    'aria-invalid': hasError ? 'true' : undefined,
    'aria-describedby': describedById,
    ref: mergeRefs(inputRef, ref)
  });
  if (multiline) {
    inputProps.rows = rows;
  } else {
    inputProps.type = type;
  }
  return /*#__PURE__*/React.createElement(Component, inputProps);
};

/**
 * TextField component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const TextField = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      chips,
      className,
      clearButtonProps,
      disabled,
      error,
      forceFocusStyle,
      hasError,
      helper,
      icon,
      id,
      inputRef: inputRefProps,
      isDisabled = disabled,
      isRequired,
      isValid,
      label,
      labelProps,
      maxLength,
      minimumRows,
      multiline,
      name,
      onBlur,
      onChange,
      onClear,
      onFocus,
      placeholder,
      textFieldRef,
      theme,
      type,
      value,
      afterElement
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded2$3);
  const generatedTextFieldId = useId();
  const textFieldId = id || generatedTextFieldId;
  /** Keep a clean local input ref to manage focus */
  const localInputRef = useRef(null);
  /** Merge prop input ref and local input ref */
  const inputRef = mergeRefs(localInputRef, inputRefProps);
  /**
   * Generate unique ids for both the helper and error texts, in order to
   * later on add them to the input native as aria-describedby. If both the error and the helper are present,
   * we want to first use the most important one, which is the errorId. That way, screen readers will read first
   * the error and then the helper
   */
  const helperId = helper ? `text-field-helper-${generatedTextFieldId}` : undefined;
  const errorId = error ? `text-field-error-${generatedTextFieldId}` : undefined;
  const describedByIds = [errorId, helperId, forwardedProps['aria-describedby']].filter(Boolean);
  const describedById = describedByIds.length === 0 ? undefined : describedByIds.join(' ');
  const [isFocus, setFocus] = useState(false);
  const {
    rows,
    recomputeNumberOfRows
  } = useComputeNumberOfRows(multiline ? minimumRows || DEFAULT_MIN_ROWS : 0);
  const valueLength = (value || '').length;
  const isNotEmpty = valueLength > 0;

  /**
   * Function triggered when the Clear Button is clicked.
   * The idea is to execute the `onChange` callback with an empty string
   * and remove focus from the clear button.
   * @param evt On clear event.
   */
  const handleClear = evt => {
    evt.nativeEvent.preventDefault();
    evt.nativeEvent.stopPropagation();
    evt.currentTarget.blur();
    onChange('');
    if (onClear) {
      onClear(evt);
    }

    /** Use local inputRef in case the prop input ref is a `mergeRefs` function. */
    const inputElement = localInputRef;
    if (inputElement && inputElement.current) {
      inputElement.current.focus();
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: classnames(className, handleBasicClasses({
      hasChips: Boolean(chips),
      hasError: !isValid && hasError,
      hasIcon: Boolean(icon),
      hasInput: !multiline,
      hasInputClear: clearButtonProps && isNotEmpty,
      hasLabel: Boolean(label),
      hasPlaceholder: Boolean(placeholder),
      hasTextarea: multiline,
      hasValue: Boolean(value),
      isDisabled,
      isFocus: isFocus || forceFocusStyle,
      isValid,
      prefix: CLASSNAME$1e,
      theme
    }))
  }, (label || maxLength) && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1e}__header`
  }, label && /*#__PURE__*/React.createElement(InputLabel, _extends({}, labelProps, {
    htmlFor: textFieldId,
    className: `${CLASSNAME$1e}__label`,
    isRequired: isRequired,
    theme: theme
  }), label), maxLength && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1e}__char-counter`
  }, /*#__PURE__*/React.createElement("span", null, maxLength - valueLength), maxLength - valueLength === 0 && /*#__PURE__*/React.createElement(Icon, {
    icon: mdiAlertCircle,
    size: Size.xxs
  }))), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1e}__wrapper`,
    ref: textFieldRef
  }, icon && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$1e}__input-icon`,
    color: theme === Theme.dark ? 'light' : undefined,
    icon: icon,
    size: Size.xs
  }), chips && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1e}__chips`
  }, chips, renderInputNative(_objectSpread2({
    id: textFieldId,
    inputRef,
    isDisabled,
    isRequired,
    maxLength,
    multiline,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    recomputeNumberOfRows,
    rows,
    setFocus,
    type,
    value,
    name,
    hasError,
    describedById
  }, forwardedProps))), !chips && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1e}__input-wrapper`
  }, renderInputNative(_objectSpread2({
    id: textFieldId,
    inputRef,
    isDisabled,
    isRequired,
    maxLength,
    multiline,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    recomputeNumberOfRows,
    rows,
    setFocus,
    type,
    value,
    name,
    hasError,
    describedById
  }, forwardedProps))), (isValid || hasError) && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$1e}__input-validity`,
    color: theme === Theme.dark ? 'light' : undefined,
    icon: isValid ? mdiCheckCircle : mdiAlertCircle,
    size: Size.xxs
  }), clearButtonProps && isNotEmpty && /*#__PURE__*/React.createElement(IconButton, _extends({}, clearButtonProps, {
    className: `${CLASSNAME$1e}__input-clear`,
    icon: mdiCloseCircle,
    emphasis: Emphasis.low,
    size: Size.s,
    theme: theme,
    onClick: handleClear,
    type: "button"
  })), afterElement && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1e}__after-element`
  }, afterElement)), hasError && error && /*#__PURE__*/React.createElement(InputHelper, {
    className: `${CLASSNAME$1e}__helper`,
    kind: Kind.error,
    theme: theme,
    id: errorId
  }, error), helper && /*#__PURE__*/React.createElement(InputHelper, {
    className: `${CLASSNAME$1e}__helper`,
    theme: theme,
    id: helperId
  }, helper));
});
TextField.displayName = COMPONENT_NAME$1h;
TextField.className = CLASSNAME$1e;
TextField.defaultProps = DEFAULT_PROPS$_;

function getState(img, event) {
  // Error event occurred or image has no source.
  if ((event === null || event === void 0 ? void 0 : event.type) === 'error' || img !== null && img !== void 0 && img.complete && !img.getAttribute('src')) {
    return 'hasError';
  }
  // Image is undefined or incomplete.
  if (!img || !img.complete) {
    return 'isLoading';
  }
  // Else loaded.
  return 'isLoaded';
}
function useImageLoad(imageURL, imgRef) {
  const [state, setState] = useState(getState(imgRef));

  // Update state when changing image URL or DOM reference.
  useEffect(() => {
    setState(getState(imgRef));
  }, [imageURL, imgRef]);

  // Listen to `load` and `error` event on image
  useEffect(() => {
    const img = imgRef;
    if (!img) return undefined;
    const update = event => setState(getState(img, event));
    img.addEventListener('load', update);
    img.addEventListener('error', update);
    return () => {
      img.removeEventListener('load', update);
      img.removeEventListener('error', update);
    };
  }, [imgRef, imgRef === null || imgRef === void 0 ? void 0 : imgRef.src]);
  return state;
}

// Calculate shift to center the focus point in the container.
function shiftPosition(_ref) {
  let {
    scale,
    focusPoint,
    imageSize,
    containerSize
  } = _ref;
  const scaledSize = imageSize / scale;
  if (scaledSize === containerSize) return 0;
  const scaledFocusHeight = focusPoint * scaledSize;
  const startFocus = scaledFocusHeight - containerSize / 2;
  const shift = startFocus / (scaledSize - containerSize);
  return Math.floor(Math.max(Math.min(shift, 1), 0) * 100);
}

// Compute CSS properties to apply the focus point.
const useFocusPointStyle = (_ref2, element, isLoaded) => {
  let {
    image,
    aspectRatio,
    focusPoint,
    imgProps: {
      width,
      height
    } = {}
  } = _ref2;
  // Get natural image size from imgProps or img element.
  const imageSize = useMemo(() => {
    // Focus point is not applicable => exit early
    if (!image || aspectRatio === AspectRatio.original || !(focusPoint !== null && focusPoint !== void 0 && focusPoint.x) && !(focusPoint !== null && focusPoint !== void 0 && focusPoint.y)) return undefined;
    if (typeof width === 'number' && typeof height === 'number') return {
      width,
      height
    };
    if (element && isLoaded) return {
      width: element.naturalWidth,
      height: element.naturalHeight
    };
    return undefined;
  }, [aspectRatio, element, focusPoint === null || focusPoint === void 0 ? void 0 : focusPoint.x, focusPoint === null || focusPoint === void 0 ? void 0 : focusPoint.y, height, image, isLoaded, width]);

  // Get container size (dependant on imageSize).
  const [containerSize, setContainerSize] = useState(undefined);
  useEffect(function updateContainerSize() {
    const cWidth = element === null || element === void 0 ? void 0 : element.offsetWidth;
    const cHeight = element === null || element === void 0 ? void 0 : element.offsetHeight;
    if (cWidth && cHeight) {
      // Update only if needed.
      setContainerSize(oldContainerSize => (oldContainerSize === null || oldContainerSize === void 0 ? void 0 : oldContainerSize.width) === cWidth && oldContainerSize.height === cHeight ? oldContainerSize : {
        width: cWidth,
        height: cHeight
      });
    } else if (imageSize) {
      // Wait for a render (in case the container size is dependent on the image size).
      requestAnimationFrame(updateContainerSize);
    }
  }, [element === null || element === void 0 ? void 0 : element.offsetHeight, element === null || element === void 0 ? void 0 : element.offsetWidth, imageSize]);

  // Compute style.
  const [style, setStyle] = useState({});
  useEffect(() => {
    // Focus point is not applicable => exit early
    if (!image || aspectRatio === AspectRatio.original || !(focusPoint !== null && focusPoint !== void 0 && focusPoint.x) && !(focusPoint !== null && focusPoint !== void 0 && focusPoint.y)) {
      return;
    }
    if (!element || !imageSize) {
      // Focus point can be computed but now right now (image size unknown).
      setStyle({
        visibility: 'hidden'
      });
      return;
    }
    if (!containerSize || !imageSize.height || !imageSize.width) {
      // Missing container or image size abort focus point compute.
      setStyle({});
      return;
    }
    const heightScale = imageSize.height / containerSize.height;
    const widthScale = imageSize.width / containerSize.width;
    const scale = Math.min(widthScale, heightScale);

    // Focus Y relative to the top (instead of the center)
    const focusPointFromTop = Math.abs(((focusPoint === null || focusPoint === void 0 ? void 0 : focusPoint.y) || 0) - 1) / 2;
    const y = shiftPosition({
      scale,
      focusPoint: focusPointFromTop,
      imageSize: imageSize.height,
      containerSize: containerSize.height
    });

    // Focus X relative to the left (instead of the center)
    const focusPointFromLeft = Math.abs(((focusPoint === null || focusPoint === void 0 ? void 0 : focusPoint.x) || 0) + 1) / 2;
    const x = shiftPosition({
      scale,
      focusPoint: focusPointFromLeft,
      imageSize: imageSize.width,
      containerSize: containerSize.width
    });
    const objectPosition = `${x}% ${y}%`;
    // Update only if needed.
    setStyle(oldStyle => oldStyle.objectPosition === objectPosition ? oldStyle : {
      objectPosition
    });
  }, [aspectRatio, containerSize, element, focusPoint === null || focusPoint === void 0 ? void 0 : focusPoint.x, focusPoint === null || focusPoint === void 0 ? void 0 : focusPoint.y, image, imageSize]);
  return style;
};

const _excluded$1p = ["align", "alt", "aspectRatio", "badge", "className", "crossOrigin", "fallback", "fillHeight", "focusPoint", "image", "imgProps", "imgRef", "isLoading", "objectFit", "loading", "loadingPlaceholderImageRef", "size", "theme", "variant", "linkProps", "linkAs"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1i = 'Thumbnail';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1f = getRootClassName(COMPONENT_NAME$1i);

/**
 * Component default props.
 */
const DEFAULT_PROPS$$ = {
  fallback: mdiImageBroken,
  loading: 'lazy',
  theme: Theme.light
};

/**
 * Thumbnail component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Thumbnail = /*#__PURE__*/forwardRef((props, ref) => {
  var _loadingPlaceholderIm;
  const {
      align,
      alt,
      aspectRatio = AspectRatio.original,
      badge,
      className,
      crossOrigin,
      fallback,
      fillHeight,
      // `focusPoint` needs to be here to remove it from `forwardedProps`.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      focusPoint,
      image,
      imgProps,
      imgRef: propImgRef,
      isLoading: isLoadingProp,
      objectFit,
      loading,
      loadingPlaceholderImageRef,
      size,
      theme,
      variant,
      linkProps,
      linkAs
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1p);
  const [imgElement, setImgElement] = useState();

  // Image loading state.
  const loadingState = useImageLoad(image, imgElement);
  const isLoaded = loadingState === 'isLoaded';
  const isLoading = isLoadingProp || loadingState === 'isLoading';
  const hasError = loadingState === 'hasError';

  // Focus point.
  const focusPointStyle = useFocusPointStyle(props, imgElement, isLoaded);
  const hasIconErrorFallback = hasError && typeof fallback === 'string';
  const hasCustomErrorFallback = hasError && !hasIconErrorFallback;
  const imageErrorStyle = {};
  if (hasIconErrorFallback) {
    // Keep the image layout on icon fallback.
    imageErrorStyle.visibility = 'hidden';
  } else if (hasCustomErrorFallback) {
    // Remove the image on custom fallback.
    imageErrorStyle.display = 'none';
  }
  const isLink = Boolean((linkProps === null || linkProps === void 0 ? void 0 : linkProps.href) || linkAs);
  const isButton = !!forwardedProps.onClick;
  const isClickable = isButton || isLink;
  let Wrapper = 'div';
  const wrapperProps = _objectSpread2({}, forwardedProps);
  if (isLink) {
    Wrapper = linkAs || 'a';
    Object.assign(wrapperProps, linkProps);
  } else if (isButton) {
    Wrapper = 'button';
    wrapperProps.type = forwardedProps.type || 'button';
    wrapperProps['aria-label'] = forwardedProps['aria-label'] || alt;
  }

  // If we have a loading placeholder image that is really loaded (complete)
  const loadingPlaceholderImage = isLoading && (loadingPlaceholderImageRef === null || loadingPlaceholderImageRef === void 0 ? void 0 : (_loadingPlaceholderIm = loadingPlaceholderImageRef.current) === null || _loadingPlaceholderIm === void 0 ? void 0 : _loadingPlaceholderIm.complete) && (loadingPlaceholderImageRef === null || loadingPlaceholderImageRef === void 0 ? void 0 : loadingPlaceholderImageRef.current) || undefined;

  // Set loading placeholder image as background
  const loadingStyle = loadingPlaceholderImage ? {
    backgroundImage: `url(${loadingPlaceholderImage.src})`
  } : undefined;
  return /*#__PURE__*/React.createElement(Wrapper, _extends({}, wrapperProps, {
    ref: ref,
    className: classnames(linkProps === null || linkProps === void 0 ? void 0 : linkProps.className, className, handleBasicClasses({
      align,
      aspectRatio,
      prefix: CLASSNAME$1f,
      size,
      theme,
      variant,
      isClickable,
      hasError,
      hasIconErrorFallback,
      hasCustomErrorFallback,
      isLoading,
      objectFit,
      hasBadge: !!badge
    }), fillHeight && `${CLASSNAME$1f}--fill-height`)
  }), /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$1f}__background`
  }, /*#__PURE__*/React.createElement("img", _extends({
    // Use placeholder image size
    width: loadingPlaceholderImage === null || loadingPlaceholderImage === void 0 ? void 0 : loadingPlaceholderImage.naturalWidth,
    height: loadingPlaceholderImage === null || loadingPlaceholderImage === void 0 ? void 0 : loadingPlaceholderImage.naturalHeight
  }, imgProps, {
    style: _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
      // Reserve space while loading (when possible)
      width: isLoading ? (imgProps === null || imgProps === void 0 ? void 0 : imgProps.width) || (loadingPlaceholderImage === null || loadingPlaceholderImage === void 0 ? void 0 : loadingPlaceholderImage.naturalWidth) : undefined
    }, imgProps === null || imgProps === void 0 ? void 0 : imgProps.style), imageErrorStyle), focusPointStyle), loadingStyle),
    ref: useMergeRefs(setImgElement, propImgRef),
    className: classnames(handleBasicClasses({
      prefix: `${CLASSNAME$1f}__image`,
      isLoading,
      hasDefinedSize: Boolean((imgProps === null || imgProps === void 0 ? void 0 : imgProps.height) && imgProps.width)
    }), imgProps === null || imgProps === void 0 ? void 0 : imgProps.className),
    crossOrigin: crossOrigin,
    src: image,
    alt: alt,
    loading: loading
  })), !isLoading && hasError && /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$1f}__fallback`
  }, hasIconErrorFallback ? /*#__PURE__*/React.createElement(Icon, {
    icon: fallback,
    size: Size.xxs,
    theme: theme
  }) : fallback)), badge && /*#__PURE__*/React.cloneElement(badge, {
    className: classnames(`${CLASSNAME$1f}__badge`, badge.props.className)
  }));
});
Thumbnail.displayName = COMPONENT_NAME$1i;
Thumbnail.className = CLASSNAME$1f;
Thumbnail.defaultProps = DEFAULT_PROPS$$;

/**
 *  Focal point using vertical alignment, horizontal alignment or coordinates (from -1 to 1).
 */

/**
 * Loading attribute is not yet supported in typescript, so we need
 * to add it in order to avoid a ts error.
 * https://github.com/typescript-cheatsheets/react-typescript-cheatsheet/blob/master/ADVANCED.md#adding-non-standard-attributes
 */

/**
 * All available aspect ratios.
 * @deprecated
 */
const ThumbnailAspectRatio = _objectSpread2({}, AspectRatio);

/**
 *  Thumbnail sizes.
 */

/**
 * Thumbnail variants.
 */
const ThumbnailVariant = {
  squared: 'squared',
  rounded: 'rounded'
};
/**
 * Thumbnail object fit.
 */
const ThumbnailObjectFit = {
  cover: 'cover',
  contain: 'contain'
};

const _excluded$1q = ["after", "before", "className", "label"];

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1j = 'Toolbar';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1g = getRootClassName(COMPONENT_NAME$1j);

/**
 * Component default props.
 */
const DEFAULT_PROPS$10 = {};

/**
 * Toolbar component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Toolbar = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      after,
      before,
      className,
      label
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1q);
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      hasAfter: Boolean(after),
      hasBefore: Boolean(before),
      hasLabel: Boolean(label),
      prefix: CLASSNAME$1g
    }))
  }), before && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1g}__before`
  }, before), label && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1g}__label`
  }, label), after && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1g}__after`
  }, after));
});
Toolbar.displayName = COMPONENT_NAME$1j;
Toolbar.className = CLASSNAME$1g;
Toolbar.defaultProps = DEFAULT_PROPS$10;

/**
 * Add ref and ARIA attribute(s) in tooltip children or wrapped children.
 * Button, IconButton, Icon and React HTML elements don't need to be wrapped but any other kind of children (array, fragment, custom components)
 * will be wrapped in a <span>.
 *
 * @param  children         Original tooltip anchor.
 * @param  setAnchorElement Set tooltip anchor element.
 * @param  isOpen           Whether the tooltip is open or not.
 * @param  id               Tooltip id.
 * @param  label            Tooltip label.
 * @return tooltip anchor.
 */
const useInjectTooltipRef = (children, setAnchorElement, isOpen, id, label) => {
  // Only add description when open
  const describedBy = isOpen ? id : undefined;
  return useMemo(() => {
    // Non-disabled element
    if ( /*#__PURE__*/React.isValidElement(children) && children.props.disabled !== true && children.props.isDisabled !== true) {
      const ref = mergeRefs(children.ref, setAnchorElement);
      const props = _objectSpread2(_objectSpread2({}, children.props), {}, {
        ref
      });

      // Add current tooltip to the aria-describedby if the label is not already present
      if (label !== props['aria-label'] && describedBy) {
        props['aria-describedby'] = [props['aria-describedby'], describedBy].filter(Boolean).join(' ');
      }
      return /*#__PURE__*/cloneElement(children, props);
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "lumx-tooltip-anchor-wrapper",
      ref: setAnchorElement,
      "aria-describedby": describedBy
    }, children);
  }, [children, setAnchorElement, describedBy, label]);
};

/** Return true if the browser does not support pointer hover */
const browserDoesNotSupportHover = () => {
  var _window$matchMedia, _window;
  return !!((_window$matchMedia = (_window = window).matchMedia) !== null && _window$matchMedia !== void 0 && _window$matchMedia.call(_window, '(hover: none)').matches);
};

/** Check if the focus is visible on the given element */
const isFocusVisible = element => {
  try {
    var _element$matches;
    return element === null || element === void 0 ? void 0 : (_element$matches = element.matches) === null || _element$matches === void 0 ? void 0 : _element$matches.call(element, ':focus-visible, [data-focus-visible-added]');
  } catch (_ignored) {
    // Can fail on non browser env
    return true;
  }
};

/**
 * Hook controlling tooltip visibility using mouse hover the anchor and delay.
 *
 * @param  delay         Delay in millisecond to display the tooltip.
 * @param  anchorElement Tooltip anchor element.
 * @return whether or not to show the tooltip.
 */
function useTooltipOpen(delay, anchorElement) {
  const [isOpen, setIsOpen] = useState(false);
  const onPopperMount = useRef(null);

  // Global close on escape
  const [closeCallback, setCloseCallback] = useState(undefined);
  useCallbackOnEscape(isOpen ? closeCallback : undefined);
  useEffect(() => {
    if (!anchorElement) {
      return undefined;
    }
    let timer;
    let openStartTime;
    let shouldOpen;

    // Run timer to defer updating the isOpen state.
    const deferUpdate = duration => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setIsOpen(!!shouldOpen);
      }, duration);
    };
    const hoverNotSupported = browserDoesNotSupportHover();
    const hasTouch = ('ontouchstart' in window);

    // Adapt open/close delay
    const openDelay = delay || (hoverNotSupported ? TOOLTIP_LONG_PRESS_DELAY.open : TOOLTIP_HOVER_DELAY.open);
    const closeDelay = hoverNotSupported ? TOOLTIP_LONG_PRESS_DELAY.close : TOOLTIP_HOVER_DELAY.close;

    // Open (or/and cancel closing) of tooltip.
    const open = () => {
      if (shouldOpen && !timer) return;
      shouldOpen = true;
      openStartTime = Date.now();
      deferUpdate(openDelay);
    };

    // Close or cancel opening of tooltip
    const getClose = function () {
      let overrideDelay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : closeDelay;
      if (!shouldOpen && !timer) return;
      shouldOpen = false;
      deferUpdate(overrideDelay);
    };
    const close = () => getClose(closeDelay);
    const closeImmediately = () => getClose(0);
    setCloseCallback(() => closeImmediately);

    // Adapt event to browsers with or without `hover` support.
    const events = [];
    if (hoverNotSupported) {
      /**
       * Handle touchend event
       * If end comes before the open delay => cancel tooltip (close immediate).
       * Else if end comes after the open delay => tooltip takes priority, the anchor's default touch end event is prevented.
       */
      const longPressEnd = evt => {
        if (!openStartTime) return;
        if (Date.now() - openStartTime >= openDelay) {
          // Tooltip take priority, event prevented.
          evt.stopPropagation();
          evt.preventDefault();
          anchorElement.focus();
          // Close with delay.
          close();
        } else {
          // Close immediately.
          closeImmediately();
        }
      };
      events.push([anchorElement, hasTouch ? 'touchstart' : 'mousedown', open], [anchorElement, hasTouch ? 'touchend' : 'mouseup', longPressEnd]);
    } else {
      events.push([anchorElement, 'mouseenter', open], [anchorElement, 'mouseleave', close], [anchorElement, 'mouseup', closeImmediately]);
      onPopperMount.current = popperElement => {
        if (!popperElement) return;
        // Popper element hover
        popperElement.addEventListener('mouseenter', open);
        popperElement.addEventListener('mouseleave', close);
        // Add to event list to remove on unmount
        events.push([popperElement, 'mouseenter', open], [popperElement, 'mouseleave', close]);
      };
    }

    // Events always applied no matter the browser:.
    events.push(
    // Open on focus (only if focus is visible).
    [anchorElement, 'focusin', e => {
      // Skip if focus is not visible
      if (!isFocusVisible(e.target)) return;
      open();
    }],
    // Close on lost focus.
    [anchorElement, 'focusout', closeImmediately]);

    // Attach events
    for (const [node, eventType, eventHandler] of events) {
      node.addEventListener(eventType, eventHandler);
    }
    return () => {
      // Clear pending timers.
      if (timer) clearTimeout(timer);

      // Detach events.
      for (const [node, eventType, eventHandler] of events) {
        node.removeEventListener(eventType, eventHandler);
      }
    };
  }, [anchorElement, delay]);
  return {
    isOpen,
    onPopperMount: onPopperMount.current
  };
}

const _excluded$1r = ["label", "children", "className", "delay", "placement", "forceOpen"];

/** Position of the tooltip relative to the anchor element. */

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1k = 'Tooltip';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1h = getRootClassName(COMPONENT_NAME$1k);

/**
 * Component default props.
 */
const DEFAULT_PROPS$11 = {
  placement: Placement.BOTTOM
};

/**
 * Arrow size (in pixel).
 */
const ARROW_SIZE$1 = 8;

/**
 * Tooltip component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Tooltip = /*#__PURE__*/forwardRef((props, ref) => {
  var _attributes$popper$da, _attributes$popper;
  const {
      label,
      children,
      className,
      delay,
      placement,
      forceOpen
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1r);
  // Disable in SSR or without a label.
  if (!DOCUMENT || !label) {
    return /*#__PURE__*/React.createElement(TooltipContextProvider, null, children);
  }
  const id = useId();
  const [popperElement, setPopperElement] = useState(null);
  const [anchorElement, setAnchorElement] = useState(null);
  const {
    styles,
    attributes
  } = usePopper(anchorElement, popperElement, {
    placement,
    modifiers: [{
      name: 'offset',
      options: {
        offset: [0, ARROW_SIZE$1]
      }
    }]
  });
  const position = (_attributes$popper$da = attributes === null || attributes === void 0 ? void 0 : (_attributes$popper = attributes.popper) === null || _attributes$popper === void 0 ? void 0 : _attributes$popper['data-popper-placement']) !== null && _attributes$popper$da !== void 0 ? _attributes$popper$da : placement;
  const {
    isOpen: isActivated,
    onPopperMount
  } = useTooltipOpen(delay, anchorElement);
  const isOpen = isActivated || forceOpen;
  const wrappedChildren = useInjectTooltipRef(children, setAnchorElement, isOpen, id, label);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TooltipContextProvider, null, wrappedChildren), isOpen && /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement("div", _extends({
    ref: mergeRefs(ref, setPopperElement, onPopperMount)
  }, forwardedProps, {
    id: id,
    role: "tooltip",
    "aria-label": label,
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$1h,
      position
    })),
    style: styles.popper
  }, attributes.popper), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1h}__arrow`
  }), /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1h}__inner`
  }, label.indexOf('\n') !== -1 ? label.split('\n').map(sentence => /*#__PURE__*/React.createElement("p", {
    key: sentence
  }, sentence)) : label)), document.body));
});
Tooltip.displayName = COMPONENT_NAME$1k;
Tooltip.className = CLASSNAME$1h;
Tooltip.defaultProps = DEFAULT_PROPS$11;

const _excluded$1s = ["aspectRatio", "className", "label", "icon", "size", "theme", "variant", "fileInputProps"];

/**
 * Uploader variants.
 */
const UploaderVariant = {
  square: 'square',
  rounded: 'rounded',
  circle: 'circle'
};

/**
 * Uploader sizes.
 */

/**
 * Extend native HTML input props with specialized `onChange` providing the a `File` array.
 */

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1l = 'Uploader';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1i = getRootClassName(COMPONENT_NAME$1l);

/**
 * Component default props.
 */
const DEFAULT_PROPS$12 = {
  aspectRatio: AspectRatio.horizontal,
  size: Size.xl,
  theme: Theme.light,
  variant: UploaderVariant.square
};

/**
 * Uploader component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const Uploader = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      aspectRatio,
      className,
      label,
      icon,
      size,
      theme,
      variant,
      fileInputProps
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1s);
  // Adjust to square aspect ratio when using circle variants.
  const adjustedAspectRatio = variant === UploaderVariant.circle ? AspectRatio.square : aspectRatio;
  const generatedInputId = useId();
  const inputId = (fileInputProps === null || fileInputProps === void 0 ? void 0 : fileInputProps.id) || generatedInputId;
  const [isDragHovering, unsetDragHovering, setDragHovering] = useBooleanState(false);
  const wrapper = fileInputProps ? {
    Component: 'label',
    props: {
      htmlFor: inputId
    }
  } : {
    Component: 'button',
    props: {
      type: 'button'
    }
  };
  const onChange = React.useMemo(() => {
    if (!(fileInputProps !== null && fileInputProps !== void 0 && fileInputProps.onChange)) return undefined;
    return evt => {
      const fileList = evt.target.files;
      const files = fileList ? Array.from(fileList) : [];
      fileInputProps.onChange(files, evt);
    };
  }, [fileInputProps]);
  return /*#__PURE__*/React.createElement(wrapper.Component, _extends({
    ref: ref
  }, wrapper.props, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      aspectRatio: adjustedAspectRatio,
      prefix: CLASSNAME$1i,
      size,
      theme,
      variant,
      isDragHovering
    }))
  }), /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$1i}__background`
  }), /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$1i}__wrapper`
  }, icon && /*#__PURE__*/React.createElement(Icon, {
    className: `${CLASSNAME$1i}__icon`,
    icon: icon,
    size: Size.s
  }), label && /*#__PURE__*/React.createElement("span", {
    className: `${CLASSNAME$1i}__label`
  }, label)), fileInputProps && /*#__PURE__*/React.createElement("input", _extends({
    type: "file",
    id: inputId,
    className: `${CLASSNAME$1i}__input`
  }, fileInputProps, {
    onChange: onChange,
    onDragEnter: setDragHovering,
    onDragLeave: unsetDragHovering,
    onDrop: unsetDragHovering
  })));
});
Uploader.displayName = COMPONENT_NAME$1l;
Uploader.className = CLASSNAME$1i;
Uploader.defaultProps = DEFAULT_PROPS$12;

const _excluded$1t = ["avatarProps", "className", "fields", "linkProps", "linkAs", "multipleActions", "name", "nameProps", "onClick", "onMouseEnter", "onMouseLeave", "orientation", "simpleAction", "size", "theme"];

/**
 * User block sizes.
 */

/**
 * Defines the props of the component.
 */

/**
 * Component display name.
 */
const COMPONENT_NAME$1m = 'UserBlock';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME$1j = getRootClassName(COMPONENT_NAME$1m);

/**
 * Component default props.
 */
const DEFAULT_PROPS$13 = {
  orientation: Orientation.horizontal,
  size: Size.m,
  theme: Theme.light
};

/**
 * UserBlock component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
const UserBlock = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      avatarProps,
      className,
      fields,
      linkProps,
      linkAs,
      multipleActions,
      name,
      nameProps,
      onClick,
      onMouseEnter,
      onMouseLeave,
      orientation,
      simpleAction,
      size,
      theme
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1t);
  let componentSize = size;

  // Special case - When using vertical orientation force the size to be Sizes.l.
  if (orientation === Orientation.vertical) {
    componentSize = Size.l;
  }
  const shouldDisplayActions = orientation === Orientation.vertical;
  const isLink = Boolean((linkProps === null || linkProps === void 0 ? void 0 : linkProps.href) || linkAs);
  const isClickable = !!onClick || isLink;
  const nameBlock = React.useMemo(() => {
    if (isEmpty(name)) {
      return null;
    }
    let NameComponent = 'span';
    const nProps = _objectSpread2(_objectSpread2({}, nameProps), {}, {
      className: classnames(`${CLASSNAME$1j}__name`, linkProps === null || linkProps === void 0 ? void 0 : linkProps.className, nameProps === null || nameProps === void 0 ? void 0 : nameProps.className)
    });
    if (isClickable) {
      NameComponent = Link;
      Object.assign(nProps, _objectSpread2(_objectSpread2({}, linkProps), {}, {
        onClick,
        linkAs,
        color: ColorPalette.dark
      }));
    }
    // Disable avatar focus since the name block is the same link / same button.
    if (avatarProps) {
      set(avatarProps, ['thumbnailProps', 'tabIndex'], -1);
    }
    return /*#__PURE__*/React.createElement(NameComponent, nProps, name);
  }, [avatarProps, isClickable, linkAs, linkProps, name, nameProps, onClick]);
  const fieldsBlock = fields && componentSize !== Size.s && componentSize !== Size.xs && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1j}__fields`
  }, fields.map((field, idx) => /*#__PURE__*/React.createElement("span", {
    key: idx,
    className: `${CLASSNAME$1j}__field`
  }, field)));
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, forwardedProps, {
    className: classnames(className, handleBasicClasses({
      prefix: CLASSNAME$1j,
      orientation,
      size: componentSize,
      theme,
      isClickable
    })),
    onMouseLeave: onMouseLeave,
    onMouseEnter: onMouseEnter
  }), avatarProps && /*#__PURE__*/React.createElement(Avatar, _extends({
    linkAs: linkAs,
    linkProps: linkProps,
    alt: ""
  }, avatarProps, {
    className: classnames(`${CLASSNAME$1j}__avatar`, avatarProps.className),
    size: componentSize,
    onClick: onClick,
    theme: theme
  })), (fields || name) && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1j}__wrapper`
  }, nameBlock, fieldsBlock), shouldDisplayActions && simpleAction && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1j}__action`
  }, simpleAction), shouldDisplayActions && multipleActions && /*#__PURE__*/React.createElement("div", {
    className: `${CLASSNAME$1j}__actions`
  }, multipleActions));
});
UserBlock.displayName = COMPONENT_NAME$1m;
UserBlock.className = CLASSNAME$1j;
UserBlock.defaultProps = DEFAULT_PROPS$13;

export { AlertDialog, Alignment, AspectRatio, Autocomplete, AutocompleteMultiple, Avatar, Badge, Button, ButtonEmphasis, ButtonGroup, Checkbox, Chip, ChipGroup, ColorPalette, ColorVariant, CommentBlock, CommentBlockVariant, DatePicker, DatePickerControlled, DatePickerField, Dialog, Divider, DragHandle, Dropdown, Emphasis, ExpansionPanel, Flag, FlexBox, GenericBlock, GenericBlockGapSize, Grid, GridColumn, GridItem, Heading, HeadingLevelProvider, Icon, IconButton, ImageBlock, ImageBlockCaptionPosition, ImageLightbox, InlineList, InputHelper, InputLabel, Kind, Lightbox, Link, LinkPreview, List, ListDivider, ListItem, ListSubheader, Message, Mosaic, Navigation, Notification, Orientation, Placement, Popover, PopoverDialog, PostBlock, Progress, ProgressCircular, ProgressLinear, ProgressTracker, ProgressTrackerProvider, ProgressTrackerStep, ProgressTrackerStepPanel, ProgressVariant, RadioButton, RadioGroup, Select, SelectMultiple, SelectMultipleField, SelectVariant, SideNavigation, SideNavigationItem, Size, SkeletonCircle, SkeletonRectangle, SkeletonRectangleVariant, SkeletonTypography, Slider, Slides, Slideshow, SlideshowControls, SlideshowItem, Switch, Tab, TabList, TabListLayout, TabPanel, TabProvider, Table, TableBody, TableCell, TableCellVariant, TableHeader, TableRow, Text, TextField, ThOrder, Theme, Thumbnail, ThumbnailAspectRatio, ThumbnailObjectFit, ThumbnailVariant, Toolbar, Tooltip, Typography, TypographyCustom, TypographyInterface, TypographyTitleCustom, Uploader, UploaderVariant, UserBlock, WhiteSpace, clamp, isClickable, useFocusPointStyle, useHeadingLevel };
//# sourceMappingURL=index.js.map
